// src/Pages/FrontOfficeNew.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TopAppBarNew from '../components/FrontOfficeComp/TopAppBarNew';
import LeftPanelNew from '../components/FrontOfficeComp/LeftPanelNew';
import CenterPanelNew from '../components/FrontOfficeComp/CenterPanelNew';
import RightPanelNew from '../components/FrontOfficeComp/RightPanelNew';
import RightTranscriptAccordion from '../components/FrontOfficeComp/RightTranscriptAccordion';
import ShortcutHelpModal from '../components/FrontOfficeComp/ShortcutHelpModal';
import AgentCallHistoryReport from './AgentCallHistoryReport';
import './FrontOfficeNew.css';
import ListTab from '../components/FrontOfficeComp/Listtab';
import VoicemailTab from '../components/FrontOfficeComp/VoicemailTab';
import SearchTab from '../components/FrontOfficeComp/Search';
import CallerProfileTab from "../components/FrontOfficeComp/CallerProfile.js";
import RemindersTab from "../components/FrontOfficeComp/Reminders.js";
import ViewHistoryTab from "../components/FrontOfficeComp/ViewHistory.js";
import NotesTab from "../components/FrontOfficeComp/Notes.js";
import SmsOpen from "../components/FrontOfficeComp/smsopen.js";
import CallerDashbordTab from "../components/FrontOfficeComp/CallerDashbord.js";
import IncomingCallNotification from '../components/IncomingCall/IncomingCallNotification';
import ChatWindow from '../components/Chat/ChatWindow';
import ChatNotification from '../components/Chat/ChatNotification';
import ChatAgentSelector from '../components/Chat/ChatAgentSelector';
import { useAsterisk } from '../hooks/useAsterisk';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';
import { useIdleLogout } from '../hooks/useIdleLogout';
import asteriskService from '../services/asteriskService';
import { parseJwt } from '../services/api';
import { useSessionUser } from '../SessionContext';
import { API_BASE, SAVE_CONTACT_URL, Update_CONTACT_URL } from '../config/api';
import { ASTERISK_API_BASE } from '../config/bridge';
import { isPlaceholder } from '../lib/validation';
import { FRONT_OFFICE_IDLE_ENABLED, FRONT_OFFICE_IDLE_MINUTES, FRONT_OFFICE_IDLE_MS } from '../config/app';
import { FRONT_OFFICE_SHORTCUT_SECTIONS } from '../constants/frontOfficeShortcuts';
import { useSoftphone } from '../contexts/SoftphoneContext';
import { useChat } from '../contexts/ChatContext';
import {
  getIncomingNotificationCalls,
  resolveCallControlChannelId,
  resolveCallHeaderId,
  resolveIncomingCallDisplayId
} from '../utils/incomingCallNotification';
import {
  buildRecordingLookupFromCall,
  buildTranscriptEntries,
  describeTranscriptStatus,
  fetchAgentRecordings,
  fetchCallTranscript,
  getTranscriptSpeakerRoster,
  queueCallTranscript
} from '../services/transcriptionService';

const UPDATE_TAGS_URL = `${API_BASE}/update-tags`;
const COMPLETE_COMPLAINT_URL = `${API_BASE}/complaints/complete`;
const CALL_CONTEXT_URL = `${ASTERISK_API_BASE}/call-context`;
const COMPLAINT_CALL_LINK_URL = `${ASTERISK_API_BASE}/complaints/link-call`;
const FORM_STORAGE_KEY = 'frontOfficeNew_formData';
const FORM_STORAGE_WRITE_DELAY_MS = 400;
const DETAILS_AUTO_SAVE_DELAY_MS = 10000;
const API_LOGGING_ENABLED =
  process.env.NODE_ENV !== 'production' ||
  String(process.env.REACT_APP_VERBOSE_API_LOGS || '').toLowerCase() === 'true';
const AVAILABILITY_STORAGE_KEY = 'frontOfficeNew_agentAvailability';
const AVAILABILITY_OPTIONS = ['Ready', 'Not Ready', 'Lunch', 'Training', 'Meeting', 'Prayer', 'Rest Room'];
const AGENT_MODE_STORAGE_KEY = 'frontOfficeNew_agentMode';
const AGENT_MODE_OPTIONS = ['Inbound', 'Outbound'];
const SOURCE_OPTIONS = ['In coming call', 'OutBound cal', 'Voice mail', 'Email'];
const LIVE_TRANSCRIPTION_ENABLED =
  String(process.env.REACT_APP_LIVE_TRANSCRIPTION_ENABLED || '').toLowerCase() === 'true';
const TRANSCRIPT_DEBUG_ENABLED =
  String(process.env.REACT_APP_TRANSCRIPT_DEBUG_LOGS || process.env.REACT_APP_VERBOSE_API_LOGS || '')
    .trim()
    .toLowerCase() === 'true';
const TRANSCRIPT_POLL_MAX_ATTEMPTS = Math.max(
  20,
  Number.parseInt(String(process.env.REACT_APP_TRANSCRIPT_POLL_MAX_ATTEMPTS || '120'), 10) || 120
);
const TRANSCRIPT_POLL_LONG_WAIT_ATTEMPT = Math.min(20, TRANSCRIPT_POLL_MAX_ATTEMPTS);

const CALL_RESPONSE_PROMPTS = {
  404: {
    key: 'invalid-number',
    files: ['invalid-number.mp3', 'invalid-number.wav'],
    text: 'Invalid number'
  },
  480: {
    key: 'phone-unavailable',
    files: ['phone-unavailable.mp3', 'phone-unavailable.wav', 'cell-off.mp3', 'cell-off.wav'],
    text: 'Phone unavailable'
  },
  484: {
    key: 'invalid-number',
    files: ['invalid-number.mp3', 'invalid-number.wav'],
    text: 'Invalid number'
  },
  486: {
    key: 'user-busy',
    files: ['user-busy.mp3', 'user-busy.wav'],
    text: 'User busy'
  },
  487: {
    key: 'call-cancelled',
    files: ['call-cancelled.mp3', 'call-cancelled.wav'],
    text: 'Call cancelled'
  },
  503: {
    key: 'service-unavailable',
    files: [
      'service-unavailable.mp3',
      'service-unavailable.wav',
      'network-unavailable.mp3',
      'network-unavailable.wav'
    ],
    text: 'Service unavailable'
  },
  603: {
    key: 'call-declined',
    files: ['call-declined.mp3', 'call-declined.wav'],
    text: 'Call declined'
  },
  NO_ANSWER: {
    key: 'no-answer',
    files: ['no-answer.mp3', 'no-answer.wav'],
    text: 'No answer'
  }
};

const CALL_RESPONSE_BY_Q850_CAUSE = {
  1: '404',
  17: '486',
  18: '480',
  19: 'NO_ANSWER',
  20: '480',
  21: '603',
  22: '484',
  28: '484',
  31: '480',
  34: '480',
  38: '503',
  41: '503',
  42: '503',
  47: '503'
};

const CALL_RESPONSE_BY_DIAL_STATUS = {
  BUSY: '486',
  NOANSWER: 'NO_ANSWER',
  CHANUNAVAIL: '480',
  CONGESTION: '480',
  FAILED: '503',
  CANCEL: '487'
};

const OUTBOUND_RINGBACK_ASSET_PATH = 'RingTone/standardringtone.mp3';
const OUTBOUND_RINGBACK_FALLBACK_DELAY_MS = Math.max(
  0,
  Number.parseInt(String(process.env.REACT_APP_OUTBOUND_RINGBACK_FALLBACK_DELAY_MS || '1800'), 10) || 1800
);

const buildPublicAssetCandidates = (assetPath) => {
  const normalizedAssetPath = String(assetPath || '').replace(/^\/+/, '');
  if (!normalizedAssetPath) return [];

  const candidates = [];
  const addCandidate = (url) => {
    const normalizedUrl = String(url || '').trim();
    if (normalizedUrl && !candidates.includes(normalizedUrl)) {
      candidates.push(normalizedUrl);
    }
  };
  const publicUrl = String(process.env.PUBLIC_URL || '').replace(/\/+$/, '');

  if (publicUrl) {
    addCandidate(`${publicUrl}/${normalizedAssetPath}`);
  }

  if (typeof window !== 'undefined') {
    const firstPathSegment = String(window.location.pathname || '')
      .split('/')
      .filter(Boolean)[0];
    if (firstPathSegment) {
      addCandidate(`/${firstPathSegment}/${normalizedAssetPath}`);
    }
  }

  addCandidate(`/${normalizedAssetPath}`);
  addCandidate(normalizedAssetPath);

  return candidates;
};

const isTruthyFlag = (value) => {
  if (value === true || value === 1) return true;
  return ['true', '1', 'yes'].includes(String(value || '').trim().toLowerCase());
};

const hasOutboundSipRingbackEvent = (info = {}) => {
  const progressCode = String(info?.sipProgressCode || '').trim();
  const dialStatus = String(info?.dialStatus || '').trim().toUpperCase();
  return (
    isTruthyFlag(info?.hasSipRingbackEvent) ||
    progressCode === '180' ||
    dialStatus === 'RINGING'
  );
};

const transcriptUiDebug = (event, details = {}) => {
  if (!TRANSCRIPT_DEBUG_ENABLED) return;
  console.log(`[transcript-ui-debug] ${event}`, details);
};

const formatDateInputValue = (value) => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultRecordingFilters = () => {
  const today = new Date();
  const fromDate = new Date(today);
  fromDate.setDate(fromDate.getDate() - 6);

  return {
    from: formatDateInputValue(fromDate),
    to: formatDateInputValue(today),
    callerNumber: '',
    recordingType: 'all'
  };
};

const pickFirstValue = (...values) => {
  for (const val of values) {
    if (val === null || val === undefined) continue;
    const str = String(val).trim();
    if (str) return str;
  }
  return '';
};

const normalizeDraftCsv = (...values) => {
  const tags = values
    .flatMap((value) => String(value ?? '').split(/[;,|]/))
    .map((value) => value.trim())
    .filter(Boolean);
  const unique = new Map();
  tags.forEach((tag) => {
    const key = tag.toLowerCase();
    if (!unique.has(key)) unique.set(key, tag);
  });
  return Array.from(unique.values())
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    .join(', ');
};

const normalizeDraftVersion = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value).trim() : date.toISOString();
};

const getComplaintDraftVersion = (row) => normalizeDraftVersion(
  row?.ModifiedOn ??
  row?.modifiedOn ??
  row?.updated_at ??
  row?.updatedAt ??
  row?.UpdatedAt
);

const isCallbackRecordingItem = (item = {}) => (
  String(item?.Direction || item?.direction || '').trim().toLowerCase() === 'callback' ||
  String(item?.Source || item?.source || '').toLowerCase().includes('callback') ||
  String(item?.RecordingPath || item?.recordingPath || '').toLowerCase().includes('/callback/') ||
  String(item?.RecordingUrl || item?.recordingUrl || '').toLowerCase().includes('/callback/')
);

const isVoicemailRecordingItem = (item = {}) => (
  isCallbackRecordingItem(item) ||
  String(item?.CallTypeGroup || item?.callTypeGroup || '').toLowerCase() === 'voicemail' ||
  String(item?.CallType || item?.callType || '').toLowerCase().includes('voicemail') ||
  Boolean(item?.IsVoicemail || item?.isVoicemail || item?.Voicemail || item?.voicemail) ||
  (Array.isArray(item?.Voicemails) && item.Voicemails.length > 0) ||
  (Array.isArray(item?.voicemails) && item.voicemails.length > 0) ||
  Number(item?.VoicemailCount || item?.voicemailCount || 0) > 0 ||
  Boolean(item?.VoicemailAt || item?.voicemailAt || item?.VoicemailMarker || item?.voicemailMarker) ||
  String(item?.Source || item?.source || '').toLowerCase().includes('voicemail')
);

const getRecordingCallType = (item = {}) => {
  if (isCallbackRecordingItem(item)) return 'queue_voicemail';
  const explicitType = String(item?.CallType || item?.callType || '').trim().toLowerCase();
  if (explicitType) return explicitType;
  return isVoicemailRecordingItem(item) ? 'after_hours_voicemail' : 'agent_call';
};

const getCallLinkFields = (source = {}) => ({
  uniqueId: pickFirstValue(
    source.callStateUniqueId,
    source.callUniqueId,
    source.uniqueId,
    source.UniqueId,
    source.stableId,
    source.linkedId,
    source.ivrUid
  ),
  channelId: pickFirstValue(
    source.callStateChannelId,
    source.channelId,
    source.ChannelId
  )
});

const getEmptyIdentityFields = () => ({
  cli: '',
  PhoneNo: '',
  contactId: '',
  name: '',
  gender: '',
  age: '',
  cnic: '',
  city: '',
  district: '',
  province: '',
  country: '',
  heardFrom: '',
  fatherHusbandName: '',
  salary: '',
  address: '',
  callback1: '',
  callback2: '',
  religion: '',
  maritalStatus: 'N/A',
  remarks: ''
});

const getEmptyDetailFields = (launchedByDefault) => ({
  queryId: '',
  status: '',
  launchedBy: launchedByDefault,
  CreatedBy: launchedByDefault,
  acceptedBy: '',
  launchedOn: '',
  acceptedOn: '',
  mainCategory: '',
  category: '',
  subCategory: '',
  faq: '',
  referredTo: false,
  referredToAgent: '',
  shortQuery: false,
  priority: 'Normal',
  source: SOURCE_OPTIONS[0],
  furtherHelp: '',
  sindhGovDepartment: 'N/A',
  query: '',
  solution: '',
  adrQuery: false,
  covid: false,
  ageAboveFifty: false,
  domesticViolence: false,
  womenProperty: false,
  womenRelated: false,
  mainTags: '',
  tagMfl: '',
  tagWrlmp: '',
  oldTags: [],
  forMfl: [],
  forWrlmp: [],
  office: ''
});

const useMediaQuery = (query) => {
  const getMatch = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia(query);
    const handler = (event) => setMatches(event.matches);
    setMatches(media.matches);
    if (media.addEventListener) {
      media.addEventListener('change', handler);
      return () => media.removeEventListener('change', handler);
    }
    media.addListener(handler);
    return () => media.removeListener(handler);
  }, [query]);

  return matches;
};

const FrontOfficeNew = () => {
  const navigate = useNavigate();
  const { user } = useSessionUser();
  const leftPanelRef = useRef(null);

  const username = user?.loginName || user?.username || 'Agent';
  const normalizeExtension = (value) => {
    const trimmed = String(value || '').trim();
    return /^[0-9]+$/.test(trimmed) ? trimmed : '';
  };
  const sessionToken = localStorage.getItem('token') || '';
  const agentId = useMemo(() => {
    const assignedExtension = normalizeExtension(parseJwt(sessionToken)?.extension);
    if (assignedExtension) return assignedExtension;
    try {
      const ext = JSON.parse(localStorage.getItem('extension') || '{}');
      const candidate =
        ext.extensionNumber ||
        ext.ExtensionNumber ||
        ext.extension ||
        ext.Extension ||
        ext.sipUsername ||
        ext.SIPUsername ||
        '';
      const normalized = normalizeExtension(candidate);
      if (normalized) return normalized;
    } catch {
      // Ignore malformed localStorage payloads.
    }
    return '';
  }, [sessionToken]);
  const agentQueueName = useMemo(() => {
    try {
      const ext = JSON.parse(localStorage.getItem('extension') || '{}');
      return String(ext.queueName || ext.QueueName || ext.queuename || '').trim();
    } catch {
      return '';
    }
  }, []);

  const lsUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const cmsUserId =
    user?.userId ??
    lsUser?.UserID ??
    lsUser?.userId ??
    lsUser?.ID ??
    lsUser?.id ??
    '';
  const cmsUserName =
    (user?.username && String(user.username).trim()) ||
    (user?.loginName && String(user.loginName).trim()) ||
    (lsUser?.FullName && String(lsUser.FullName).trim()) ||
    (lsUser?.UserName && String(lsUser.UserName).trim()) ||
    (lsUser?.LoginName && String(lsUser.LoginName).trim()) ||
    (lsUser?.username && String(lsUser.username).trim()) ||
    '';
  const currentUserIdentity = useMemo(() => ({
    userId: cmsUserId,
    username:
      (user?.username && String(user.username).trim()) ||
      (lsUser?.UserName && String(lsUser.UserName).trim()) ||
      (lsUser?.username && String(lsUser.username).trim()) ||
      '',
    loginName:
      (user?.loginName && String(user.loginName).trim()) ||
      (lsUser?.LoginName && String(lsUser.LoginName).trim()) ||
      '',
    fullName:
      (lsUser?.FullName && String(lsUser.FullName).trim()) ||
      '',
    extension: agentId,
  }), [agentId, cmsUserId, lsUser?.FullName, lsUser?.LoginName, lsUser?.UserName, lsUser?.username, user?.loginName, user?.username]);

  const launchedByDefault =
    (lsUser?.UserName && String(lsUser.UserName).trim()) ||
    (user?.loginName && String(user.loginName).trim()) ||
    (user?.username && String(user.username).trim()) ||
    'Agent';

  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('slacc-theme');
    if (savedTheme === 'light' || savedTheme === 'dark') return savedTheme;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [isLive, setIsLive] = useState(false);
  const [isDialpadOpen, setIsDialpadOpen] = useState(false);
  const [dialedNumber, setDialedNumber] = useState('');
  const [isListOpen, setIsListOpen] = useState(false);
  const [isVoicemailOpen, setIsVoicemailOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [isReminderOpen, setIsReminderOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isSmsOpen, setIsSmsOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 991.98px)');
  const [mobileAccordionOpen, setMobileAccordionOpen] = useState('controls');
  const [isDesktopTranscriptOpen, setIsDesktopTranscriptOpen] = useState(true);
  const [agentMode, setAgentMode] = useState(() => {
    try {
      const stored = localStorage.getItem(AGENT_MODE_STORAGE_KEY);
      if (AGENT_MODE_OPTIONS.includes(stored)) {
        return stored;
      }
    } catch {
      // Ignore storage errors and fall back to default.
    }
    return 'Inbound';
  });
  const [agentAvailability, setAgentAvailability] = useState(() => {
    try {
      const stored = localStorage.getItem(AVAILABILITY_STORAGE_KEY);
      if (AVAILABILITY_OPTIONS.includes(stored)) {
        return stored;
      }
    } catch {
      // Ignore storage errors and fall back to default.
    }
    return 'Not Ready';
  });
  const [healthEnabled, setHealthEnabled] = useState(true);
  const [listData, setListData] = useState([]);
  const [listReloadKey, setListReloadKey] = useState(0);
  const [searchFilters, setSearchFilters] = useState({ cli: '', cnic: '', name: '', callerId: '' });
  const [searchRows, setSearchRows] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Chat state variables
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [currentChatSession, setCurrentChatSession] = useState(null);
  const [callerDataLoaded, setCallerDataLoaded] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [wrapupSeconds, setWrapupSeconds] = useState(0);
  const [callError, setCallError] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isShortcutsHelpOpen, setIsShortcutsHelpOpen] = useState(false);
  const [isTodayCallActivityOpen, setIsTodayCallActivityOpen] = useState(false);
  const [currentCallerId, setCurrentCallerId] = useState('');
  const [sipControlChannelId, setSipControlChannelId] = useState('');
  const [queueMetrics, setQueueMetrics] = useState({
    totalWaitingCallers: 0,
    longestWaitSeconds: 0,
    longestWaitStartedAt: '',
    nextCallerNumber: '',
    nextQueueName: '',
    agentStatus: '',
    agentStatusLabel: '',
    updatedAt: '',
    metricsReceivedAt: 0
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [countries, setCountries] = useState([]);
  const [religions, setReligions] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [heardFromOptions, setHeardFromOptions] = useState([]);
  const [maritalStatusOptions, setMaritalStatusOptions] = useState([]);
  const [clearSelectionsToken, setClearSelectionsToken] = useState(0);
  const [categories, setCategories] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]); // [{id,name,parent}]
  const [childCategoryOptions, setChildCategoryOptions] = useState([]); // [{id,name,parent}]
  const [complaintDetail, setComplaintDetail] = useState(null);
  const createdByForReminders =
    (lsUser?.UserName && String(lsUser.UserName).trim()) ||
    (lsUser?.FullName && String(lsUser.FullName).trim()) ||
    (user?.username && String(user.username).trim()) ||
    launchedByDefault;
  const dialInputRef = useRef(null);
  const callTimerRef = useRef(null);
  const searchTriggeredRef = useRef(false);
  const doFindContactRef = useRef(true);
  // Refs to allow stable handlers (useCallback with empty deps) to call
  // the latest fetchContactDetails / fetchComplaintDetails functions.
  const fetchContactDetailsRef = useRef(null);
  const fetchComplaintDetailsRef = useRef(null);
  const complaintDetailsRequestIdRef = useRef(0);
  const lastHydratedOutboundCallRef = useRef('');
  const skipCategoryResetRef = useRef(false);
  const prevActiveCallRef = useRef(false);
  const attentionTimerRef = useRef(null);
  const originalTitleRef = useRef('');
  const lastNotifiedCallIdRef = useRef('');
  const callAnsweredRef = useRef(false);
  const transcriptPollTimeoutRef = useRef(null);
  const transcriptRequestKeyRef = useRef('');
  const transcriptCallActiveRef = useRef(false);
  const transcriptLookupRef = useRef({});
  const initialLookupLoadedRef = useRef(false);
  const queueMetricsRequestInFlightRef = useRef(false);
  const cliLookupInFlightRef = useRef(false);
  const topBarRef = useRef(null);
  const [topBarHeight, setTopBarHeight] = useState(72);

  const toggleMobileAccordion = useCallback((key) => {
    setMobileAccordionOpen((prev) => (prev === key ? '' : key));
  }, []);

  const logApi = useCallback((label, info) => {
    if (!API_LOGGING_ENABLED) return;
    console.info(`[API] ${label}`, info);
  }, []);

  // ARI call handling
  const {
    isConnected,
    activeChannels,
    currentChannel: ariCurrentChannel,
    hangupCall: ariHangupCall,
    hangupAllCalls: ariHangupAllCalls,
    makeCall: ariMakeCall,
    acceptIncomingCall: ariAcceptIncomingCall,
    rejectIncomingCall: ariRejectIncomingCall,
    resetCurrentCall: ariResetCurrentCall,
    callStatus,
    lastCallResponse,
    incomingCalls
  } = useAsterisk();

  // Softphone functions for SIP registration/audio if needed
  const { 
    hangup: softphoneHangup, 
    incomingCall: softphoneIncomingCall,
    currentCall: softphoneCall,
    answerCall: sipAnswerCall,
    prepareAutoAnswer,
    clearAutoAnswer,
    setHold: softphoneSetHold,
    muted: softphoneMuted,
    toggleMute: softphoneToggleMute
  } = useSoftphone();

  const normalizedCallStatus = String(callStatus || '').trim().toLowerCase();
  const normalizedAgentMode = String(agentMode || '').trim().toLowerCase();
  const isOutboundMode = normalizedAgentMode === 'outbound';
  const hasActiveCall =
    isLive ||
    ['ringing', 'dialing', 'in-call', 'connected'].includes(normalizedCallStatus);

  useIdleLogout({
    enabled: FRONT_OFFICE_IDLE_ENABLED,
    timeoutMs: FRONT_OFFICE_IDLE_MS,
    pause: hasActiveCall,
    onIdle: () => {
      navigate('/logout', {
        replace: true,
        state: {
          logoutNotice: `Logged out after ${FRONT_OFFICE_IDLE_MINUTES} minutes of inactivity.`
        }
      });
    }
  });

  useEffect(() => {
    localStorage.setItem('slacc-theme', theme);
  }, [theme]);

  // Inbound SIP sessions do not expose an ARI destination. Use the real call-state
  // channel resolved while the SIP popup was ringing so call controls stay available.
  const currentChannel =
    ariCurrentChannel ||
    (softphoneCall ? sipControlChannelId : '') ||
    softphoneCall?.destination;
  useEffect(() => {
    if (!softphoneCall) {
      setSipControlChannelId((prev) => (prev ? '' : prev));
    }
  }, [softphoneCall]);
  const currentChannelId = useMemo(() => {
    if (!currentChannel) return '';
    if (typeof currentChannel === 'string') return currentChannel;
    return currentChannel.id || currentChannel.channelId || '';
  }, [currentChannel]);
  const activeChannelInfo = useMemo(() => {
    if (!currentChannelId) return null;
    return activeChannels.find((channel) => String(channel.channelId || '').trim() === String(currentChannelId).trim()) || null;
  }, [activeChannels, currentChannelId]);
  const [, setRecordingTick] = useState(0);
  const isRecordingActive = Boolean(activeChannelInfo?.isRecording || activeChannelInfo?.recordingName);

  useEffect(() => {
    if (!isRecordingActive) {
      setRecordingTick(0);
      return undefined;
    }

    const interval = setInterval(() => {
      setRecordingTick((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isRecordingActive, activeChannelInfo?.recordingStartedAt]);

  const recordingState = (() => {
    if (!isRecordingActive) {
      return {
        isRecording: false,
        durationSeconds: 0,
        recordingName: '',
        recordingStartedAt: ''
      };
    }

    const startedAtRaw = String(activeChannelInfo?.recordingStartedAt || '').trim();
    const startedAtMs = startedAtRaw ? new Date(startedAtRaw).getTime() : NaN;
    const durationSeconds =
      Number.isFinite(startedAtMs) && startedAtMs > 0
        ? Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000))
        : 0;

    return {
      isRecording: true,
      durationSeconds,
      recordingName: String(activeChannelInfo?.recordingName || '').trim(),
      recordingStartedAt: startedAtRaw
    };
  })();

  const handleTransferComplete = useCallback(() => {
    ariResetCurrentCall(ariCurrentChannel || '');
    setIsLive(false);
  }, [ariCurrentChannel, ariResetCurrentCall]);

  const handleAttendedTransferComplete = useCallback(() => {
    if (softphoneCall) {
      softphoneHangup();
    }
    ariResetCurrentCall(ariCurrentChannel || '');
    setSipControlChannelId('');
    setIsLive(false);
    setCallDuration(0);
  }, [ariCurrentChannel, ariResetCurrentCall, softphoneCall, softphoneHangup]);

  const activeCallLink = useMemo(() => {
    const fromActiveChannel = getCallLinkFields(activeChannelInfo || {});
    if (fromActiveChannel.uniqueId || fromActiveChannel.channelId) {
      return {
        uniqueId: fromActiveChannel.uniqueId,
        channelId: fromActiveChannel.channelId || currentChannelId
      };
    }

    const liveIncomingCalls = Array.isArray(incomingCalls) ? incomingCalls : [];
    const matchingIncomingCall =
      liveIncomingCalls.find((call) => String(call?.channelId || '').trim() === String(currentChannelId || '').trim()) ||
      liveIncomingCalls[0] ||
      null;
    const fromIncomingCall = getCallLinkFields(matchingIncomingCall || {});

    return {
      uniqueId: fromIncomingCall.uniqueId,
      channelId: fromIncomingCall.channelId || currentChannelId
    };
  }, [activeChannelInfo, currentChannelId, incomingCalls]);

const createInitialFormData = useCallback(() => ({
    // identity
    cli: '',
    PhoneNo: '',
    contactId: '',
    name: '',
    gender: '',
    age: '',
    cnic: '',
    city: '',
    district: '',
    province: '',
    country: '',
    heardFrom: '',
    fatherHusbandName: '',
    salary: '',
    address: '',
    callback1: '',
    callback2: '',
    religion: '',
    maritalStatus: 'N/A',
    remarks: '',

    // detail
    queryId: '',
    status: '',
    launchedBy: launchedByDefault,
    CreatedBy: launchedByDefault,
    acceptedBy: '',
    launchedOn: '',
    acceptedOn: '',
    mainCategory: '',
    category: '',
    subCategory: '',
    faq: '',
    referredTo: false,
    referredToAgent: '',
    shortQuery: false,
    priority: 'Normal',
    source: SOURCE_OPTIONS[0],
    furtherHelp: '',
    sindhGovDepartment: 'N/A',
    query: '',
    solution: '',
    adrQuery: false,
    covid: false,
    ageAboveFifty: false,
    domesticViolence: false,
    womenProperty: false,
    womenRelated: false,
    mainTags: '',
    tagMfl: '',
    tagWrlmp: '',
    oldTags: [],
    forMfl: [],
    forWrlmp: [],
    callerId: '',
    callStateUniqueId: '',
    callStateChannelId: '',
    office: ''
  }), [launchedByDefault]);

  const [formData, setFormData] = useState(() => {
    const base = createInitialFormData();
    try {
      const raw = localStorage.getItem(FORM_STORAGE_KEY);
      if (!raw) return base;
      const saved = JSON.parse(raw);
      if (!saved || typeof saved !== 'object') return base;
      const restored = { ...base, ...saved };
      const restoredDepartment = String(restored.sindhGovDepartment || '').trim().toLowerCase();
      if (!String(restored.queryId || '').trim() && ['', 'select', '--select--'].includes(restoredDepartment)) {
        restored.sindhGovDepartment = 'N/A';
      }
      const restoredMaritalStatus = String(restored.maritalStatus || '').trim().toLowerCase();
      if (['', 'select', '--select--'].includes(restoredMaritalStatus)) {
        restored.maritalStatus = 'N/A';
      }
      return restored;
    } catch {
      return base;
    }
  });
  const [isNewQuery, setIsNewQuery] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [identityErrors, setIdentityErrors] = useState([]);
  const [detailsAutoSaveStatus, setDetailsAutoSaveStatus] = useState({ state: 'idle', savedAt: '' });
  const formActionInFlightRef = useRef(false);
  const detailsAutoSaveTimerRef = useRef(null);
  const detailsAutoSaveInFlightRef = useRef(false);
  const detailsAutoSaveActiveKeyRef = useRef('');
  const detailsAutoSavePausedRef = useRef(false);
  const detailsAutoSavePromiseRef = useRef(null);
  const detailsAutoSavePendingRef = useRef(null);
  const detailsAutoSaveLatestRef = useRef(null);
  const detailsAutoSaveLastSavedKeyRef = useRef('');
  const detailsAutoSaveQueryIdRef = useRef('');
  const detailsAutoSaveVersionRef = useRef(null);
  const detailsAutoSaveHydratingRef = useRef(false);
  const detailsAutoSaveUserDirtyRef = useRef(false);
  const detailsAutoSaveGenerationRef = useRef(0);
  const detailsAutoSaveMountedRef = useRef(true);
  const executeDetailsAutoSaveRef = useRef(null);

  const resetDetailsAutoSave = useCallback(({ queryId = '', version = null, hydrating = false } = {}) => {
    if (detailsAutoSaveTimerRef.current) {
      clearTimeout(detailsAutoSaveTimerRef.current);
      detailsAutoSaveTimerRef.current = null;
    }
    detailsAutoSaveGenerationRef.current += 1;
    detailsAutoSavePendingRef.current = null;
    detailsAutoSaveLatestRef.current = null;
    detailsAutoSaveLastSavedKeyRef.current = '';
    detailsAutoSaveQueryIdRef.current = String(queryId || '').trim();
    detailsAutoSaveVersionRef.current = normalizeDraftVersion(version);
    detailsAutoSaveHydratingRef.current = Boolean(hydrating);
    detailsAutoSaveUserDirtyRef.current = false;
    if (detailsAutoSaveMountedRef.current) {
      setDetailsAutoSaveStatus({ state: 'idle', savedAt: '' });
    }
  }, []);

  useEffect(() => {
    detailsAutoSaveMountedRef.current = true;
    return () => {
      complaintDetailsRequestIdRef.current += 1;
      detailsAutoSaveMountedRef.current = false;
      detailsAutoSaveGenerationRef.current += 1;
      detailsAutoSavePendingRef.current = null;
      if (detailsAutoSaveTimerRef.current) {
        clearTimeout(detailsAutoSaveTimerRef.current);
        detailsAutoSaveTimerRef.current = null;
      }
    };
  }, []);

  const clearIdentityTab = useCallback(() => {
    setIdentityErrors([]);
    return getEmptyIdentityFields();
  }, []);

  useEffect(() => {
    if (user) {
      const nameVal =
        user.loginName ||
        user.username ||
        launchedByDefault;

      setFormData(prev => ({
        ...prev,
        launchedBy: nameVal,
        CreatedBy: nameVal
      }));
    }
  }, [launchedByDefault, user]);

  useEffect(() => {
    const topBarNode = topBarRef.current;
    if (!topBarNode) return undefined;

    const updateTopBarHeight = () => {
      const nextHeight = Math.ceil(topBarNode.getBoundingClientRect().height || 0);
      if (nextHeight > 0) {
        setTopBarHeight((prev) => (prev === nextHeight ? prev : nextHeight));
      }
    };

    updateTopBarHeight();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateTopBarHeight());
      resizeObserver.observe(topBarNode);
    }

    window.addEventListener('resize', updateTopBarHeight);

    return () => {
      window.removeEventListener('resize', updateTopBarHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
      } catch (err) {
        console.error('Persist form error:', err);
      }
    }, FORM_STORAGE_WRITE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [formData]);

  useEffect(() => {
    try {
      localStorage.setItem(AVAILABILITY_STORAGE_KEY, agentAvailability);
    } catch (err) {
      console.error('Persist availability error:', err);
    }
  }, [agentAvailability]);

  useEffect(() => {
    try {
      localStorage.setItem(AGENT_MODE_STORAGE_KEY, agentMode);
    } catch (err) {
      console.error('Persist agent mode error:', err);
    }
  }, [agentMode]);

  useEffect(() => {
    const normalizedMode = String(agentMode || '').trim().toLowerCase();
    if (normalizedMode === 'outbound' && agentAvailability !== 'Ready') {
      setAgentAvailability('Ready');
    }
  }, [agentAvailability, agentMode]);

  useEffect(() => {
    const hasSoftphoneCall = Boolean(softphoneCall);
    const hasAriActiveChannel = Array.isArray(activeChannels) && activeChannels.length > 0;
    const shouldBeLive =
      hasSoftphoneCall ||
      (isConnected &&
        (callStatus === 'ringing' ||
          callStatus === 'dialing' ||
          callStatus === 'connecting')) ||
      (isConnected &&
        (callStatus === 'in-call' || callStatus === 'connected') &&
        hasAriActiveChannel);

    setIsLive((prev) => (prev === shouldBeLive ? prev : shouldBeLive));
  }, [activeChannels, callStatus, isConnected, softphoneCall]);

  useEffect(() => {
    if (!currentChannelId) return;
    const queryId = String(
      pickFirstValue(
        activeChannelInfo?.queryId,
        Array.isArray(incomingCalls) ? incomingCalls[0]?.queryId : '',
        formData.queryId
      ) || ''
    ).trim();
    const callerNumber = String(
      pickFirstValue(
        activeChannelInfo?.callerNumber,
        activeChannelInfo?.calledNumber,
        Array.isArray(incomingCalls) ? incomingCalls[0]?.callerNumber : '',
        currentCallerId,
        formData.cli
      ) || ''
    ).trim();
    if (!queryId && !callerNumber) return;

    const controller = new AbortController();
    fetch(CALL_CONTEXT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      signal: controller.signal,
      body: JSON.stringify({
        channelId: currentChannelId,
        queryId,
        callerNumber
      })
    }).catch(() => {});

    return () => controller.abort();
  }, [activeChannelInfo?.calledNumber, activeChannelInfo?.callerNumber, activeChannelInfo?.queryId, currentCallerId, currentChannelId, formData.cli, formData.queryId, incomingCalls]);

  useEffect(() => {
    const nextUniqueId = String(activeCallLink.uniqueId || '').trim();
    const nextChannelId = String(activeCallLink.channelId || currentChannelId || '').trim();
    if (!nextUniqueId && !nextChannelId) return;

    setFormData((prev) => {
      const resolvedUniqueId = nextUniqueId || String(prev.callStateUniqueId || '').trim();
      const resolvedChannelId = nextChannelId || String(prev.callStateChannelId || '').trim();

      if (
        String(prev.callStateUniqueId || '').trim() === resolvedUniqueId &&
        String(prev.callStateChannelId || '').trim() === resolvedChannelId
      ) {
        return prev;
      }

      return {
        ...prev,
        callStateUniqueId: resolvedUniqueId,
        callStateChannelId: resolvedChannelId
      };
    });
  }, [activeCallLink.channelId, activeCallLink.uniqueId, currentChannelId]);

  const [transcript, setTranscript] = useState([]);
  const [transcriptRecord, setTranscriptRecord] = useState(null);
  const [transcriptStatus, setTranscriptStatus] = useState({
    state: LIVE_TRANSCRIPTION_ENABLED ? 'idle' : 'disabled',
    message: LIVE_TRANSCRIPTION_ENABLED ? '' : 'Live transcription disabled.'
  });
  const [agentRecordingFilters, setAgentRecordingFilters] = useState(getDefaultRecordingFilters);
  const [agentRecordingFilterDraft, setAgentRecordingFilterDraft] = useState(getDefaultRecordingFilters);
  const [agentRecordings, setAgentRecordings] = useState([]);
  const [agentRecordingsLoading, setAgentRecordingsLoading] = useState(false);
  const [agentRecordingsError, setAgentRecordingsError] = useState('');
  const [selectedRecordingKey, setSelectedRecordingKey] = useState('');
  const [agentSpeakerId, setAgentSpeakerId] = useState('');

  const isAgeAboveFiftyValue = useCallback((value) => {
    const age = Number(String(value ?? '').trim());
    return Number.isFinite(age) && age > 50;
  }, []);
  const transcriptSpeakerRoster = useMemo(
    () => getTranscriptSpeakerRoster(transcriptRecord || {}),
    [transcriptRecord]
  );

  const [timelineData] = useState([
    {
      type: 'CALL_START',
      user: 'Asad',
      timestamp: '03:45 PM, Aug 01, 2024',
      content: 'Call started with caller 0316-XXXXXXX.'
    },
    {
      type: 'NOTE',
      user: 'Asad',
      timestamp: '03:48 PM, Aug 01, 2024',
      content:
        'Caller is facing an issue with their landlord regarding an eviction notice. Rental agreement exists.'
    }
  ]);
  const callStatusRef = useRef({ ringing: false, answered: false, failed: false });
  const outboundRingbackAudioRef = useRef(null);
  const outboundRingbackFallbackTimerRef = useRef(null);
  const outboundRingbackStopTimerRef = useRef(null);
  const callResponseAudioRef = useRef(null);
  const callResponseAudioContextRef = useRef(null);
  const callResponseAudioUnlockedRef = useRef(false);
  const lastPlayedCallResponseRef = useRef('');
  const outboundCallResponseArmedRef = useRef(false);
  const outboundDialCancelRequestedRef = useRef(false);
  const appendCallStatusLog = useCallback((text) => {
    const timestamp = new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
    setTranscript((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.text === text) return prev;
      return [...prev, { speaker: 'System', text, timestamp }];
    });
  }, []);

  const stopOutboundRingback = useCallback(() => {
    if (outboundRingbackFallbackTimerRef.current) {
      window.clearTimeout(outboundRingbackFallbackTimerRef.current);
      outboundRingbackFallbackTimerRef.current = null;
    }

    if (outboundRingbackStopTimerRef.current) {
      window.clearInterval(outboundRingbackStopTimerRef.current);
      outboundRingbackStopTimerRef.current = null;
    }

    const audio = outboundRingbackAudioRef.current;
    if (!audio) return;
    if (audio.dataset) {
      audio.dataset.ringbackStopped = 'true';
    }
    audio.onended = null;
    audio.pause();
    audio.currentTime = 0;
  }, []);

  const startOutboundRingback = useCallback(async () => {
    if (typeof window === 'undefined') return false;

    const publicUrl = String(process.env.PUBLIC_URL || '').replace(/\/+$/, '');
    const ringToneSrc = publicUrl
      ? `${publicUrl}/${OUTBOUND_RINGBACK_ASSET_PATH}`
      : `/${OUTBOUND_RINGBACK_ASSET_PATH}`;
    let audio = outboundRingbackAudioRef.current;
    if (!audio) {
      audio = new Audio(ringToneSrc);
      audio.preload = 'auto';
      outboundRingbackAudioRef.current = audio;
    } else if (audio.dataset?.ringbackSrc !== ringToneSrc) {
      audio.pause();
      audio.src = ringToneSrc;
      audio.preload = 'auto';
    }
    if (audio.dataset) {
      audio.dataset.ringbackSrc = ringToneSrc;
      audio.dataset.ringbackStopped = 'false';
    }

    const replayRingback = () => {
      if (audio.dataset?.ringbackStopped === 'true') return;
      try {
        audio.currentTime = 0;
        const replayAttempt = audio.play();
        if (replayAttempt && typeof replayAttempt.catch === 'function') {
          replayAttempt.catch(() => {});
        }
      } catch {
        // The interval below will retry while the call remains in fallback ringback state.
      }
    };

    audio.loop = true;
    audio.volume = 0.55;
    audio.onended = replayRingback;

    try {
      if (outboundRingbackStopTimerRef.current) {
        window.clearInterval(outboundRingbackStopTimerRef.current);
      }
      outboundRingbackStopTimerRef.current = window.setInterval(() => {
        if (audio.dataset?.ringbackStopped === 'true') return;
        if (audio.paused || audio.ended) {
          replayRingback();
        }
      }, 1000);

      if (!audio.paused && !audio.ended) {
        return true;
      }

      audio.currentTime = 0;
      const playAttempt = audio.play();
      if (playAttempt && typeof playAttempt.then === 'function') {
        await playAttempt;
      }
      return true;
    } catch (error) {
      console.warn('Outbound ringback playback failed:', error);
      return false;
    }
  }, []);

  const scheduleOutboundRingbackFallback = useCallback(() => {
    if (typeof window === 'undefined') return;

    const audio = outboundRingbackAudioRef.current;
    if (audio && !audio.paused && !audio.ended) return;
    if (outboundRingbackFallbackTimerRef.current) return;

    outboundRingbackFallbackTimerRef.current = window.setTimeout(() => {
      outboundRingbackFallbackTimerRef.current = null;
      void startOutboundRingback();
    }, OUTBOUND_RINGBACK_FALLBACK_DELAY_MS);
  }, [startOutboundRingback]);

  const stopCallResponsePrompt = useCallback(() => {
    const audio = callResponseAudioRef.current;
    if (audio) {
      audio.pause();
      try {
        audio.currentTime = 0;
      } catch {
        // Some browsers reject currentTime before metadata is loaded.
      }
    }

    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, []);

  const unlockCallResponseAudio = useCallback(async () => {
    if (typeof window === 'undefined') return false;

    try {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (AudioContextCtor) {
        let context = callResponseAudioContextRef.current;
        if (!context || context.state === 'closed') {
          context = new AudioContextCtor();
          callResponseAudioContextRef.current = context;
        }
        if (context.state === 'suspended') {
          await context.resume();
        }

        const source = context.createBufferSource();
        source.buffer = context.createBuffer(1, 1, 22050);
        source.connect(context.destination);
        source.start(0);
      }

      callResponseAudioUnlockedRef.current = true;
      return true;
    } catch (error) {
      console.warn('Call response audio unlock failed:', error);
      callResponseAudioUnlockedRef.current = false;
      return false;
    }
  }, []);

  const speakCallResponsePrompt = useCallback((text) => {
    if (typeof window === 'undefined' || !window.speechSynthesis || !text) {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      let settled = false;
      let timeoutId = null;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        if (timeoutId) window.clearTimeout(timeoutId);
        resolve(value);
      };

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.95;
      utterance.volume = 1;
      utterance.onend = () => finish(true);
      utterance.onerror = () => finish(false);
      timeoutId = window.setTimeout(() => finish(true), 3500);

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    });
  }, []);

  const playCallResponsePrompt = useCallback(async (prompt) => {
    if (!prompt) return false;
    if (typeof window === 'undefined') return false;

    stopOutboundRingback();
    stopCallResponsePrompt();

    const playAudioSource = async (src) => {
      let audio = callResponseAudioRef.current;
      if (!audio) {
        audio = new Audio();
        callResponseAudioRef.current = audio;
      }

      audio.pause();
      audio.src = src;
      audio.preload = 'auto';
      audio.muted = false;
      audio.volume = 0.9;
      audio.load();

      return await new Promise((resolve) => {
        let settled = false;
        let timeoutId = null;
        let onPlaying = null;
        let onEnded = null;
        let onError = null;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          audio.removeEventListener('playing', onPlaying);
          audio.removeEventListener('ended', onEnded);
          audio.removeEventListener('error', onError);
          if (timeoutId) window.clearTimeout(timeoutId);
          resolve(value);
        };
        const markStarted = () => {
          if (timeoutId) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
          }
        };
        onPlaying = markStarted;
        onEnded = () => finish(true);
        onError = () => finish(false);
        timeoutId = window.setTimeout(() => finish(false), 1500);

        audio.addEventListener('playing', onPlaying, { once: true });
        audio.addEventListener('ended', onEnded, { once: true });
        audio.addEventListener('error', onError, { once: true });

        const playAttempt = audio.play();
        if (playAttempt && typeof playAttempt.catch === 'function') {
          playAttempt.catch(() => finish(false));
        }
        if (playAttempt && typeof playAttempt.then === 'function') {
          playAttempt.then(markStarted).catch(() => {});
        }
      });
    };

    const promptFiles = Array.isArray(prompt.files) && prompt.files.length
      ? prompt.files
      : [prompt.file].filter(Boolean);
    const promptSources = promptFiles.flatMap((fileName) =>
      buildPublicAssetCandidates(`CallResponses/${fileName}`)
    );

    for (const promptSrc of promptSources) {
      try {
        const played = await playAudioSource(promptSrc);
        if (played) return true;
      } catch (error) {
        console.warn('Call response prompt playback failed:', promptSrc, error);
      }
    }

    if (promptSources.length) {
      console.warn('No call response recording could be played:', {
        prompt: prompt.key,
        tried: promptSources
      });
    }

    return speakCallResponsePrompt(prompt.text);
  }, [speakCallResponsePrompt, stopCallResponsePrompt, stopOutboundRingback]);

  const getCallResponsePrompt = useCallback((info = {}) => {
    const sipCode = String(info?.sipResponseCode || '').trim();
    const q850Cause = String(info?.q850Cause || '').trim();
    const dialStatus = String(info?.dialStatus || '').trim().toUpperCase();
    const q850Prompt = CALL_RESPONSE_PROMPTS[CALL_RESPONSE_BY_Q850_CAUSE[q850Cause]];
    if (q850Prompt) return q850Prompt;

    const sipPrompt = CALL_RESPONSE_PROMPTS[sipCode];
    if (sipPrompt) return sipPrompt;

    return CALL_RESPONSE_PROMPTS[CALL_RESPONSE_BY_DIAL_STATUS[dialStatus]] || null;
  }, []);

  const hangupOutboundAfterResponsePrompt = useCallback(async (channelId = '') => {
    const targetChannel = String(
      channelId ||
      currentChannelId ||
      (typeof ariCurrentChannel === 'string' ? ariCurrentChannel : ariCurrentChannel?.id || ariCurrentChannel?.channelId || '') ||
      ''
    ).trim();

    try {
      if (targetChannel) {
        await ariHangupCall(targetChannel);
      } else if (softphoneCall) {
        softphoneHangup();
      }
    } catch (error) {
      console.warn('Auto hangup after response prompt failed:', error?.message || error);
      if (targetChannel) {
        ariResetCurrentCall(targetChannel);
      }
    } finally {
      setIsLive(false);
      setCallDuration(0);
    }
  }, [
    ariCurrentChannel,
    ariHangupCall,
    ariResetCurrentCall,
    currentChannelId,
    softphoneCall,
    softphoneHangup
  ]);

  const clearTranscriptPolling = useCallback(() => {
    if (transcriptPollTimeoutRef.current) {
      window.clearTimeout(transcriptPollTimeoutRef.current);
      transcriptPollTimeoutRef.current = null;
    }
  }, []);

  const applyTranscriptRecord = useCallback((record) => {
    if (!record || typeof record !== 'object') return;

    setTranscriptRecord(record);
    setTranscriptStatus({
      state: String(record.status || '').trim().toLowerCase() || 'idle',
      message: describeTranscriptStatus(record, '')
    });

    if (String(record.status || '').trim().toLowerCase() !== 'completed') {
      setTranscript([]);
    }
  }, []);

  useEffect(() => {
    if (!transcriptRecord || String(transcriptRecord.status || '').trim().toLowerCase() !== 'completed') {
      return;
    }

    setTranscript(buildTranscriptEntries(transcriptRecord, { agentSpeakerId }));
  }, [agentSpeakerId, transcriptRecord]);

  useEffect(() => {
    if (!transcriptSpeakerRoster.length) {
      setAgentSpeakerId('');
      return;
    }

    const hasSelectedSpeaker = transcriptSpeakerRoster.some((speaker) => speaker.speakerId === agentSpeakerId);
    if (agentSpeakerId && !hasSelectedSpeaker) {
      setAgentSpeakerId('');
    }
  }, [agentSpeakerId, transcriptSpeakerRoster]);

  useEffect(() => {
    setAgentRecordingFilterDraft(agentRecordingFilters);
  }, [agentRecordingFilters]);

  const handleRecordingFilterDraftChange = useCallback((field, value) => {
    setAgentRecordingFilterDraft((prev) => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleApplyRecordingFilters = useCallback(() => {
    setAgentRecordingFilters((prev) => {
      if (
        prev?.from === agentRecordingFilterDraft?.from &&
        prev?.to === agentRecordingFilterDraft?.to &&
        prev?.callerNumber === agentRecordingFilterDraft?.callerNumber &&
        prev?.recordingType === agentRecordingFilterDraft?.recordingType
      ) {
        return prev;
      }

      return {
        ...prev,
        ...agentRecordingFilterDraft
      };
    });
  }, [agentRecordingFilterDraft]);

  const isRecordingFilterDirty = useMemo(
    () =>
      agentRecordingFilters?.from !== agentRecordingFilterDraft?.from ||
      agentRecordingFilters?.to !== agentRecordingFilterDraft?.to ||
      agentRecordingFilters?.callerNumber !== agentRecordingFilterDraft?.callerNumber ||
      agentRecordingFilters?.recordingType !== agentRecordingFilterDraft?.recordingType,
    [agentRecordingFilterDraft, agentRecordingFilters]
  );

  const buildRecordingKey = useCallback((recording = {}) => (
    [
      pickFirstValue(recording.recordingName, recording.RecordingName),
      pickFirstValue(recording.UniqueId, recording.uniqueId),
      pickFirstValue(recording.CallStartedAt, recording.callStartedAt, recording.CallEndedAt, recording.callEndedAt)
    ].filter(Boolean).join('|')
  ), []);

  const queueTranscriptForLookup = useCallback(async (lookup = {}, options = {}) => {
    if (!LIVE_TRANSCRIPTION_ENABLED) return;

    const normalizedLookup = {
      uniqueId: pickFirstValue(
        lookup.uniqueId,
        lookup.stableId,
        lookup.linkedId
      ),
      linkedId: pickFirstValue(lookup.linkedId, lookup.uniqueId),
      stableId: pickFirstValue(lookup.stableId, lookup.uniqueId, lookup.linkedId),
      channelId: pickFirstValue(lookup.channelId),
      queryId: pickFirstValue(lookup.queryId),
      callerNumber: pickFirstValue(lookup.callerNumber),
      callerName: pickFirstValue(lookup.callerName),
      recordingName: pickFirstValue(lookup.recordingName),
      outputLanguage: pickFirstValue(lookup.outputLanguage)
    };

    if (!normalizedLookup.recordingName && !normalizedLookup.uniqueId && !normalizedLookup.channelId) {
      transcriptUiDebug('queue.skip-missing-lookup', { lookup, normalizedLookup });
      return;
    }

    transcriptUiDebug('queue.start', {
      normalizedLookup,
      options,
      maxAttempts: TRANSCRIPT_POLL_MAX_ATTEMPTS
    });

    clearTranscriptPolling();
    setTranscriptStatus({
      state: 'processing',
      message: options.statusMessage || 'The call has ended. Preparing the transcript...'
    });

    try {
      const queued = await queueCallTranscript(normalizedLookup, {
        force: Boolean(options.force)
      });
      transcriptUiDebug('queue.response', {
        status: queued?.transcript?.status || '',
        recordingName: queued?.transcript?.recordingName || '',
        attempts: queued?.transcript?.attempts || 0,
        error: queued?.transcript?.error || '',
        transcript: queued?.transcript || null
      });
      if (queued?.transcript) {
        applyTranscriptRecord(queued.transcript);
      }
    } catch (error) {
      transcriptUiDebug('queue.error', {
        message: error.message || String(error),
        status: error.status,
        payload: error.payload || null
      });
      setTranscriptStatus({
        state: 'failed',
        message: error.message || 'The transcript could not be queued.'
      });
      return;
    }

    let attempts = 0;
    const pollTranscript = async () => {
      attempts += 1;
      transcriptUiDebug('poll.start', { attempts, normalizedLookup });
      try {
        const result = await fetchCallTranscript(normalizedLookup, { start: true });
        const record = result?.transcript || null;
        transcriptUiDebug('poll.response', {
          attempts,
          status: record?.status || '',
          recordingName: record?.recordingName || '',
          recordAttempts: record?.attempts || 0,
          error: record?.error || '',
          textLength: String(record?.text || '').length,
          noSpeechDetected: Boolean(record?.noSpeechDetected),
          transcriptionOptions: record?.transcriptionOptions || null,
          diarization: record?.diarization || null,
          record
        });
        if (record) {
          applyTranscriptRecord(record);
          const status = String(record.status || '').trim().toLowerCase();
          if (status === 'completed' || status === 'failed') {
            transcriptUiDebug('poll.stop-terminal-status', { attempts, status, error: record.error || '' });
            transcriptPollTimeoutRef.current = null;
            return;
          }
        }
      } catch (error) {
        transcriptUiDebug('poll.error', {
          attempts,
          message: error.message || String(error),
          status: error.status,
          payload: error.payload || null
        });
        if (attempts >= TRANSCRIPT_POLL_MAX_ATTEMPTS) {
          setTranscriptStatus({
            state: 'failed',
            message: error.message || 'The transcript could not be loaded.'
          });
          transcriptPollTimeoutRef.current = null;
          return;
        }
      }

      if (attempts === TRANSCRIPT_POLL_LONG_WAIT_ATTEMPT) {
        setTranscriptStatus({
          state: 'processing',
          message: 'The recording is still being transcribed. This can take a few minutes for longer calls.'
        });
      }

      if (attempts >= TRANSCRIPT_POLL_MAX_ATTEMPTS) {
        transcriptUiDebug('poll.timeout', { attempts, maxAttempts: TRANSCRIPT_POLL_MAX_ATTEMPTS });
        setTranscriptStatus({
          state: 'failed',
          message: 'Transcription is taking longer than expected. Please reopen the recording to retry.'
        });
        transcriptPollTimeoutRef.current = null;
        return;
      }

      transcriptPollTimeoutRef.current = window.setTimeout(pollTranscript, 3000);
      transcriptUiDebug('poll.schedule-next', { attempts, delayMs: 3000 });
    };

    transcriptPollTimeoutRef.current = window.setTimeout(pollTranscript, 2500);
    transcriptUiDebug('poll.schedule-first', { delayMs: 2500 });
  }, [applyTranscriptRecord, clearTranscriptPolling]);

  const handleRecordingSelect = useCallback((recording, outputLanguage = 'original') => {
    const requestedOutputLanguage = outputLanguage === 'en' ? 'en' : 'original';
    const lookup = {
      ...buildRecordingLookupFromCall(recording),
      outputLanguage: requestedOutputLanguage
    };
    if (!lookup.recordingName && !lookup.uniqueId && !lookup.channelId) {
      return;
    }

    const statusMessage = requestedOutputLanguage === 'en'
      ? 'Translating the selected recording to English...'
      : 'Loading the transcript for the selected recording...';
    setSelectedRecordingKey(buildRecordingKey(recording));
    setTranscriptRecord(null);
    setTranscript([]);
    setTranscriptStatus({
      state: 'processing',
      message: statusMessage
    });
    void queueTranscriptForLookup(lookup, {
      statusMessage,
      force: true
    });
  }, [buildRecordingKey, queueTranscriptForLookup]);

  useEffect(() => {
    if (!LIVE_TRANSCRIPTION_ENABLED || !agentId) {
      setAgentRecordings([]);
      setAgentRecordingsError('');
      setAgentRecordingsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadAgentRecordings = async () => {
      try {
        setAgentRecordingsLoading(true);
        setAgentRecordingsError('');
        const result = await fetchAgentRecordings({
          agentId,
          from: agentRecordingFilters.from,
          to: agentRecordingFilters.to,
          callerNumber: agentRecordingFilters.callerNumber,
          limit: 40
        });
        if (cancelled) return;

        const calls = Array.isArray(result?.calls) ? result.calls : [];
        const recordingType = String(agentRecordingFilters.recordingType || 'all').trim().toLowerCase();
        const filteredCalls = calls.filter((call) => {
          const recordingName = pickFirstValue(call.recordingName, call.RecordingName);
          if (!recordingName) return false;

          const callType = getRecordingCallType(call);
          if (recordingType === 'agent_call') return callType === 'agent_call';
          if (recordingType === 'after_hours_voicemail') return callType === 'after_hours_voicemail';
          if (recordingType === 'queue_voicemail') return callType === 'queue_voicemail';
          if (recordingType === 'voicemail') return isVoicemailRecordingItem(call);
          if (recordingType === 'agent') return !isVoicemailRecordingItem(call);
          return true;
        });
        const nextRecordings = filteredCalls.map((call) => ({
          ...call,
          recordingKey: buildRecordingKey(call)
        }));
        const recordingTimeValue = (call) => {
          const raw = pickFirstValue(
            call.CallEndedAt, call.callEndedAt,
            call.CallStartedAt, call.callStartedAt,
            call.RecordingStoppedAt, call.recordingStoppedAt,
            call.RecordingStartedAt, call.recordingStartedAt,
            call.CreatedAt, call.createdAt
          );
          let ts = Date.parse(raw);
          if (Number.isNaN(ts)) ts = Date.parse(raw.replace(' ', 'T'));
          if (Number.isNaN(ts)) ts = Date.parse(raw.replace(' ', 'T').replace(/\.\d+/, ''));
          return Number.isNaN(ts) ? 0 : ts;
        };
        nextRecordings.sort((a, b) => recordingTimeValue(b) - recordingTimeValue(a));

        setAgentRecordings(nextRecordings);
        setSelectedRecordingKey((prev) => (
          prev && nextRecordings.some((item) => item.recordingKey === prev) ? prev : ''
        ));
      } catch (error) {
        if (cancelled) return;
        setAgentRecordings([]);
        setAgentRecordingsError(error.message || 'The recordings could not be loaded.');
      } finally {
        if (!cancelled) {
          setAgentRecordingsLoading(false);
        }
      }
    };

    void loadAgentRecordings();

    return () => {
      cancelled = true;
    };
  }, [
    agentId,
    agentRecordingFilters.callerNumber,
    agentRecordingFilters.from,
    agentRecordingFilters.recordingType,
    agentRecordingFilters.to,
    buildRecordingKey
  ]);

  const handleFieldUpdate = (field, value) => {
    setFormData(prev => {
      const next = {
        ...prev,
        [field]: value
      };
      if (field === 'age') {
        next.ageAboveFifty = isAgeAboveFiftyValue(value);
      }
      return next;
    });
    setIdentityErrors(prev => (prev.length ? [] : prev));
  };

  const extractQueryId = useCallback((result) => {
    if (!result || typeof result !== 'object') return '';
    const data =
      result.data ??
      result.Data ??
      result.result ??
      result.complaint ??
      result.Complaint ??
      result.contact ??
      result.Contact ??
      result.record ??
      null;
    const nested = Array.isArray(data) ? data[0] : data;
    return pickFirstValue(
      result.QueryID,
      result.QueryId,
      result.queryId,
      result.QID,
      result.qid,
      result.LastQID,
      result.ComplaintID,
      result.complaintId,
      result.complaint_id,
      result.complaintid,
      result.id,
      result.ID,
      nested?.QueryID,
      nested?.QueryId,
      nested?.queryId,
      nested?.QID,
      nested?.qid,
      nested?.LastQID,
      nested?.ComplaintID,
      nested?.complaintId,
      nested?.complaint_id,
      nested?.complaintid,
      nested?.id,
      nested?.ID
    );
  }, []);

  const extractContactId = useCallback((result) => {
    if (!result || typeof result !== 'object') return '';
    const data = result.data ?? result.Data ?? result.result ?? result.contact ?? result.Contact ?? result.record ?? null;
    const nested = Array.isArray(data) ? data[0] : data;
    return pickFirstValue(
      result.ContactID,
      result.ContactId,
      result.contactId,
      result.contact_id,
      result.CID,
      result.contact,
      result.id,
      result.ID,
      nested?.ContactID,
      nested?.ContactId,
      nested?.contactId,
      nested?.contact_id,
      nested?.CID,
      nested?.contact,
      nested?.id,
      nested?.ID
    );
  }, []);

  const validateIdentityBeforeSave = useCallback(() => {
    const isBlankSelect = value => {
      const trimmed = String(value ?? '').trim();
      if (!trimmed) return true;
      const lowered = trimmed.toLowerCase();
      return lowered === '--select--' || lowered === 'select';
    };
    const focusField = (id) => {
      const el = document.getElementById(id);
      if (el && typeof el.focus === 'function') {
        el.focus();
        el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
    };
    const fail = (message, fieldId) => {
      setIdentityErrors([message]);
      focusField(fieldId);
      return false;
    };

    if (isBlankSelect(formData.name)) return fail('Provide Full Name.', 'name');
     if (isBlankSelect(formData.gender)) return fail('Select Gender.', 'gender');
      if (isBlankSelect(formData.city)) return fail('Select City.', 'city');
      if (isBlankSelect(formData.district)) return fail('Select District.', 'district');

    if (isBlankSelect(formData.province)) return fail('Select Province.', 'province');
        if (isBlankSelect(formData.heardFrom)) return fail('Select Heard From.', 'heardFrom');
    if (isBlankSelect(formData.maritalStatus)) return fail('Select Marital Status.', 'maritalStatus');

   
   
    
    const ageValue = String(formData.age ?? '').trim();
    if (!ageValue) return fail('Provide Age.', 'age');
    if (!/^\d+$/.test(ageValue) || Number(ageValue) <= 0) {
      return fail('Age must be a positive number.', 'age');
    }

    setIdentityErrors([]);
    return true;
  }, [formData]);

  const toContactPayload = useCallback(() => {
    const createdByValue =
      user?.userId ??
      user?.username ??
      launchedByDefault;

    return {
      // contact basics
      PhoneNo: formData.cli || formData.PhoneNo || '',
      Name: formData.name || '',
      Gender: formData.gender || '',
      Age: formData.age || '',
      City: formData.city || '',
      AdditionalInfo2: formData.district || '',
      AdditionalInfo3: formData.province || '',
      AdditionalInfo4: formData.country || '',
      CNIC: formData.cnic || '',
      FatherName: formData.fatherHusbandName || '',
      CellNo: formData.callback1 || '',
      AdditionalInfo10: formData.callback2 || '',
      Salary: formData.salary || '',
      AdditionalInfo1: formData.salary || '',
      Address: formData.address || '',
      Religion: formData.religion || '',
      AdditionalInfo6: formData.religion || '',
      AdditionalInfo8: formData.maritalStatus || '',
      AdditionalInfo9: formData.remarks || '',
      AdditionalInfo5: formData.heardFrom || '',

      // identifiers
      QueryID: formData.queryId || '',
      CallerID: formData.callerId || '',

      // audit
      CreatedBy: createdByValue,
      ModifiedBy: createdByValue
    };
  }, [formData, launchedByDefault, user]);

  const handleNewCaller = useCallback(() => {
    complaintDetailsRequestIdRef.current += 1;
    resetDetailsAutoSave();
    setFormData(prev => {
      const base = createInitialFormData();
      const cliValue = String(prev.cli || prev.PhoneNo || '').trim();
      if (!cliValue) return base;
      return { ...base, cli: cliValue, PhoneNo: cliValue };
    });
    setCallerDataLoaded(false);
    setIdentityErrors([]);
    setIsNewQuery(false);
  }, [createInitialFormData, resetDetailsAutoSave]);

  const handleClearInfo = useCallback(() => {
    complaintDetailsRequestIdRef.current += 1;
    resetDetailsAutoSave();
    try {
      localStorage.removeItem(FORM_STORAGE_KEY);
    } catch (err) {
      console.error('Clear form storage error:', err);
    }
    setFormData(createInitialFormData());
    setCurrentCallerId('');
    setCallerDataLoaded(false);
    setIdentityErrors([]);
    setErrorMessage('');
    setClearSelectionsToken(prev => prev + 1);
    setIsNewQuery(false);
  }, [createInitialFormData, resetDetailsAutoSave]);

  const handleNewQuery = useCallback(() => {
    complaintDetailsRequestIdRef.current += 1;
    resetDetailsAutoSave();
    setFormData(prev => ({
      ...prev,
      ...getEmptyDetailFields(launchedByDefault),
    }));
    setComplaintDetail(null);
    setClearSelectionsToken(prev => prev + 1);
    setIsNewQuery(true);
  }, [launchedByDefault, resetDetailsAutoSave]);

  const handleSaveForm = useCallback(async () => {
    if (!validateIdentityBeforeSave()) return;
    try {
      const normalizeSavedSource = (value, fallback = '') => {
        const text = String(value ?? '').trim();
        if (!text) return fallback;
        const compact = text.toLowerCase().replace(/\s+/g, '');
        if (compact === 'incomingcall') return SOURCE_OPTIONS[0];
        if (compact === 'outboundcall' || compact === 'outboundcal') return SOURCE_OPTIONS[1];
        if (compact === 'voicemail') return SOURCE_OPTIONS[2];
        if (compact === 'email') return SOURCE_OPTIONS[3];
        return text;
      };

      const payload = toContactPayload();
      logApi('save-contact', { url: SAVE_CONTACT_URL, payload });
      const res = await fetch(SAVE_CONTACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });

      const text = await res.text();
      let result = null;
      try { result = text ? JSON.parse(text) : null; } catch { result = { message: text }; }
        if (res.ok) {
          const savedQueryId = extractQueryId(result);
          const savedContactId = extractContactId(result);
          const savedCallerId = savedContactId || savedQueryId || '';
          const successMessage = `Saved successfully${savedQueryId ? ` (Query ID: ${savedQueryId})` : ''}${savedContactId ? ` (Contact ID: ${savedContactId})` : ''}.`;
          setIdentityErrors([]);
          setErrorMessage('');
          setInfoMessage(successMessage);
          alert(successMessage);
          if (savedQueryId || savedContactId) {
            setFormData(prev => ({
              ...prev,
              ...(savedQueryId ? { queryId: savedQueryId } : {}),
              ...(savedContactId ? { contactId: savedContactId } : {}),
              ...(savedCallerId ? { callerId: savedCallerId } : {})
            }));
          }
          if (savedCallerId) {
            setCurrentCallerId(savedCallerId);
          }
          if (savedQueryId) {
            //load data after save on detail page
            try {
              const url = `${API_BASE}/complaintsLoad/${encodeURIComponent(savedQueryId)}`;
              logApi('complaint-load', { url });
              const detailRes = await fetch(url);
            if (!detailRes.ok) {
              const text = await detailRes.text();
              console.error('complaintsLoad error:', detailRes.status, text);
              return;
            }

            const detail = await detailRes.json();
            const row = detail?.data?.[0] ?? detail?.data ?? detail;
            if (!row || typeof row !== 'object') return;

              const asText = (value) => String(value ?? '').trim();
              const rowContactId = asText(
                row.CID || row.ContactID || row.contactId || row.contact_id || row.cid
              );
              const rowCallerId = rowContactId || asText(
                row.CallerID || row.CallersID || row.callerId
              );
              const flagIs = (value, match) =>
                asText(value).toLowerCase() === String(match).toLowerCase();
              const isTruthy = (value) => {
                const v = asText(value).toLowerCase();
                return v === '1' || v === 'true' || v === 'yes';
            };

            setComplaintDetail({ ...row, QueryID: savedQueryId });
            setFormData(prev => ({
              ...prev,
              queryId: asText(row.QueryID) || prev.queryId,
              status: row.JobStatus || prev.status || '',
              launchedOn: row.CreatedOn || prev.launchedOn,
              launchedBy: row.Agentid || prev.launchedBy,
                mainCategory: row.Category || prev.mainCategory,
                category: row.SubCategory || prev.category,
                subCategory: row.SubCatChild || prev.subCategory,
                query: row.Query || prev.query,
                solution: row.Solution || prev.solution,
                smsText: row.SMSText || prev.smsText,
                contactId: rowContactId || prev.contactId,
                callerId: rowCallerId || prev.callerId,
                source: normalizeSavedSource(
                  pickFirstValue(row.ComplaintSource, row.Source, row.source),
                  prev.source
                ),
                shortQuery: isTruthy(row.ShortQuery),
                ageAboveFifty: flagIs(row.AgeAboveFifty, 'Age > 50'),
                domesticViolence: flagIs(row.Domesticviolence, 'Gender Base Violence'),
                womenProperty: flagIs(row.WomenProperty, 'Women Property'),
                furtherHelp: row.FurtherHelp || prev.furtherHelp
              }));
              if (rowCallerId) {
                setCurrentCallerId(rowCallerId);
              }
            } catch (error) {
              console.error('complaintsLoad error:', error);
            }
          }
          setListReloadKey(k => k + 1);
      } else {
        const message = [result?.message, result?.error]
          .filter(Boolean)
          .join(': ') || res.statusText || `HTTP ${res.status}`;
        setIdentityErrors([`Save failed: ${message}`]);
        alert(`Save failed: ${message}`);
      }
    } catch (error) {
      setIdentityErrors([`Save error: ${error.message}`]);
      alert(`Save error: ${error.message}`);
    }
  }, [extractContactId, extractQueryId, logApi, setInfoMessage, toContactPayload, validateIdentityBeforeSave]);

  const applyContactUpdateToForm = useCallback((payload, result = null) => {
    const updatedContactId = extractContactId(result);
    setFormData(prev => {
      const nextAge = payload?.Age ?? prev.age;
      return {
        ...prev,
        ...(updatedContactId ? { contactId: updatedContactId, callerId: updatedContactId } : {}),
        age: nextAge,
        salary: payload?.Salary ?? payload?.AdditionalInfo1 ?? prev.salary,
        religion: payload?.Religion ?? payload?.AdditionalInfo6 ?? prev.religion,
        ageAboveFifty: isAgeAboveFiftyValue(nextAge)
      };
    });
  }, [extractContactId, isAgeAboveFiftyValue]);

  const updateContactRecord = useCallback(async ({
    alertOnSuccess = false,
    alertOnError = true,
    allowMissing = false,
  } = {}) => {
    doFindContactRef.current = false;
    const identifier = String(formData.contactId || formData.callerId || formData.queryId || '').trim();
    if (!identifier) {
      if (!allowMissing && alertOnError) {
        alert('No record selected to update (missing Contact ID / Query ID / Caller ID).');
      }
      return false;
    }

    try {
      const payload = toContactPayload();
      const url = Update_CONTACT_URL(encodeURIComponent(identifier));
      logApi('update-contact', { url, payload });
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });

      const text = await res.text();
      let result = null;
      try { result = text ? JSON.parse(text) : null; } catch { result = { message: text }; }

      if (res.ok) {
        applyContactUpdateToForm(payload, result);
        if (alertOnSuccess) {
          alert('Contact updated!');
        }
        return true;
      }

      if (alertOnError && !(allowMissing && res.status === 404)) {
        alert(`Update failed: ${result?.message || res.statusText}`);
      }
      return false;
    } catch (error) {
      if (alertOnError) {
        alert(`Update error: ${error.message}`);
      }
      return false;
    }
  }, [
    applyContactUpdateToForm,
    formData.callerId,
    formData.contactId,
    formData.queryId,
    logApi,
    toContactPayload
  ]);

  const handleUpdateContact = useCallback(async () => {
    await updateContactRecord({ alertOnSuccess: true, alertOnError: true });
  }, [updateContactRecord]);

  const normalizeText = useCallback((value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    const str = String(value).trim();
    return str === '' ? fallback : str;
  }, []);

  const normalizeDateInput = useCallback((value) => {
    const text = String(value ?? '').trim();
    if (!text) return '';
    const match = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
    return '';
  }, []);

  const normalizeStatus = useCallback((value, fallback = '') => {
    const text = normalizeText(value, '');
    if (!text) return fallback;
    const lower = text.toLowerCase();
    if (lower === 'inprogress' || lower === 'in progress' || lower === 'in-process') {
      return 'In Progress';
    }
    if (lower === 'completed' || lower === 'complete' || lower === 'closed') {
      return 'Completed';
    }
    if (lower === 'initiate' || lower === 'initiated' || lower === 'open' || lower === 'new') {
      return 'Initiated';
    }
    return text;
  }, [normalizeText]);

  const normalizeName = useCallback((value) => normalizeText(value, '').toLowerCase(), [normalizeText]);

  const normalizeSelectValue = useCallback((value, options = [], fallback = '') => {
    const text = normalizeText(value, '');
    if (!text) return fallback;
    const match = options.find(
      (opt) => String(opt).toLowerCase() === text.toLowerCase()
    );
    return match || text;
  }, [normalizeText]);

  const normalizeSource = useCallback((value, fallback = '') => {
    const text = normalizeText(value, '');
    if (!text) return fallback;
    const compact = text.toLowerCase().replace(/\s+/g, '');
    if (compact === 'incomingcall') return SOURCE_OPTIONS[0];
    if (compact === 'outboundcall' || compact === 'outboundcal') return SOURCE_OPTIONS[1];
    if (compact === 'voicemail') return SOURCE_OPTIONS[2];
    if (compact === 'email') return SOURCE_OPTIONS[3];
    return normalizeSelectValue(text, SOURCE_OPTIONS, fallback);
  }, [normalizeSelectValue, normalizeText]);

  const normalizeTagCsv = useCallback((value) => {
    if (value === null || value === undefined) return '';
    return String(value)
      .split(/[;,|]/)
      .map((tag) => tag.trim())
      .filter(Boolean)
      .join(', ');
  }, []);

  const normalizeGender = useCallback((value) => {
    const g = normalizeText(value).toLowerCase();
    if (g === 'm' || g === 'male') return 'Male';
    if (g === 'f' || g === 'female') return 'Female';
    if (g === 'o' || g === 'other') return 'Other';
    return '';
  }, [normalizeText]);

  const applyComplaintDetails = useCallback((row, fallbackQid) => {
    if (!row || typeof row !== 'object') return;

    const asText = (value) => String(value ?? '').trim();
    const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
    const pickFieldValue = (keys = []) => {
      for (const key of keys) {
        if (hasOwn(row, key)) return row[key];
      }
      return undefined;
    };
    const normalizeTextValue = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    };
    const readText = (keys) => {
      const raw = pickFieldValue(keys);
      if (raw === undefined) return undefined;
      return normalizeTextValue(raw);
    };
    const readDate = (keys) => {
      const raw = pickFieldValue(keys);
      if (raw === undefined) return undefined;
      return normalizeDateInput(raw);
    };
    const readSource = (keys) => {
      const raw = pickFieldValue(keys);
      if (raw === undefined) return undefined;
      return normalizeSource(raw, '');
    };
    const readSelect = (keys, options = []) => {
      const raw = pickFieldValue(keys);
      if (raw === undefined) return undefined;
      return normalizeSelectValue(raw, options, '');
    };
    const readFlag = (keys, label) => {
      const raw = pickFieldValue(keys);
      if (raw === undefined) return undefined;
      return flagIs(raw, label);
    };
    const readTruthy = (keys) => {
      const raw = pickFieldValue(keys);
      if (raw === undefined) return undefined;
      return isTruthy(raw);
    };
    const flagIs = (value, match) =>
      asText(value).toLowerCase() === String(match).toLowerCase();
    const isTruthy = (value) => {
      const v = asText(value).toLowerCase();
      return v === '1' || v === 'true' || v === 'yes';
    };

    const qid = normalizeText(
      pickFirstValue(
        row.QueryID,
        row.QueryId,
        row.queryId,
        row.queryid,
        row.QID,
        row.qid,
        row.ComplaintID,
        row.complaintId,
        row.complaint_id,
        fallbackQid
      ),
      fallbackQid
    );
    detailsAutoSaveQueryIdRef.current = String(qid || fallbackQid || '').trim();
    detailsAutoSaveVersionRef.current = getComplaintDraftVersion(row);

    const mainCategory = readText([
      'CategoryName',
      'MainCat',
      'MainCategory',
      'Category',
      'mainCategory',
      ...(row.subcategory !== undefined ? ['category'] : []),
    ]);
    const category = readText([
      'SubCategoryName',
      'Cat',
      'SubCategory',
      'subcategory',
      ...(row.subcategory === undefined ? ['category'] : []),
    ]);
    const subCategory = readText([
      'SubCatChild',
      'Sub-Cat',
      'ChildCatName',
      'SubCategoryChild',
      'subcatchild',
      'subCategory',
    ]);
    const loadedMainTags = normalizeTagCsv(
      readText(['MainTags', 'mainTags', 'maintags'])
    );
    const loadedTagMfl = normalizeTagCsv(
      readText(['TagMFL', 'tagMFL', 'tagMfl', 'tagmfl'])
    );
    const loadedTagWrlmp = normalizeTagCsv(
      readText(['TagWRLMP', 'tagWRLMP', 'tagWrlmp', 'tagwrlmp'])
    );
    const contactId = readText(['CID', 'ContactID', 'ContactId', 'contactId', 'contact_id', 'cid']);
    const phone = readText(['PhoneNo', 'phoneno', 'ContactNumber', 'contactnumber', 'CLI', 'cli']);
    const name = readText(['Name', 'FullName', 'ContactPerson', 'contactPerson', 'name']);
    const genderRaw = readText(['Gender', 'gender']);
    const gender = genderRaw !== undefined ? (normalizeGender(genderRaw) || genderRaw) : undefined;
    const age = readText(['Age', 'age']);
    const city = readText(['City', 'city']);
    const district = readText(['District', 'district']);
    const province = readText(['Province', 'province']);
    const country = readText(['Country', 'country']);
    const heardFrom = readText(['HeardFrom', 'heardFrom', 'heardfrom']);
    const fatherHusbandName = readText(['FatherName', 'fatherName', 'fathername', 'FatherHusbandName', 'fatherHusbandName']);
    const salary = readText(['Salary', 'salary']);
    const religion = readText(['Religion', 'religion']);
    const maritalStatus = readText(['MaritalStatus', 'maritalStatus', 'maritalstatus', 'marital_status']);
    const address = readText(['Address', 'address']);
    const remarks = readText(['Remarks', 'remarks']);
    const callback1 = readText(['CellNo', 'CellNO', 'cellno', 'Callback1', 'callback1', 'callback_one']);
    const callback2 = readText(['CallbackTwo', 'callbackTwo', 'callbacktwo', 'Callback2', 'callback_two']);

    skipCategoryResetRef.current = true;

    setIsNewQuery(false);
    setComplaintDetail({ ...row, QueryID: qid || fallbackQid });
    setFormData(prev => ({
      ...prev,
      queryId: qid || prev.queryId,
      contactId: contactId !== undefined ? contactId : prev.contactId,
      callerId: contactId !== undefined ? contactId : prev.callerId,
      cli: phone !== undefined ? phone : prev.cli,
      PhoneNo: phone !== undefined ? phone : prev.PhoneNo,
      name: name !== undefined ? name : prev.name,
      gender: gender !== undefined ? gender : prev.gender,
      age: age !== undefined ? age : prev.age,
      city: city !== undefined ? city : prev.city,
      district: district !== undefined ? district : prev.district,
      province: province !== undefined ? province : prev.province,
      country: country !== undefined ? country : prev.country,
      heardFrom: heardFrom !== undefined ? heardFrom : prev.heardFrom,
      fatherHusbandName: fatherHusbandName !== undefined ? fatherHusbandName : prev.fatherHusbandName,
      salary: salary !== undefined ? salary : prev.salary,
      religion: religion !== undefined ? religion : prev.religion,
      maritalStatus: maritalStatus !== undefined ? maritalStatus : prev.maritalStatus,
      address: address !== undefined ? address : prev.address,
      remarks: remarks !== undefined ? remarks : prev.remarks,
      callback1: callback1 !== undefined ? callback1 : prev.callback1,
      callback2: callback2 !== undefined ? callback2 : prev.callback2,
      status: (() => {
        const raw = pickFirstValue(
          row.JobStatus,
          row.jobStatus,
          row.jobstatus,
          row.Jobstatus,
          row.CurrentStatus,
          row.currentStatus,
          row.currentstatus,
          row.QueryStatus,
          row.queryStatus,
          row.querystatus,
          row.Status,
          row.status
        );
        if (!raw) return prev.status || '';
        return normalizeStatus(raw, prev.status || '');
      })(),
      launchedOn: (() => {
        const val = readDate(['CreatedOn', 'createdOn', 'createdon', 'LaunchedOn']);
        return val !== undefined ? val : prev.launchedOn;
      })(),
      launchedBy: (() => {
        const val = readText(['Created', 'created', 'CreatedBy', 'createdBy', 'Agentid']);
        return val !== undefined ? val : prev.launchedBy;
      })(),
      acceptedOn: (() => {
        const val = readDate(['ModifiedOn', 'modifiedOn', 'modifiedon', 'AcceptedOn']);
        return val !== undefined ? val : prev.acceptedOn;
      })(),
      acceptedBy: (() => {
        const val = readText(['Modified', 'modified', 'ModifiedBy', 'modifiedBy']);
        return val !== undefined ? val : prev.acceptedBy;
      })(),
      callStateUniqueId: (() => {
        const val = readText(['CallStateUniqueId', 'callStateUniqueId', 'UniqueId', 'uniqueId']);
        return val !== undefined ? val : prev.callStateUniqueId;
      })(),
      callStateChannelId: (() => {
        const val = readText(['CallStateChannelId', 'callStateChannelId', 'ChannelId', 'channelId']);
        return val !== undefined ? val : prev.callStateChannelId;
      })(),
      mainCategory: mainCategory !== undefined ? mainCategory : prev.mainCategory,
      category: category !== undefined ? category : prev.category,
      subCategory: subCategory !== undefined ? subCategory : prev.subCategory,
      query: (() => {
        const val = readText(['Query', 'query']);
        return val !== undefined ? val : prev.query;
      })(),
      solution: (() => {
        const val = readText(['Solution', 'solution']);
        return val !== undefined ? val : prev.solution;
      })(),
      smsText: (() => {
        const val = readText(['SMS', 'SMSText', 'smsText', 'sms', 'smstext']);
        return val !== undefined ? val : prev.smsText;
      })(),
      source: (() => {
        const val = readSource(['ComplaintSource', 'complaintsource', 'Source', 'source']);
        return val !== undefined ? val : prev.source;
      })(),
      priority: (() => {
        const val = readSelect(['Priority', 'priority'], ['Normal', 'Low', 'Medium', 'High']);
        return val !== undefined ? val : prev.priority;
      })(),
      office: (() => {
        const val = readText(['RoutedToDepartment', 'routedtodepartment', 'Office', 'office']);
        return val !== undefined ? val : prev.office;
      })(),
      shortQuery: (() => {
        const val = readTruthy(['ShortQuery', 'shortquery', 'AdditionalInfo8', 'additionalinfo8']);
        return val !== undefined ? val : prev.shortQuery;
      })(),
      adrQuery: (() => {
        const val = readFlag(
          ['AdditionalInfo1', 'additionalinfo1', 'ADRcheck', 'ADRQuery', 'adrQuery'],
          'ADR Query'
        );
        return val !== undefined ? val : prev.adrQuery;
      })(),
      womenRelated: (() => {
        const val = readFlag(
          ['AdditionalInfo2', 'additionalinfo2', 'WomenRelated', 'womenRelated'],
          'Women Related'
        );
        return val !== undefined ? val : prev.womenRelated;
      })(),
      covid: (() => {
        const val = readFlag(['COVID', 'covid'], 'COVID-19');
        return val !== undefined ? val : prev.covid;
      })(),
      ageAboveFifty: (() => {
        const val = readFlag(
          ['AgeAboveFifty', 'ageAboveFifty', 'ageabovefifty'],
          'Age > 50'
        );
        if (val === undefined && age !== undefined) return isAgeAboveFiftyValue(age);
        return val !== undefined ? val : prev.ageAboveFifty;
      })(),
      domesticViolence: (() => {
        const val = readFlag(
          ['Domesticviolence', 'domesticViolence', 'domesticviolence'],
          'Gender Base Violence'
        );
        return val !== undefined ? val : prev.domesticViolence;
      })(),
      womenProperty: (() => {
        const val = readFlag(
          ['WomenProperty', 'womenProperty', 'womenproperty'],
          'Women Property'
        );
        return val !== undefined ? val : prev.womenProperty;
      })(),
      furtherHelp: (() => {
        const val = readText(['FurtherHelp', 'furtherHelp', 'furtherhelp']);
        return val !== undefined ? val : prev.furtherHelp;
      })(),
      sindhGovDepartment: (() => {
        const val = readText([
          'SindhGovDept',
          'SindhGovDepartment',
          'sindhGovDepartment',
          'sindhgovdept',
        ]);
        return val !== undefined ? val : prev.sindhGovDepartment;
      })(),
      mainTags: loadedMainTags,
      tagMfl: loadedTagMfl,
      tagWrlmp: loadedTagWrlmp,
      oldTags: [],
      forMfl: [],
      forWrlmp: [],
    }));
    detailsAutoSaveHydratingRef.current = false;
  }, [
    isAgeAboveFiftyValue,
    normalizeDateInput,
    normalizeGender,
    normalizeSelectValue,
    normalizeSource,
    normalizeStatus,
    normalizeTagCsv,
    normalizeText
  ]);

  const fetchComplaintDetails = useCallback(async (complaintID) => {
    const qid = normalizeText(complaintID, '');
    if (!qid) return;
    const requestId = complaintDetailsRequestIdRef.current + 1;
    complaintDetailsRequestIdRef.current = requestId;
    resetDetailsAutoSave({ queryId: qid, hydrating: true });
    const authToken = localStorage.getItem('token');
    const requestOptions = {
      headers: {
        Accept: 'application/json',
        ...(authToken && { Authorization: `Bearer ${authToken}` })
      }
    };
    try {
      const url = `${API_BASE}/complaints/${encodeURIComponent(qid)}/detail`;
      logApi('complaint-detail-by-qid', { url });
      const res = await fetch(url, requestOptions);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      if (requestId !== complaintDetailsRequestIdRef.current) return;
      const row =
        Array.isArray(result?.data) ? result.data[0]
          : Array.isArray(result) ? result[0]
            : result?.data || result?.record || null;
      if (row) {
        applyComplaintDetails(row, qid);
        return;
      }
    } catch (err) {
      if (requestId !== complaintDetailsRequestIdRef.current) return;
      console.error('Complaint detail error:', err);
    }

    try {
      const legacyUrl = `${API_BASE}/DetailEditQueryForGrid/${encodeURIComponent(qid)}`;
      logApi('complaint-detail-legacy', { url: legacyUrl });
      const res = await fetch(legacyUrl, requestOptions);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const legacy = await res.json();
      if (requestId !== complaintDetailsRequestIdRef.current) return;
      const row =
        Array.isArray(legacy) ? legacy[0]
          : Array.isArray(legacy?.data) ? legacy.data[0]
            : legacy?.data || legacy?.record || null;
      if (row) {
        applyComplaintDetails(row, qid);
      }
    } catch (err) {
      if (requestId !== complaintDetailsRequestIdRef.current) return;
      console.error('Complaint detail legacy error:', err);
    }
    if (requestId === complaintDetailsRequestIdRef.current) {
      detailsAutoSaveHydratingRef.current = false;
    }
  }, [applyComplaintDetails, logApi, normalizeText, resetDetailsAutoSave]);

  const resolveRoleName = useCallback(() => {
    const fromSession = user?.roleName;
    const fromLegacy = lsUser?.RoleName || lsUser?.roleName;
    return String(fromSession || fromLegacy || 'FO').trim();
  }, [lsUser?.RoleName, lsUser?.roleName, user?.roleName]);

  const resolveUserId = useCallback(() => {
    const raw =
      user?.userId ??
      lsUser?.UserID ??
      lsUser?.userId ??
      lsUser?.ID ??
      lsUser?.id;
    const num = Number(raw);
    return Number.isFinite(num) ? num : 0;
  }, [lsUser?.ID, lsUser?.UserID, lsUser?.id, lsUser?.userId, user?.userId]);

  const resolveMachineName = useCallback(() => {
    const fromEnv = process.env.REACT_APP_MACHINE_NAME;
    if (fromEnv && String(fromEnv).trim()) return String(fromEnv).trim();
    try {
      const stored =
        localStorage.getItem('machineName') ||
        localStorage.getItem('MachineName') ||
        localStorage.getItem('computerName') ||
        localStorage.getItem('ComputerName') ||
        '';
      if (stored && String(stored).trim()) return String(stored).trim();
    } catch {
      // ignore storage errors
    }
    if (typeof window !== 'undefined') {
      const host = String(window.location?.hostname || '').trim();
      if (host) return host;
    }
    return '';
  }, []);

  const syncComplaintCallLink = useCallback(async ({ complaintId, queryId = '' } = {}) => {
    const complaintIdNum = Number.parseInt(String(complaintId || '').trim(), 10);
    if (!Number.isFinite(complaintIdNum)) {
      return null;
    }

    const callStateUniqueId = pickFirstValue(
      activeCallLink.uniqueId,
      formData.callStateUniqueId,
      activeChannelInfo?.ivrUid,
      activeChannelInfo?.linkedId,
      activeChannelInfo?.stableId
    );
    const callStateChannelId = pickFirstValue(
      currentChannelId,
      activeCallLink.channelId,
      formData.callStateChannelId
    );
    const callerNumber = pickFirstValue(
      activeChannelInfo?.callerNumber,
      activeChannelInfo?.calledNumber,
      Array.isArray(incomingCalls) ? incomingCalls[0]?.callerNumber : '',
      currentCallerId,
      formData.cli,
      formData.PhoneNo,
      activeChannelInfo?.callerNumber,
      activeChannelInfo?.number
    );

    const payload = {
      complaintId: complaintIdNum,
      queryId: String(queryId || complaintIdNum).trim(),
      callStateUniqueId,
      channelId: callStateChannelId,
      callerNumber,
      ivrUid: activeChannelInfo?.ivrUid || '',
      linkedId: activeChannelInfo?.linkedId || '',
      stableId: activeChannelInfo?.stableId || ''
    };

    try {
      logApi('link-complaint-call', { url: COMPLAINT_CALL_LINK_URL, payload });
      const res = await fetch(COMPLAINT_CALL_LINK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });

      const text = await res.text();
      let result = null;
      try { result = text ? JSON.parse(text) : null; } catch { result = { message: text }; }

      if (!res.ok) {
        console.warn('Complaint call-link sync failed:', result?.message || res.statusText, payload);
        return null;
      }

      setFormData((prev) => {
        const nextUniqueId = pickFirstValue(result?.callStateUniqueId, prev.callStateUniqueId);
        const nextChannelId = pickFirstValue(result?.callStateChannelId, prev.callStateChannelId);

        if (
          String(prev.callStateUniqueId || '').trim() === nextUniqueId &&
          String(prev.callStateChannelId || '').trim() === nextChannelId
        ) {
          return prev;
        }

        return {
          ...prev,
          callStateUniqueId: nextUniqueId,
          callStateChannelId: nextChannelId
        };
      });

      return result;
    } catch (error) {
      console.warn('Complaint call-link sync error:', error?.message || error);
      return null;
    }
  }, [
    activeCallLink.channelId,
    activeCallLink.uniqueId,
    incomingCalls,
    activeChannelInfo?.callerNumber,
    activeChannelInfo?.calledNumber,
    activeChannelInfo?.ivrUid,
    activeChannelInfo?.linkedId,
    activeChannelInfo?.number,
    activeChannelInfo?.stableId,
    currentCallerId,
    currentChannelId,
    formData.PhoneNo,
    formData.callStateChannelId,
    formData.callStateUniqueId,
    formData.cli,
    logApi
  ]);

  const normalizeJobStatus = useCallback((value) => {
    const text = normalizeText(value, '');
    if (!text) return '';
    const lower = text.toLowerCase();
    if (lower === 'initiated' || lower === 'in progress' || lower === 'inprogress' || lower === 'open') return 'Initiated';
    if (lower === 'approved') return 'Approved';
    if (lower === 'refer' || lower === 'referred') return 'Refer';
    if (lower === 'completed' || lower === 'complete') return 'Completed';
    if (lower === 'advised') return 'Advised';
    if (lower === 'corrected') return 'Corrected';
    return text;
  }, [normalizeText]);

  const getNextStatusForComplete = useCallback((currentStatus) => {
    const normalized = normalizeJobStatus(currentStatus);
    if (normalized === 'Initiated') return 'Completed';
    if (normalized === 'Approved') return 'Advised';
    if (normalized === 'Refer') return 'Corrected';
    return '';
  }, [normalizeJobStatus]);

  const readApiResult = useCallback(async (res) => {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return { message: text };
    }
  }, []);

  const formatApiError = useCallback((result, fallback = 'Request failed') => {
    const messages = [];
    if (result?.message) messages.push(result.message);
    if (result?.error) messages.push(result.error);

    if (result?.errors && typeof result.errors === 'object') {
      Object.entries(result.errors).forEach(([field, value]) => {
        const detail = Array.isArray(value)
          ? value.filter(Boolean).join(', ')
          : String(value ?? '').trim();
        if (detail) messages.push(`${field}: ${detail}`);
      });
    }

    return messages.filter(Boolean).join(' | ') || fallback;
  }, []);

  const showPageError = useCallback((message) => {
    setInfoMessage('');
    setErrorMessage(message);
  }, []);

  const showPageInfo = useCallback((message) => {
    setErrorMessage('');
    setInfoMessage(message);
  }, []);

  const runFormAction = useCallback(async (action) => {
    if (formActionInFlightRef.current) {
      showPageInfo('Previous request is still processing. Please wait.');
      return false;
    }

    formActionInFlightRef.current = true;
    setFormSaving(true);
    try {
      await action();
      return true;
    } finally {
      formActionInFlightRef.current = false;
      setFormSaving(false);
    }
  }, [showPageInfo]);

  const buildDetailsPersistencePayload = useCallback((data, tagPayload = {}) => {
    const complaintIdNum = Number(normalizeText(data?.queryId, ''));
    const tagIds = Array.isArray(tagPayload.tagIds)
      ? tagPayload.tagIds.filter(Boolean)
      : [];
    const categoryId = normalizeText(tagPayload.categoryId, '');
    const subCategoryId = normalizeText(tagPayload.subCategoryId, '');
    const childCategoryId = normalizeText(tagPayload.childCategoryId, '');
    const payload = {
      complaint_id: complaintIdNum,
      tag_ids: tagIds,
      ComplaintID: complaintIdNum,
      CallStateUniqueId: pickFirstValue(data?.callStateUniqueId, activeCallLink.uniqueId),
      UniqueId: pickFirstValue(data?.callStateUniqueId, activeCallLink.uniqueId),
      CallStateChannelId: pickFirstValue(data?.callStateChannelId, currentChannelId, activeCallLink.channelId),
      ChannelId: pickFirstValue(data?.callStateChannelId, currentChannelId, activeCallLink.channelId),
      RoleName: resolveRoleName(),
      ModifiedBy: resolveUserId(),
      CID: normalizeText(data?.contactId || '', ''),
      ContactPerson: normalizeText(data?.name, ''),
      ContactNumber: normalizeText(data?.cli || data?.PhoneNo, ''),
      CategoryName: normalizeText(data?.mainCategory, ''),
      SubCategoryName: normalizeText(data?.category, ''),
      ChildCatName: normalizeText(data?.subCategory, ''),
      ComplaintSource: normalizeText(data?.source, ''),
      Priority: normalizeText(data?.priority, ''),
      SMS: normalizeText(data?.smsText, ''),
      Query: normalizeText(data?.query, ''),
      Solution: normalizeText(data?.solution, ''),
      ShortQuery: !!data?.shortQuery,
      ADRcheck: data?.adrQuery ? 'ADR Query' : ' ',
      WomenRelated: data?.womenRelated ? 'Women Related' : ' ',
      COVID: data?.covid ? 'COVID-19' : ' ',
      AgeAboveFifty: isAgeAboveFiftyValue(data?.age) ? 'Age > 50' : ' ',
      Domesticviolence: data?.domesticViolence ? 'Gender Base Violence' : ' ',
      WomenProperty: data?.womenProperty ? 'Women Property' : ' ',
      FurtherHelp: normalizeText(data?.furtherHelp, ''),
      SindhGovDept: normalizeText(data?.sindhGovDepartment, ''),
      OldTags: tagPayload.oldTagsCsv || '',
      TagMFL: tagPayload.tagMflCsv || '',
      TagWRLMP: tagPayload.tagWrlmpCsv || '',
      CategoryID: categoryId || 0,
    };

    if (subCategoryId) payload.SubCategoryID = subCategoryId;
    if (childCategoryId) payload.ChildCatID = childCategoryId;
    return payload;
  }, [
    activeCallLink.channelId,
    activeCallLink.uniqueId,
    currentChannelId,
    isAgeAboveFiftyValue,
    normalizeText,
    resolveRoleName,
    resolveUserId
  ]);

  const handleSaveDetails = useCallback(
    async (tagPayload = {}) => {
      const complaintIdRaw = normalizeText(formData.queryId, '');
      if (!complaintIdRaw) {
        showPageError('No Query ID found to update complaint.');
        return;
      }
      const complaintIdNum = Number(complaintIdRaw);
      if (!Number.isFinite(complaintIdNum)) {
        showPageError('Invalid Query ID.');
        return;
      }

      const modifiedBy = resolveUserId();
      if (!modifiedBy) {
        showPageError('No user ID found for update.');
        return;
      }

      const payload = buildDetailsPersistencePayload(formData, tagPayload);

      try {
        logApi('update-tags', { url: UPDATE_TAGS_URL, payload });
        const res = await fetch(UPDATE_TAGS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const result = await readApiResult(res);
        if (res.ok) {
          await updateContactRecord({ alertOnError: false, allowMissing: true });
          showPageInfo(result?.message || 'Complaint updated successfully.');
          await Promise.allSettled([
            syncComplaintCallLink({ complaintId: complaintIdNum, queryId: complaintIdNum }),
            fetchComplaintDetails(complaintIdNum)
          ]);
          setListReloadKey(k => k + 1);
        } else {
          showPageError(`Update failed: ${formatApiError(result, res.statusText || `HTTP ${res.status}`)}`);
        }
      } catch (error) {
        showPageError(`Update error: ${error.message}`);
      }
    },
    [
      buildDetailsPersistencePayload,
      fetchComplaintDetails,
      formatApiError,
      formData,
      logApi,
      normalizeText,
      readApiResult,
      resolveUserId,
      showPageError,
      showPageInfo,
      syncComplaintCallLink,
      updateContactRecord
    ]
  );

  const handleCompleteUpdate = useCallback(
    async (tagPayload = {}, forcedStatus = '') => {
      const complaintIdRaw = normalizeText(formData.queryId, '');
      if (!complaintIdRaw) {
        showPageError('No Query ID found to complete.');
        return;
      }
      const complaintIdNum = Number(complaintIdRaw);
      if (!Number.isFinite(complaintIdNum)) {
        showPageError('Invalid Query ID.');
        return;
      }

      const modifiedBy = resolveUserId();
      if (!modifiedBy) {
        showPageError('No user ID found for update.');
        return;
      }

      const roleName = resolveRoleName();
      const createdById = resolveUserId();
      const machineName = resolveMachineName();
      const currentStatus = normalizeText(formData.status, 'Initiated');
      const normalizedCurrentStatus = normalizeJobStatus(currentStatus);
      const requestedStatus = normalizeJobStatus(forcedStatus);
      const nextStatus = requestedStatus || getNextStatusForComplete(currentStatus);

      if (requestedStatus === 'Corrected' && normalizedCurrentStatus !== 'Refer') {
        showPageError('Corrected is only available for Refer status.');
        return;
      }

      if (!nextStatus) {
        showPageError('Unknown Status.');
        return;
      }

      const deptName =
        lsUser?.DepartmentName ||
        lsUser?.departmentName ||
        '';

      const nameValue = normalizeText(formData.name, '');
      const phoneValue = normalizeText(formData.cli || formData.PhoneNo, '');
      const queryValue = normalizeText(formData.query, '');
      const solutionValue = normalizeText(formData.solution, '');

      const payload = {
        ComplaintID: complaintIdNum,
        complaintId: complaintIdNum,
        CallStateUniqueId: pickFirstValue(formData.callStateUniqueId, activeCallLink.uniqueId),
        UniqueId: pickFirstValue(formData.callStateUniqueId, activeCallLink.uniqueId),
        CallStateChannelId: pickFirstValue(formData.callStateChannelId, currentChannelId, activeCallLink.channelId),
        ChannelId: pickFirstValue(formData.callStateChannelId, currentChannelId, activeCallLink.channelId),
        CurrentStatus: currentStatus,
        ModifiedBy: modifiedBy,
        JobStatus: nextStatus,
        ContactPerson: nameValue,
        CategoryName: normalizeText(formData.mainCategory, ''),
        SubCategoryName: normalizeText(formData.category, ''),
        ComplaintSource: normalizeText(formData.source, ''),
        AdditionalInfo6: normalizeText(formData.smsText, ''),
        AdditionalInfo7: machineName,
        CreatedBy: createdById || normalizeText(formData.CreatedBy || formData.launchedBy, ''),
        Priority: normalizeText(formData.priority, ''),
        ResolutionProvided: formData.shortQuery ? 'Short Query' : '',
        AdditionalInfo9: queryValue,
        AdditionalInfo10: solutionValue,
        CategoryID: tagPayload.categoryId || 0,
        SubCategoryID: tagPayload.subCategoryId || 0,
        CID: normalizeText(formData.contactId || '', ''),
        ComplaintType: 'LAW',
        ContactNumber: phoneValue,
        RoutedToDepartment: deptName,
        AdditionalInfo8: formData.shortQuery ? '1' : '0',
        ShortQuery: formData.shortQuery ? '1' : '0',
        AdditionalInfo4: tagPayload.childCategoryId || 0,
        AdditionalInfo5: normalizeText(formData.subCategory, ''),
        ChildCatID: tagPayload.childCategoryId || 0,
        ChildCatName: normalizeText(formData.subCategory, ''),
        AdditionalInfo1: formData.adrQuery ? 'ADR Query' : ' ',
        AdditionalInfo2: formData.womenRelated ? 'Women Related' : ' ',
        COVID: formData.covid ? 'COVID-19' : ' ',
        AgeAboveFifty: isAgeAboveFiftyValue(formData.age) ? 'Age > 50' : ' ',
        Domesticviolence: formData.domesticViolence ? 'Gender Base Violence' : ' ',
        WomenProperty: formData.womenProperty ? 'Women Property' : ' ',
        FurtherHelp: normalizeText(formData.furtherHelp, ''),
        SindhGovDept: normalizeText(formData.sindhGovDepartment, ''),
        MainTags: tagPayload.oldTagsCsv || '',
        TagMFL: tagPayload.tagMflCsv || '',
        TagWRLMP: tagPayload.tagWrlmpCsv || '',
      };

      if (normalizedCurrentStatus !== 'Refer') {
        payload.AdditionalInfo3 = roleName === 'FOE' ? '1' : '0';
      }

      try {
        logApi('complete-complaint', { url: COMPLETE_COMPLAINT_URL, payload });
        const res = await fetch(COMPLETE_COMPLAINT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });

        const result = await readApiResult(res);

        if (!res.ok) {
          showPageError(`Complete failed: ${formatApiError(result, `HTTP ${res.status}`)}`);
          return;
        }

        setFormData(prev => ({
          ...prev,
          status: nextStatus,
        }));
        setIsNewQuery(false);
        await updateContactRecord({ alertOnError: false, allowMissing: true });
        await Promise.allSettled([
          syncComplaintCallLink({ complaintId: complaintIdNum, queryId: complaintIdNum }),
          fetchComplaintDetails(complaintIdNum)
        ]);
        showPageInfo(result?.message || `${nextStatus} successfully.`);
        setListReloadKey(k => k + 1);
      } catch (error) {
        showPageError(`Complete error: ${error.message}`);
      }
    },
    [
      activeCallLink.channelId,
      activeCallLink.uniqueId,
      currentChannelId,
      fetchComplaintDetails,
      formatApiError,
      formData,
      getNextStatusForComplete,
      isAgeAboveFiftyValue,
      logApi,
      normalizeJobStatus,
      normalizeText,
      lsUser?.DepartmentName,
      lsUser?.departmentName,
      readApiResult,
      resolveMachineName,
      resolveRoleName,
      resolveUserId,
      showPageError,
      showPageInfo,
      syncComplaintCallLink,
      updateContactRecord
    ]
  );

  const handleProceed = useCallback(
    async (mode = 'Initiated', tagPayload = {}) => {
      const isCompletionMode = mode === 'Complete' || mode === 'Corrected';
      const status = isCompletionMode ? 'Complete' : 'Initiated';
      const sourceValue = normalizeText(formData.source, '');
      const mainCategoryValue = normalizeText(formData.mainCategory, '');
      const categoryValue = normalizeText(formData.category, '');
      const subCategoryValue = normalizeText(formData.subCategory, '');
      const deptValue = normalizeText(formData.sindhGovDepartment, '');
      const queryValue = normalizeText(formData.query, '');
      const solutionValue = normalizeText(formData.solution, '');
      const phoneValue = normalizeText(formData.cli || formData.PhoneNo, '');
      const nameValue = normalizeText(formData.name, '');
      const roleName = resolveRoleName();
      const modifiedBy = resolveUserId();
      const createdById = resolveUserId();
      const tagIds = Array.isArray(tagPayload.tagIds) ? tagPayload.tagIds.filter(Boolean) : [];
      const categoryId = normalizeText(tagPayload.categoryId, '');
      const subCategoryId = normalizeText(tagPayload.subCategoryId, '');
      const childCategoryId = normalizeText(tagPayload.childCategoryId, '');
      const oldTagsCsv = tagPayload.oldTagsCsv || '';
      const tagMflCsv = tagPayload.tagMflCsv || '';
      const tagWrlmpCsv = tagPayload.tagWrlmpCsv || '';
      const machineName = resolveMachineName();

      if (isPlaceholder(sourceValue, ['--Select--', 'Select'])) {
        showPageError('Please select Source.');
        return;
      }

      if (isCompletionMode) {
        const missingFields = [];
        if (!categoryId || categoryId === '0' || isPlaceholder(mainCategoryValue, ['--Select--', 'Select'])) missingFields.push('Main Category');
        if (!subCategoryId || subCategoryId === '0' || isPlaceholder(categoryValue, ['--Select--', 'Select', '-- No Subcategories --'])) missingFields.push('Category');
        if (!childCategoryId || childCategoryId === '0' || isPlaceholder(subCategoryValue, ['--Select--', 'Select', '--Select Sub-Child--'])) missingFields.push('Sub Category');
        if (isPlaceholder(deptValue, ['--Select--', 'Select'])) missingFields.push('Sindh Government Department');
        if (!queryValue) missingFields.push('Query');
        if (!solutionValue) missingFields.push('Solution');

        if (missingFields.length > 0) {
          showPageError(`Complete ke liye required fields select/fill karein: ${missingFields.join(', ')}.`);
          return;
        }

        if (mode === 'Corrected') {
          await handleCompleteUpdate(tagPayload, 'Corrected');
          return;
        }

        if (!isNewQuery && normalizeText(formData.queryId, '')) {
          await handleCompleteUpdate(tagPayload, '');
          return;
        }
      }

      if (status === 'Initiated') {
        if (!phoneValue) {
          showPageError("Phone No. can't be empty");
          return;
        }
        if (!nameValue) {
          showPageError('Please enter caller name.');
          return;
        }
      }

      const payload = {
        ContactPerson: nameValue,
        ContactNumber: phoneValue,
        CID: normalizeText(formData.contactId || '', ''),
        CallStateUniqueId: pickFirstValue(formData.callStateUniqueId, activeCallLink.uniqueId),
        UniqueId: pickFirstValue(formData.callStateUniqueId, activeCallLink.uniqueId),
        CallStateChannelId: pickFirstValue(formData.callStateChannelId, currentChannelId, activeCallLink.channelId),
        ChannelId: pickFirstValue(formData.callStateChannelId, currentChannelId, activeCallLink.channelId),

        CategoryName: normalizeText(formData.mainCategory, ''),
        SubCategoryName: normalizeText(formData.category, ''),
        SubCatChild: normalizeText(formData.subCategory, ''),
        ComplaintSource: isPlaceholder(sourceValue, ['--Select--', 'Select']) ? '' : sourceValue,
        Priority: normalizeText(formData.priority, 'Normal'),

        SMSText: normalizeText(formData.smsText, ''),
        Query: queryValue,
        Solution: solutionValue,

        AdditionalInfo1: formData.adrQuery ? 'ADR Query' : ' ',
        AdditionalInfo2: formData.womenRelated ? 'Women Related' : ' ',
        AdditionalInfo7: machineName,
        COVID: formData.covid ? 'COVID-19' : ' ',
        AgeAboveFifty: isAgeAboveFiftyValue(formData.age) ? 'Age > 50' : ' ',
        Domesticviolence: formData.domesticViolence ? 'Gender Base Violence' : ' ',
        WomenProperty: formData.womenProperty ? 'Women Property' : ' ',
        FurtherHelp: normalizeText(formData.furtherHelp, ''),
        SindhGovDept: isPlaceholder(deptValue, ['--Select--', 'Select']) ? '' : deptValue,

        MainTags: oldTagsCsv,
        OldTags: oldTagsCsv,
        TagMFL: tagMflCsv,
        TagWRLMP: tagWrlmpCsv,
        SMS: normalizeText(formData.smsText, ''),
        ADRcheck: formData.adrQuery ? 'ADR Query' : ' ',
        WomenRelated: formData.womenRelated ? 'Women Related' : ' ',
        ChildCatName: normalizeText(formData.subCategory, ''),
        ChildCatID: tagPayload.childCategoryId || 0,
        RoleName: roleName,
        ModifiedBy: modifiedBy,
        tag_ids: tagIds,

        CategoryID: tagPayload.categoryId || 0,
        SubCategoryID: tagPayload.subCategoryId || 0,
        ChildCategoryID: tagPayload.childCategoryId || 0,

        JobStatus: status,
        ComplaintType: 'LAW',
        ShortQuery: formData.shortQuery ? '1' : '0',
        CreatedBy: createdById || normalizeText(formData.CreatedBy || formData.launchedBy, ''),
      };

      try {
        const url = `${API_BASE}/InitiateQuery`;
        logApi('create-complaint', { url, payload });
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });

        const result = await readApiResult(res);

        if (!res.ok) {
          showPageError(`Create failed: ${formatApiError(result, `HTTP ${res.status}`)}`);
          return;
        }

        const newId = extractQueryId(result);
        if (!newId) {
          showPageError('Created, but no Query ID returned.');
          return;
        }

        setFormData(prev => ({
          ...prev,
          queryId: newId,
          status,
        }));
        setIsNewQuery(false);
        await Promise.allSettled([
          syncComplaintCallLink({ complaintId: newId, queryId: newId }),
          fetchComplaintDetails(newId)
        ]);
        showPageInfo(result?.message || `Query ${newId} created successfully.`);
        setListReloadKey(k => k + 1);
      } catch (error) {
        showPageError(`Create error: ${error.message}`);
      }
    },
    [
      activeCallLink.channelId,
      activeCallLink.uniqueId,
      currentChannelId,
      extractQueryId,
      fetchComplaintDetails,
      formatApiError,
      formData,
      handleCompleteUpdate,
      isAgeAboveFiftyValue,
      isNewQuery,
      logApi,
      normalizeText,
      readApiResult,
      resolveMachineName,
      resolveRoleName,
      resolveUserId,
      showPageError,
      showPageInfo,
      syncComplaintCallLink
    ]
  );

  const buildDetailsDraftSnapshot = useCallback((data, tagPayload = {}) => {
    const queryId = normalizeText(data?.queryId, '');
    if (!queryId) return null;

    const persisted = buildDetailsPersistencePayload(data, tagPayload);
    const oldTags = normalizeDraftCsv(data?.mainTags, persisted.OldTags);
    const tagMfl = normalizeDraftCsv(data?.tagMfl, persisted.TagMFL);
    const tagWrlmp = normalizeDraftCsv(data?.tagWrlmp, persisted.TagWRLMP);
    const normalizeIds = (value) => (
      Array.isArray(value)
        ? Array.from(new Set(value.map((item) => String(item || '').trim()).filter(Boolean))).sort()
        : []
    );
    const draft = {
      CategoryName: persisted.CategoryName,
      SubCategoryName: persisted.SubCategoryName,
      ChildCatName: persisted.ChildCatName,
      ComplaintSource: persisted.ComplaintSource,
      Priority: persisted.Priority,
      SMS: persisted.SMS,
      Query: persisted.Query,
      Solution: persisted.Solution,
      ShortQuery: persisted.ShortQuery,
      ADRcheck: persisted.ADRcheck,
      WomenRelated: persisted.WomenRelated,
      COVID: persisted.COVID,
      AgeAboveFifty: persisted.AgeAboveFifty,
      Domesticviolence: persisted.Domesticviolence,
      WomenProperty: persisted.WomenProperty,
      FurtherHelp: persisted.FurtherHelp,
      SindhGovDept: persisted.SindhGovDept,
      FAQ: normalizeText(data?.faq, ''),
      ReferredTo: Boolean(data?.referredTo),
      ReferredToAgent: normalizeText(data?.referredToAgent, ''),
      OldTags: oldTags,
      TagMFL: tagMfl,
      TagWRLMP: tagWrlmp,
      TagIds: normalizeIds(persisted.tag_ids),
      OldTagIds: normalizeIds(data?.oldTags),
      MFLIds: normalizeIds(data?.forMfl),
      WRLMPIds: normalizeIds(data?.forWrlmp),
      CategoryID: persisted.CategoryID,
      SubCategoryID: persisted.SubCategoryID || null,
      ChildCatID: persisted.ChildCatID || null,
    };
    const comparable = {
      ...draft,
      CategoryName: String(draft.CategoryName || '').trim().toLowerCase(),
      SubCategoryName: String(draft.SubCategoryName || '').trim().toLowerCase(),
      ChildCatName: String(draft.ChildCatName || '').trim().toLowerCase(),
      OldTags: String(draft.OldTags || '').toLowerCase(),
      TagMFL: String(draft.TagMFL || '').toLowerCase(),
      TagWRLMP: String(draft.TagWRLMP || '').toLowerCase(),
    };
    delete comparable.TagIds;
    delete comparable.OldTagIds;
    delete comparable.MFLIds;
    delete comparable.WRLMPIds;
    delete comparable.CategoryID;
    delete comparable.SubCategoryID;
    delete comparable.ChildCatID;

    return {
      queryId,
      draft,
      key: JSON.stringify(comparable),
      generation: detailsAutoSaveGenerationRef.current,
    };
  }, [buildDetailsPersistencePayload, normalizeText]);

  const executeDetailsAutoSave = useCallback(async (snapshot) => {
    if (!snapshot || !detailsAutoSaveMountedRef.current) return;
    if (
      snapshot.generation !== detailsAutoSaveGenerationRef.current ||
      snapshot.queryId !== detailsAutoSaveQueryIdRef.current ||
      snapshot.key === detailsAutoSaveLastSavedKeyRef.current ||
      detailsAutoSavePausedRef.current
    ) return;

    if (detailsAutoSaveInFlightRef.current) {
      detailsAutoSavePendingRef.current = snapshot.key === detailsAutoSaveActiveKeyRef.current
        ? null
        : snapshot;
      return;
    }

    detailsAutoSaveInFlightRef.current = true;
    detailsAutoSaveActiveKeyRef.current = snapshot.key;
    setDetailsAutoSaveStatus({ state: 'saving', savedAt: '' });

    const requestPromise = asteriskService.saveComplaintDraft(snapshot.queryId, {
      expectedVersion: detailsAutoSaveVersionRef.current,
      draft: snapshot.draft,
    });
    detailsAutoSavePromiseRef.current = requestPromise;
    let succeeded = false;

    try {
      const result = await requestPromise;
      if (
        snapshot.generation !== detailsAutoSaveGenerationRef.current ||
        snapshot.queryId !== detailsAutoSaveQueryIdRef.current
      ) return;

      const savedVersion = normalizeDraftVersion(result?.version ?? result?.updatedAt);
      detailsAutoSaveVersionRef.current = savedVersion;
      detailsAutoSaveLastSavedKeyRef.current = snapshot.key;
      succeeded = true;
      setComplaintDetail((previous) => {
        const previousId = pickFirstValue(
          previous?.QueryID,
          previous?.QueryId,
          previous?.ComplaintID,
          previous?.complaintId
        );
        if (!previous || previousId !== snapshot.queryId || !savedVersion) return previous;
        return { ...previous, ModifiedOn: savedVersion };
      });
    } catch (error) {
      if (
        snapshot.generation === detailsAutoSaveGenerationRef.current &&
        snapshot.queryId === detailsAutoSaveQueryIdRef.current
      ) {
        setDetailsAutoSaveStatus({
          state: 'error',
          savedAt: '',
          error: error?.response?.data?.message || error?.message || 'Draft save failed',
        });
      }
    } finally {
      detailsAutoSaveInFlightRef.current = false;
      detailsAutoSaveActiveKeyRef.current = '';
      detailsAutoSavePromiseRef.current = null;

      const latest = detailsAutoSavePendingRef.current || detailsAutoSaveLatestRef.current;
      detailsAutoSavePendingRef.current = null;
      const isCurrent =
        snapshot.generation === detailsAutoSaveGenerationRef.current &&
        snapshot.queryId === detailsAutoSaveQueryIdRef.current;

      const latestIsCurrent =
        latest &&
        latest.generation === detailsAutoSaveGenerationRef.current &&
        latest.queryId === detailsAutoSaveQueryIdRef.current;
      if (
        !detailsAutoSavePausedRef.current &&
        latestIsCurrent &&
        latest.key !== detailsAutoSaveLastSavedKeyRef.current &&
        (succeeded || !isCurrent)
      ) {
        executeDetailsAutoSaveRef.current?.({
          ...latest,
          generation: detailsAutoSaveGenerationRef.current,
        });
      } else if (succeeded && isCurrent && detailsAutoSaveMountedRef.current) {
        detailsAutoSaveUserDirtyRef.current = false;
        setDetailsAutoSaveStatus({
          state: 'saved',
          savedAt: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }),
        });
      }
    }
  }, []);

  useEffect(() => {
    executeDetailsAutoSaveRef.current = executeDetailsAutoSave;
  }, [executeDetailsAutoSave]);

  const handleDetailsFieldChange = useCallback(() => {
    detailsAutoSaveUserDirtyRef.current = true;
  }, []);

  const handleDetailsDraftChange = useCallback(({ formData: data, tagPayload, canAutoSave } = {}) => {
    const queryId = normalizeText(data?.queryId, '');
    if (!queryId || !canAutoSave) {
      if (detailsAutoSaveQueryIdRef.current || detailsAutoSaveTimerRef.current) {
        resetDetailsAutoSave({ queryId });
      }
      return;
    }

    if (detailsAutoSaveQueryIdRef.current !== queryId) {
      resetDetailsAutoSave({ queryId });
    }

    const snapshot = buildDetailsDraftSnapshot(data, tagPayload);
    if (!snapshot) return;
    snapshot.generation = detailsAutoSaveGenerationRef.current;
    detailsAutoSaveLatestRef.current = snapshot;

    if (detailsAutoSaveHydratingRef.current || !detailsAutoSaveUserDirtyRef.current) {
      detailsAutoSaveLastSavedKeyRef.current = snapshot.key;
      return;
    }

    if (detailsAutoSaveTimerRef.current) {
      clearTimeout(detailsAutoSaveTimerRef.current);
      detailsAutoSaveTimerRef.current = null;
    }

    if (snapshot.key === detailsAutoSaveLastSavedKeyRef.current) {
      if (detailsAutoSaveInFlightRef.current) {
        detailsAutoSavePendingRef.current = snapshot.key === detailsAutoSaveActiveKeyRef.current
          ? null
          : snapshot;
      } else {
        detailsAutoSaveUserDirtyRef.current = false;
        setDetailsAutoSaveStatus((previous) => (
          previous.state === 'saved' ? previous : { state: 'idle', savedAt: '' }
        ));
      }
      return;
    }

    if (detailsAutoSaveInFlightRef.current) {
      detailsAutoSavePendingRef.current = snapshot.key === detailsAutoSaveActiveKeyRef.current
        ? null
        : snapshot;
      return;
    }
    setDetailsAutoSaveStatus({ state: 'dirty', savedAt: '' });
    if (detailsAutoSavePausedRef.current) return;

    detailsAutoSaveTimerRef.current = setTimeout(() => {
      detailsAutoSaveTimerRef.current = null;
      executeDetailsAutoSaveRef.current?.(snapshot);
    }, DETAILS_AUTO_SAVE_DELAY_MS);
  }, [buildDetailsDraftSnapshot, normalizeText, resetDetailsAutoSave]);

  const handleDetailsAutoSaveRetry = useCallback(() => {
    const latest = detailsAutoSaveLatestRef.current;
    if (!latest || latest.key === detailsAutoSaveLastSavedKeyRef.current) return;
    if (detailsAutoSaveTimerRef.current) {
      clearTimeout(detailsAutoSaveTimerRef.current);
      detailsAutoSaveTimerRef.current = null;
    }
    const currentSnapshot = {
      ...latest,
      generation: detailsAutoSaveGenerationRef.current,
    };
    if (detailsAutoSaveInFlightRef.current) {
      detailsAutoSavePendingRef.current = currentSnapshot;
      return;
    }
    executeDetailsAutoSaveRef.current?.(currentSnapshot);
  }, []);

  const runAfterPendingDetailsAutoSave = useCallback(async (action) => {
    detailsAutoSavePausedRef.current = true;
    if (detailsAutoSaveTimerRef.current) {
      clearTimeout(detailsAutoSaveTimerRef.current);
      detailsAutoSaveTimerRef.current = null;
    }
    detailsAutoSavePendingRef.current = null;
    try {
      if (detailsAutoSavePromiseRef.current) {
        try {
          await detailsAutoSavePromiseRef.current;
        } catch {
          // The explicit action remains available as the fallback after an auto-save failure.
        }
      }
      return await action();
    } finally {
      detailsAutoSavePausedRef.current = false;
      const latest = detailsAutoSaveLatestRef.current;
      if (
        latest &&
        latest.queryId === detailsAutoSaveQueryIdRef.current &&
        latest.key !== detailsAutoSaveLastSavedKeyRef.current &&
        !detailsAutoSaveTimerRef.current &&
        !detailsAutoSaveInFlightRef.current
      ) {
        setDetailsAutoSaveStatus({ state: 'dirty', savedAt: '' });
        detailsAutoSaveTimerRef.current = setTimeout(() => {
          detailsAutoSaveTimerRef.current = null;
          executeDetailsAutoSaveRef.current?.({
            ...latest,
            generation: detailsAutoSaveGenerationRef.current,
          });
        }, DETAILS_AUTO_SAVE_DELAY_MS);
      }
    }
  }, []);

  const lockedHandleSaveForm = useCallback(() => (
    runFormAction(() => handleSaveForm())
  ), [handleSaveForm, runFormAction]);

  const lockedHandleUpdateContact = useCallback(() => (
    runFormAction(() => handleUpdateContact())
  ), [handleUpdateContact, runFormAction]);

  const lockedHandleSaveDetails = useCallback((tagPayload = {}) => (
    runFormAction(() => runAfterPendingDetailsAutoSave(() => handleSaveDetails(tagPayload)))
  ), [handleSaveDetails, runAfterPendingDetailsAutoSave, runFormAction]);

  const lockedHandleProceed = useCallback((mode = 'Initiated', tagPayload = {}) => (
    runFormAction(() => runAfterPendingDetailsAutoSave(() => handleProceed(mode, tagPayload)))
  ), [handleProceed, runAfterPendingDetailsAutoSave, runFormAction]);

  const lockedHandleNewQuery = useCallback(() => {
    if (formActionInFlightRef.current) {
      showPageInfo('Previous request is still processing. Please wait.');
      return;
    }
    handleNewQuery();
  }, [handleNewQuery, showPageInfo]);

  const handleNewCall = useCallback(() => {
    setIsLive(true);
  }, []);

  const handleLogout = () => {
    navigate('/logout', { replace: true });
  };

  const handleListOpen = () => setIsListOpen(true);
  const handleListClose = () => setIsListOpen(false);
  const handleVoicemailOpen = () => setIsVoicemailOpen(true);
  const handleVoicemailClose = () => setIsVoicemailOpen(false);
  const handleSearchOpen = () => {
    const cliValue =
      formData.cli ||
      formData.PhoneNo ||
      effectiveCallerId ||
      '';
    const callerIdValue =
      effectiveQueryId ||
      formData.callerId ||
      '';
    setSearchFilters(prev => ({
      cli: cliValue || prev.cli,
      cnic: formData.cnic || prev.cnic,
      name: formData.name || prev.name,
      callerId: callerIdValue || prev.callerId,
    }));
    setIsSearchOpen(true);
  };
  const handleSearchClose = () => setIsSearchOpen(false);
  const handleProfileOpen = () => setIsProfileOpen(true);
  const handleProfileClose = () => setIsProfileOpen(false);
  const handleDashboardOpen = useCallback(() => setIsDashboardOpen(true), []);
  const handleDashboardClose = useCallback(() => setIsDashboardOpen(false), []);
  const handleReminderOpen = () => setIsReminderOpen(true);
  const handleReminderClose = () => setIsReminderOpen(false);
  const handleHistoryOpen = () => setIsHistoryOpen(true);
  const handleHistoryClose = () => setIsHistoryOpen(false);
  const handleNotesOpen = () => setIsNotesOpen(true);
  const handleNotesClose = () => setIsNotesOpen(false);
  const handleSmsOpen = () => setIsSmsOpen(true);
  const handleSmsClose = () => setIsSmsOpen(false);

  // Chat functionality
  const { unreadCounts, getSessionSummary } = useChat();
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentSelector, setShowAgentSelector] = useState(false);

  const handleChatOpen = useCallback(async () => {
    setIsChatOpen(false);
    setShowAgentSelector(true);
  }, []);

  const handleSelectAgent = async (agent, sessionId) => {
    setSelectedAgent(agent);
    if (sessionId) {
      setCurrentChatSession(sessionId);
    }
    setShowAgentSelector(false);
    setIsChatOpen(true);
  };

  const handleChatNotificationOpen = useCallback((sessionId) => {
    const summary = getSessionSummary(sessionId);
    const peerId = String(summary?.peerId || '').trim();

    if (!sessionId || !peerId) {
      return;
    }

    setSelectedAgent({
      extension: peerId,
      username: peerId,
      displayName: peerId
    });
    setCurrentChatSession(sessionId);
    setShowAgentSelector(false);
    setIsChatOpen(true);
  }, [getSessionSummary]);

  const handleToggleHealth = () => {
    // Health check toggle - ARI bridge handles connection health
    setHealthEnabled(!healthEnabled);
    console.log(`Health monitoring ${!healthEnabled ? 'enabled' : 'disabled'}`);
  };
  const historyQueryId =
    complaintDetail?.QueryID ||
    activeChannelInfo?.queryId ||
    (Array.isArray(incomingCalls) ? incomingCalls[0]?.queryId : '') ||
    formData.queryId ||
    formData.callerId ||
    formData.PhoneNo ||
    '';
  const notesQueryId = historyQueryId;
  const notesUserId =
    user?.userId ??
    lsUser?.UserID ??
    lsUser?.userId ??
    lsUser?.ID ??
    lsUser?.id ??
    0;

  const handleSIPRegistrationOpen = () => {
    console.log('SIP Registration - using auto-registration from softphone on login');
    alert('Your extension is automatically registered when you login. Check the top bar for connection status.');
  };

  const handleShortcutsHelpOpen = useCallback(() => {
    setIsShortcutsHelpOpen(true);
  }, []);

  const handleShortcutsHelpClose = useCallback(() => {
    setIsShortcutsHelpOpen(false);
  }, []);

  const triggerLeftPanelAction = useCallback((actionName) => {
    const action = leftPanelRef.current?.[actionName];

    if (typeof action !== 'function') {
      return false;
    }

    return Boolean(action());
  }, []);

  const closeTopLayer = useCallback(() => {
    if (isShortcutsHelpOpen) {
      setIsShortcutsHelpOpen(false);
      return true;
    }

    if (leftPanelRef.current?.closeTransferModal?.()) {
      return true;
    }

    if (showAgentSelector) {
      setShowAgentSelector(false);
      return true;
    }

    if (isDialpadOpen) {
      setIsDialpadOpen(false);
      return true;
    }

    if (isListOpen) {
      setIsListOpen(false);
      return true;
    }

    if (isVoicemailOpen) {
      setIsVoicemailOpen(false);
      return true;
    }

    if (isDashboardOpen) {
      setIsDashboardOpen(false);
      return true;
    }

    if (isReminderOpen) {
      setIsReminderOpen(false);
      return true;
    }

    if (isHistoryOpen) {
      setIsHistoryOpen(false);
      return true;
    }

    if (isNotesOpen) {
      setIsNotesOpen(false);
      return true;
    }

    if (isSmsOpen) {
      setIsSmsOpen(false);
      return true;
    }

    if (isProfileOpen) {
      setIsProfileOpen(false);
      return true;
    }

    if (isSearchOpen) {
      setIsSearchOpen(false);
      return true;
    }

    if (isChatOpen) {
      setIsChatOpen(false);
      return true;
    }

    return false;
  }, [
    isChatOpen,
    isDashboardOpen,
    isDialpadOpen,
    isHistoryOpen,
    isListOpen,
    isNotesOpen,
    isProfileOpen,
    isReminderOpen,
    isSearchOpen,
    isShortcutsHelpOpen,
    isSmsOpen,
    isVoicemailOpen,
    showAgentSelector
  ]);

  // eslint-disable-next-line no-unused-vars
  const handleRegistrationChange = (isRegistered, userInfo) => {
    if (isRegistered) {
      setCurrentUser(userInfo);
      console.log(`✅ User registered: ${userInfo.username} (${userInfo.extension})`);
    } else {
      setCurrentUser(null);
      console.log(`❌ User unregistered: ${userInfo.extension}`);
    }
  };

  const handleEmergencyHangup = useCallback(async () => {
    try {
      console.log('🚨 Emergency hangup - disconnecting all calls...');
      
      if (softphoneCall) {
        softphoneHangup();
      }

      await ariHangupAllCalls();
      
      setIsLive(false);
      setCallDuration(0);
      setDialedNumber('');

      setTranscript(prev => [
        ...prev,
        {
          speaker: 'System',
          text: 'Emergency hangup - all calls terminated',
          timestamp: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })
        }
      ]);
    } catch (error) {
      console.error('❌ Emergency hangup failed:', error);
      setCallError(`Emergency hangup failed: ${error.message}`);
    }
  }, [softphoneCall, softphoneHangup, ariHangupAllCalls]);

  const handleAcceptIncomingCall = useCallback(
    callInfo => {
      console.log('📞 Incoming call accepted:', callInfo);

      const acceptedCallLink = getCallLinkFields(callInfo || {});
      const callerNumber = String(
        callInfo?.callerNumber ??
          callInfo?.cli ??
          callInfo?.number ??
          callInfo?.callerId ??
          ''
      ).trim();
      const displayNumber = callerNumber || callInfo?.callerName || 'Unknown';

      setIsLive(true);
      setCallDuration(0);
      setDialedNumber(callerNumber);

      setTranscript(prev => [
        ...prev,
        {
          speaker: 'System',
          text: `Incoming call from ${displayNumber} accepted`,
          timestamp: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })
        }
      ]);

      setFormData(prev => ({
        ...prev,
        cli: callerNumber,
        PhoneNo: callerNumber,
        callStateUniqueId: acceptedCallLink.uniqueId || prev.callStateUniqueId,
        callStateChannelId:
          acceptedCallLink.channelId ||
          String(callInfo?.channelId || '').trim() ||
          prev.callStateChannelId,
        acceptedBy: pickFirstValue(callInfo?.acceptedBy, prev.acceptedBy),
        acceptedOn: pickFirstValue(callInfo?.acceptedAt, prev.acceptedOn)
      }));

      // Auto-populate Identity tab from caller's phone number,
      // and Details tab from any incoming queryId on the call event.
      if (callerNumber && fetchContactDetailsRef.current) {
        fetchContactDetailsRef.current(callerNumber, -1, {
          clearOnMissing: true,
          callStateUniqueId: acceptedCallLink.uniqueId,
          callStateChannelId:
            acceptedCallLink.channelId || String(callInfo?.channelId || '').trim(),
          acceptedBy: callInfo?.acceptedBy,
          acceptedOn: callInfo?.acceptedAt
        });
      }
      const incomingQid = String(
        callInfo?.queryId ??
          callInfo?.QueryID ??
          callInfo?.qid ??
          callInfo?.complaintId ??
          ''
      ).trim();
      if (incomingQid && fetchComplaintDetailsRef.current) {
        fetchComplaintDetailsRef.current(incomingQid);
      }
    },
    []
  );

  const handleAcceptIncomingCallStart = useCallback(() => {
    return prepareAutoAnswer();
  }, [prepareAutoAnswer]);

  const handleAcceptSipIncomingCall = useCallback(async (callInfo) => {
    const answered = await sipAnswerCall();
    if (!answered) {
      return { success: false, error: 'Failed to answer SIP call' };
    }

    let acceptedCallInfo = callInfo || {};
    let acceptedControlChannelId = resolveCallControlChannelId(acceptedCallInfo);
    const callerNumber = String(
      acceptedCallInfo?.callerNumber ??
      acceptedCallInfo?.cli ??
      acceptedCallInfo?.number ??
      acceptedCallInfo?.callerId ??
      ''
    ).trim();

    // A fast SIP answer can happen before the popup's background context lookup
    // finishes. Resolve it once more after answering so controls get the real
    // Asterisk channel rather than the local "sip:*" notification key.
    if (!acceptedControlChannelId && callerNumber) {
      try {
        const params = new URLSearchParams({ callerNumber });
        const uniqueId = resolveIncomingCallDisplayId(acceptedCallInfo);
        if (uniqueId) params.set('uniqueId', uniqueId);
        if (agentId) params.set('agentId', agentId);

        const response = await fetch(`${CALL_CONTEXT_URL}?${params.toString()}`, {
          headers: { Accept: 'application/json' }
        });
        if (response.ok) {
          const context = await response.json();
          acceptedCallInfo = {
            ...acceptedCallInfo,
            callId: acceptedCallInfo.callId || context.callId || '',
            uniqueId: acceptedCallInfo.uniqueId || context.uniqueId || '',
            callStateUniqueId:
              acceptedCallInfo.callStateUniqueId ||
              context.callStateUniqueId ||
              '',
            callStateChannelId:
              resolveCallControlChannelId(acceptedCallInfo) ||
              context.channelId ||
              ''
          };
          acceptedControlChannelId = resolveCallControlChannelId(acceptedCallInfo);
        }
      } catch (error) {
        console.warn('SIP call context lookup failed:', error?.message || error);
      }
    }

    const acceptedCallLink = getCallLinkFields(acceptedCallInfo);
    if (acceptedControlChannelId) {
      setSipControlChannelId(acceptedControlChannelId);
    }
    const displayNumber = callerNumber || acceptedCallInfo?.callerName || 'Unknown';

    setIsLive(true);
    setCallDuration(0);
    setDialedNumber(callerNumber);

    setTranscript((prev) => [
      ...prev,
      {
        speaker: 'System',
        text: `Incoming call from ${displayNumber} accepted`,
        timestamp: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    ]);

    setFormData((prev) => ({
      ...prev,
      cli: callerNumber,
      PhoneNo: callerNumber,
      callStateUniqueId: acceptedCallLink.uniqueId || prev.callStateUniqueId,
      callStateChannelId: acceptedControlChannelId || prev.callStateChannelId,
      acceptedBy: pickFirstValue(acceptedCallInfo?.acceptedBy, prev.acceptedBy, username),
      acceptedOn: pickFirstValue(acceptedCallInfo?.acceptedAt, prev.acceptedOn, new Date().toISOString())
    }));

    // Auto-populate Identity tab from caller's phone number,
    // and Details tab from any incoming queryId on the call event.
    if (callerNumber && fetchContactDetailsRef.current) {
      fetchContactDetailsRef.current(callerNumber, -1, {
        clearOnMissing: true,
        callStateUniqueId: acceptedCallLink.uniqueId,
        callStateChannelId: acceptedControlChannelId,
        acceptedBy: pickFirstValue(acceptedCallInfo?.acceptedBy, username),
        acceptedOn: pickFirstValue(acceptedCallInfo?.acceptedAt, new Date().toISOString())
      });
    }
    const incomingQid = String(
      acceptedCallInfo?.queryId ??
        acceptedCallInfo?.QueryID ??
        acceptedCallInfo?.qid ??
        acceptedCallInfo?.complaintId ??
        ''
    ).trim();
    if (incomingQid && fetchComplaintDetailsRef.current) {
      fetchComplaintDetailsRef.current(incomingQid);
    }

    return { success: true, callInfo: acceptedCallInfo };
  }, [agentId, sipAnswerCall, username]);

  const toSearchRow = useCallback((c) => ({
    id: c.ID ?? c.id ?? c.QID ?? c.QueryID ?? c.QueryId ?? c.queryid ?? c.qid ?? '',
    queryId: c.QID ?? c.QueryID ?? c.QueryId ?? c.queryId ?? c.qid ?? c.qID ?? c.ComplaintID ?? c.complaintid ?? c.latest_complaint_id ?? c.latestComplaintId ?? '',
    callerId: c.CallerID ?? c.CallersID ?? c.callerId ?? c.callerid ?? c.callersid ?? '',
    name: c.Name ?? c.name ?? c.ContactPerson ?? c.contactperson ?? c.fullName ?? c.FullName ?? '',
    fatherName:
      c.FatherName ??
      c.fatherName ??
      c.fathername ??
      c.FatherHusbandName ??
      c.father_husband_name ??
      '',
    gender: c.Gender ?? c.gender ?? '',
    age: c.Age ?? c.age ?? '',
    additionalInfo3: c.Province ?? c.province ?? c.AdditionalInfo3 ?? c.additionalinfo3 ?? '',
    additionalInfo2: c.District ?? c.district ?? c.AdditionalInfo2 ?? c.additionalinfo2 ?? '',
    city: c.City ?? c.city ?? '',
    phoneNo:
      c.PhoneNo ??
      c.phoneno ??
      c.phoneNo ??
      c.phone ??
      c.ContactNumber ??
      c.contactnumber ??
      c.CLI ??
      c.cli ??
      '',
    address: c.Address ?? c.address ?? '',
    additionalInfo5:
      c.HeardFrom ?? c.heardFrom ?? c.heardfrom ?? c.AdditionalInfo5 ?? c.additionalinfo5 ?? '',
    cnic: c.CNIC ?? c.cnic ?? '',
    cellNo: c.CellNO ?? c.CellNo ?? c.cellno ?? c.cellNo ?? '',
    additionalInfo6: c.Religion ?? c.religion ?? c.AdditionalInfo6 ?? c.additionalinfo6 ?? '',
    additionalInfo1: c.Salary ?? c.salary ?? c.AdditionalInfo1 ?? c.additionalinfo1 ?? '',
    additionalInfo4: c.Country ?? c.country ?? c.AdditionalInfo4 ?? c.additionalinfo4 ?? '',
    additionalInfo10:
      c.CallbackTwo ??
      c.callbackTwo ??
      c.callbacktwo ??
      c.Callback2 ??
      c.AdditionalInfo10 ??
      c.additionalinfo10 ??
      '',
    additionalInfo9: c.Remarks ?? c.remarks ?? c.AdditionalInfo9 ?? c.additionalinfo9 ?? '',
    additionalInfo8:
      c.MaritalStatus ??
      c.maritalstatus ??
      c.maritalStatus ??
      c.AdditionalInfo8 ??
      c.additionalinfo8 ??
      '',
  }), []);

  const incomingRenderableAriCalls = useMemo(
    () =>
      (Array.isArray(incomingCalls) ? incomingCalls : []).filter((call) =>
        Boolean(String(call?.callerNumber || '').trim())
      ),
    [incomingCalls]
  );
  const incomingAriPrimaryCall = incomingRenderableAriCalls[0] || null;
  const incomingAriNumber = incomingAriPrimaryCall?.callerNumber || '';
  const hasAriIncoming = incomingRenderableAriCalls.length > 0;
  const incomingCallIdentitySources = useMemo(() => [
    ...incomingRenderableAriCalls,
    ...(Array.isArray(activeChannels) ? activeChannels : []),
    activeChannelInfo,
    {
      callerNumber: pickFirstValue(incomingAriNumber, currentCallerId, formData.cli, formData.PhoneNo),
      callStateUniqueId: formData.callStateUniqueId,
      callStateChannelId: formData.callStateChannelId,
      channelId: currentChannelId
    },
    {
      callerNumber: pickFirstValue(incomingAriNumber, currentCallerId, formData.cli, formData.PhoneNo),
      uniqueId: activeCallLink.uniqueId,
      channelId: activeCallLink.channelId
    }
  ].filter(Boolean), [
    activeCallLink.channelId,
    activeCallLink.uniqueId,
    activeChannelInfo,
    activeChannels,
    currentCallerId,
    currentChannelId,
    formData.PhoneNo,
    formData.callStateChannelId,
    formData.callStateUniqueId,
    formData.cli,
    incomingAriNumber,
    incomingRenderableAriCalls
  ]);
  const incomingNotificationCalls = useMemo(
    () => getIncomingNotificationCalls(
      incomingRenderableAriCalls,
      softphoneIncomingCall,
      incomingCallIdentitySources
    ),
    [incomingCallIdentitySources, incomingRenderableAriCalls, softphoneIncomingCall]
  );
  const incomingPrimaryCall = incomingNotificationCalls[0] || incomingAriPrimaryCall;
  const hasIncomingNotification = incomingNotificationCalls.length > 0;
  const incomingStableId = resolveIncomingCallDisplayId(incomingPrimaryCall);
  const incomingDisplayNumber = incomingPrimaryCall?.callerNumber || incomingAriNumber;
  const isDialing = callStatus === 'dialing';
  const isConnecting = callStatus === 'connecting';
  const activeChannelStatus = String(activeChannelInfo?.status || '').trim().toLowerCase();
  const activeDialStatus = String(activeChannelInfo?.dialStatus || '').trim().toUpperCase();
  const activeSipResponseCode = String(activeChannelInfo?.sipResponseCode || '').trim();
  const outboundAnswerDetected =
    isOutboundMode &&
    Boolean(currentChannelId) &&
    (activeChannelStatus === 'connected' ||
      activeChannelStatus === 'in-call' ||
      activeDialStatus === 'ANSWER' ||
      activeSipResponseCode === '200' ||
      Boolean(activeChannelInfo?.acceptedAt && activeDialStatus === 'ANSWER'));
  const outboundContactNumber = String(
    pickFirstValue(
      activeChannelInfo?.calledNumber,
      activeChannelInfo?.number,
      activeChannelInfo?.callerNumber,
      dialedNumber
    ) || ''
  ).trim();
  const outboundQueryId = String(
    activeChannelInfo?.queryId ??
      activeChannelInfo?.QueryID ??
      activeChannelInfo?.qid ??
      activeChannelInfo?.complaintId ??
      ''
  ).trim();

  useEffect(() => {
    if (!outboundAnswerDetected || !outboundContactNumber) return;

    const hydrationKey = `${currentChannelId}:${outboundContactNumber}`;
    if (lastHydratedOutboundCallRef.current === hydrationKey) return;
    lastHydratedOutboundCallRef.current = hydrationKey;

    setFormData(prev => ({
      ...prev,
      cli: outboundContactNumber,
      PhoneNo: outboundContactNumber
    }));

    if (fetchContactDetailsRef.current) {
      fetchContactDetailsRef.current(outboundContactNumber, -1);
    }
    if (outboundQueryId && fetchComplaintDetailsRef.current) {
      fetchComplaintDetailsRef.current(outboundQueryId);
    }
  }, [currentChannelId, outboundAnswerDetected, outboundContactNumber, outboundQueryId]);

  const outboundSipRingbackEventReceived = hasOutboundSipRingbackEvent(activeChannelInfo);
  const isOutboundFallbackRingbackEligible =
    isOutboundMode &&
    Boolean(currentChannelId) &&
    !outboundAnswerDetected &&
    !outboundSipRingbackEventReceived &&
    ['dialing', 'connecting', 'ringing'].includes(normalizedCallStatus);
  const isOutboundFallbackRinging =
    isOutboundFallbackRingbackEligible &&
    ['connecting', 'ringing'].includes(normalizedCallStatus);
  const isRinging =
    (callStatus === 'ringing' && !outboundAnswerDetected) ||
    hasAriIncoming ||
    isOutboundFallbackRinging;
  const hasSoftphoneCall = Boolean(softphoneCall);
  const hasAriActiveChannel = Array.isArray(activeChannels) && activeChannels.length > 0;
  const activeAsteriskNumber = String(
    activeChannelInfo?.callerNumber ||
    activeChannelInfo?.calledNumber ||
    activeChannelInfo?.number ||
    incomingDisplayNumber ||
    ''
  ).trim();
  const asteriskCallerId = pickFirstValue(
    activeChannelInfo?.callerNumber,
    incomingAriPrimaryCall?.callerNumber,
    activeChannelInfo?.calledNumber,
    activeChannelInfo?.number
  );
  const asteriskQueryId = pickFirstValue(
    activeChannelInfo?.queryId,
    incomingAriPrimaryCall?.queryId
  );
  const asteriskAcceptedBy = pickFirstValue(
    activeChannelInfo?.acceptedBy,
    incomingAriPrimaryCall?.acceptedBy
  );
  const asteriskAcceptedOn = pickFirstValue(
    activeChannelInfo?.acceptedAt,
    incomingAriPrimaryCall?.acceptedAt
  );
  const effectiveCallerId = asteriskCallerId || currentCallerId || formData.cli || '';
  const effectiveQueryId = asteriskQueryId || formData.queryId || '';
  const asteriskCallDuration = (() => {
    if (hasAriIncoming) {
      return Math.max(0, Number(incomingAriPrimaryCall?.duration || 0));
    }
    if (hasAriActiveChannel) {
      return Math.max(0, Number(activeChannelInfo?.duration || 0));
    }
    return 0;
  })();
  const isOnCall =
    hasSoftphoneCall ||
    outboundAnswerDetected ||
    (hasAriActiveChannel &&
      (callStatus === 'in-call' || callStatus === 'connected'));
  const isCallControlActive = Boolean(currentChannelId) && (
    outboundAnswerDetected ||
    ['in-call', 'connected'].includes(normalizedCallStatus) ||
    (!isOutboundMode && softphoneCall?.state === 'answered')
  );
  const hasActiveCallUi =
    hasSoftphoneCall || hasAriActiveChannel || isRinging || isConnecting || isDialing;
  const showOutboundHangup =
    isOutboundMode && ['dialing', 'connecting', 'ringing', 'connected', 'in-call'].includes(normalizedCallStatus);
  const shouldPreventPageExit = hasActiveCallUi || hasIncomingNotification || isLive;
  const activeNumberToShow = hasActiveCallUi
    ? (activeAsteriskNumber || dialedNumber || formData.cli || '')
    : '';
  const statusCaller = asteriskCallerId || incomingDisplayNumber || dialedNumber || formData.cli || '';
  const topBarId = resolveCallHeaderId(
    effectiveQueryId,
    hasActiveCallUi,
    resolveIncomingCallDisplayId(incomingPrimaryCall),
    incomingPrimaryCall?.channelId,
    resolveIncomingCallDisplayId(activeChannelInfo),
    formData.callStateUniqueId,
    currentChannelId
  );

  useEffect(() => {
    if (!shouldPreventPageExit) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const handleRefreshShortcut = (event) => {
      const key = String(event.key || '').toLowerCase();
      const isRefreshShortcut =
        key === 'f5' || ((event.ctrlKey || event.metaKey) && key === 'r');
      if (!isRefreshShortcut) return;
      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleRefreshShortcut, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleRefreshShortcut, true);
    };
  }, [shouldPreventPageExit]);

  useEffect(() => {
    const nextLookup = {};
    const uniqueId = pickFirstValue(
      formData.callStateUniqueId,
      activeCallLink.uniqueId,
      activeChannelInfo?.stableId,
      activeChannelInfo?.linkedId,
      activeChannelInfo?.ivrUid
    );
    const channelId = pickFirstValue(
      formData.callStateChannelId,
      activeCallLink.channelId,
      currentChannelId
    );
    const recordingName = pickFirstValue(activeChannelInfo?.recordingName);
    const queryId = pickFirstValue(
      activeChannelInfo?.queryId,
      formData.queryId
    );
    const callerNumber = pickFirstValue(
      activeChannelInfo?.callerNumber,
      activeChannelInfo?.calledNumber,
      effectiveCallerId,
      formData.cli
    );
    const callerName = pickFirstValue(
      activeChannelInfo?.callerName,
      formData.name
    );

    if (uniqueId) nextLookup.uniqueId = uniqueId;
    if (channelId) nextLookup.channelId = channelId;
    if (recordingName) nextLookup.recordingName = recordingName;
    if (queryId) nextLookup.queryId = queryId;
    if (callerNumber) nextLookup.callerNumber = callerNumber;
    if (callerName) nextLookup.callerName = callerName;
    if (activeChannelInfo?.linkedId) nextLookup.linkedId = String(activeChannelInfo.linkedId).trim();
    if (activeChannelInfo?.stableId) nextLookup.stableId = String(activeChannelInfo.stableId).trim();

    if (Object.keys(nextLookup).length) {
      transcriptLookupRef.current = {
        ...transcriptLookupRef.current,
        ...nextLookup
      };
    }
  }, [
    activeCallLink.channelId,
    activeCallLink.uniqueId,
    activeChannelInfo?.callerName,
    activeChannelInfo?.callerNumber,
    activeChannelInfo?.calledNumber,
    activeChannelInfo?.ivrUid,
    activeChannelInfo?.linkedId,
    activeChannelInfo?.queryId,
    activeChannelInfo?.recordingName,
    activeChannelInfo?.stableId,
    currentChannelId,
    effectiveCallerId,
    formData.callStateChannelId,
    formData.callStateUniqueId,
    formData.cli,
    formData.name,
    formData.queryId
  ]);

  useEffect(() => {
    const wasActive = transcriptCallActiveRef.current;
    transcriptCallActiveRef.current = hasActiveCallUi;

    if (hasActiveCallUi) {
      if (!wasActive) {
        clearTranscriptPolling();
        transcriptRequestKeyRef.current = '';
        setSelectedRecordingKey('');
        setAgentSpeakerId('');
        transcriptLookupRef.current = {
          uniqueId: pickFirstValue(
            formData.callStateUniqueId,
            activeCallLink.uniqueId,
            activeChannelInfo?.stableId,
            activeChannelInfo?.linkedId,
            activeChannelInfo?.ivrUid
          ),
          channelId: pickFirstValue(
            formData.callStateChannelId,
            activeCallLink.channelId,
            currentChannelId
          ),
          recordingName: pickFirstValue(activeChannelInfo?.recordingName),
          queryId: pickFirstValue(activeChannelInfo?.queryId, formData.queryId),
          callerNumber: pickFirstValue(
            activeChannelInfo?.callerNumber,
            activeChannelInfo?.calledNumber,
            effectiveCallerId,
            formData.cli
          ),
          callerName: pickFirstValue(activeChannelInfo?.callerName, formData.name),
          linkedId: pickFirstValue(activeChannelInfo?.linkedId),
          stableId: pickFirstValue(activeChannelInfo?.stableId)
        };
        setTranscript([]);
        setTranscriptRecord(null);
        setTranscriptStatus({
          state: LIVE_TRANSCRIPTION_ENABLED ? 'live' : 'disabled',
          message: LIVE_TRANSCRIPTION_ENABLED
            ? 'The call is being recorded. The transcript will appear here after the call ends.'
            : 'Live transcription disabled.'
        });
      }
      return;
    }

    if (!wasActive || !callAnsweredRef.current || !LIVE_TRANSCRIPTION_ENABLED) {
      return;
    }

    const requestLookup = transcriptLookupRef.current || {};
    const requestKey = [
      requestLookup.recordingName,
      requestLookup.uniqueId,
      requestLookup.channelId
    ].filter(Boolean).join('|');

    if (!requestKey || transcriptRequestKeyRef.current === requestKey) {
      return;
    }

    transcriptRequestKeyRef.current = requestKey;
    void queueTranscriptForLookup(requestLookup);
  }, [
    activeCallLink.channelId,
    activeCallLink.uniqueId,
    activeChannelInfo?.callerName,
    activeChannelInfo?.callerNumber,
    activeChannelInfo?.calledNumber,
    activeChannelInfo?.ivrUid,
    activeChannelInfo?.linkedId,
    activeChannelInfo?.queryId,
    activeChannelInfo?.recordingName,
    activeChannelInfo?.stableId,
    clearTranscriptPolling,
    currentChannelId,
    effectiveCallerId,
    formData.callStateChannelId,
    formData.callStateUniqueId,
    formData.cli,
    formData.name,
    formData.queryId,
    hasActiveCallUi,
    queueTranscriptForLookup
  ]);

  useEffect(() => () => {
    clearTranscriptPolling();
  }, [clearTranscriptPolling]);

  useEffect(() => {
    if (!asteriskAcceptedBy && !asteriskAcceptedOn) {
      return;
    }

    setFormData((prev) => {
      const nextAcceptedBy = asteriskAcceptedBy || prev.acceptedBy;
      const nextAcceptedOn = asteriskAcceptedOn || prev.acceptedOn;

      if (
        String(prev.acceptedBy || '').trim() === String(nextAcceptedBy || '').trim() &&
        String(prev.acceptedOn || '').trim() === String(nextAcceptedOn || '').trim()
      ) {
        return prev;
      }

      return {
        ...prev,
        acceptedBy: nextAcceptedBy,
        acceptedOn: nextAcceptedOn
      };
    });
  }, [asteriskAcceptedBy, asteriskAcceptedOn]);

  const shortcutsBlocked = isShortcutsHelpOpen;
  const keyboardShortcuts = useMemo(() => ([
    {
      key: 'F2',
      allowInInput: true,
      handler: handleShortcutsHelpOpen
    },
    {
      key: 'Escape',
      allowInInput: true,
      handler: closeTopLayer
    },
    {
      key: 'A',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: handleDashboardOpen
    },
    {
      key: 'K',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => {
        void handleChatOpen();
      }
    },
    {
      key: 'N',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked || hasActiveCallUi || wrapupSeconds > 0,
      handler: handleNewCall
    },
    {
      key: 'D',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => triggerLeftPanelAction('openDialpad')
    },
    {
      key: 'H',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked || !hasActiveCallUi,
      handler: () => triggerLeftPanelAction('toggleHold')
    },
    {
      key: 'M',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked || !hasActiveCallUi,
      handler: () => triggerLeftPanelAction('toggleMute')
    },
    {
      key: 'T',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked || !hasActiveCallUi,
      handler: () => triggerLeftPanelAction('openTransfer')
    },
    {
      key: 'F',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked || !hasActiveCallUi,
      handler: () => triggerLeftPanelAction('transferToFeedback')
    },
    {
      key: 'X',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked || !hasActiveCallUi,
      handler: () => triggerLeftPanelAction('hangup')
    },
    {
      key: 'X',
      ctrlKey: true,
      altKey: true,
      shiftKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked || !isConnected,
      handler: () => triggerLeftPanelAction('emergencyCleanup')
    },
    {
      key: 'S',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => triggerLeftPanelAction('openSearch')
    },
    {
      key: 'L',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => triggerLeftPanelAction('openList')
    },
    {
      key: 'P',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => triggerLeftPanelAction('openProfile')
    },
    {
      key: 'R',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => triggerLeftPanelAction('openReminder')
    },
    {
      key: 'Y',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => triggerLeftPanelAction('openHistory')
    },
    {
      key: 'O',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => triggerLeftPanelAction('openNotes')
    },
    {
      key: 'U',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => triggerLeftPanelAction('openSms')
    },
    {
      key: 'I',
      ctrlKey: true,
      altKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => triggerLeftPanelAction('openRegistration')
    },
    {
      key: 'N',
      ctrlKey: true,
      altKey: true,
      shiftKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: handleNewCaller
    },
    {
      key: 'Q',
      ctrlKey: true,
      altKey: true,
      shiftKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: handleNewQuery
    },
    {
      key: 'C',
      ctrlKey: true,
      altKey: true,
      shiftKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: handleClearInfo
    },
    {
      key: 'Enter',
      ctrlKey: true,
      altKey: true,
      shiftKey: true,
      allowInInput: true,
      disabled: shortcutsBlocked,
      handler: () => {
        void handleProceed();
      }
    }
  ]), [
    closeTopLayer,
    handleChatOpen,
    handleClearInfo,
    handleDashboardOpen,
    handleNewCall,
    handleNewCaller,
    handleNewQuery,
    handleProceed,
    handleShortcutsHelpOpen,
    hasActiveCallUi,
    isConnected,
    shortcutsBlocked,
    triggerLeftPanelAction,
    wrapupSeconds
  ]);

  useKeyboardShortcuts(keyboardShortcuts);

  useEffect(() => {
    const suffix = statusCaller ? ` (${statusCaller})` : '';

    if (isRinging && !callStatusRef.current.ringing) {
      appendCallStatusLog(`Ringing${suffix}`);
      callStatusRef.current = { ringing: true, answered: false, failed: false };
    }

    if (isOnCall && !callStatusRef.current.answered) {
      appendCallStatusLog(`Answered${suffix}`);
      callStatusRef.current = { ringing: false, answered: true, failed: false };
    }

    if (callStatus === 'failed' && !callStatusRef.current.failed) {
      appendCallStatusLog(`No Answer${suffix}`);
      callStatusRef.current = { ringing: false, answered: false, failed: true };
    }

    if (!isRinging && callStatusRef.current.ringing && !callStatusRef.current.answered) {
      appendCallStatusLog(`No Answer${suffix}`);
      callStatusRef.current = { ringing: false, answered: false, failed: false };
    }

    if (!isOnCall && callStatusRef.current.answered && !isRinging) {
      callStatusRef.current = { ringing: false, answered: false, failed: false };
    }

    if (callStatus !== 'failed' && callStatusRef.current.failed) {
      callStatusRef.current.failed = false;
    }
  }, [appendCallStatusLog, callStatus, isOnCall, isRinging, statusCaller]);

  useEffect(() => {
    if (isOnCall) {
      callAnsweredRef.current = true;
    }
  }, [isOnCall]);

  useEffect(() => {
    const wasActive = prevActiveCallRef.current;
    const isActive = hasActiveCallUi;
    prevActiveCallRef.current = isActive;

    if (isActive) {
      setWrapupSeconds(0);
      if (!wasActive) {
        callAnsweredRef.current = false;
      }
      return;
    }

    if (wasActive && !isActive && callAnsweredRef.current) {
      setWrapupSeconds(10);
    }
  }, [hasActiveCallUi]);

  useEffect(() => {
    if (wrapupSeconds <= 0) return;
    const timer = setTimeout(() => {
      setWrapupSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearTimeout(timer);
  }, [wrapupSeconds]);

  useEffect(() => {
    if (!infoMessage) return undefined;
    const timer = setTimeout(() => setInfoMessage(''), 3000);
    return () => clearTimeout(timer);
  }, [infoMessage]);

  useEffect(() => {
    if (!errorMessage) return undefined;
    const timer = setTimeout(() => setErrorMessage(''), 8000);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  useEffect(() => {
    if (!hasIncomingNotification) {
      document.body.classList.remove('incoming-call-attention');
      if (attentionTimerRef.current) {
        clearInterval(attentionTimerRef.current);
        attentionTimerRef.current = null;
      }
      if (originalTitleRef.current) {
        document.title = originalTitleRef.current;
      }
      lastNotifiedCallIdRef.current = '';
      return;
    }

    if (!originalTitleRef.current) {
      originalTitleRef.current = document.title;
    }

    document.body.classList.add('incoming-call-attention');
    if (!attentionTimerRef.current) {
      let toggle = false;
      attentionTimerRef.current = setInterval(() => {
        document.title = toggle ? 'CALL RINGING' : originalTitleRef.current;
        toggle = !toggle;
      }, 1000);
    }

    if (document.hidden) {
      try {
        window.focus();
      } catch (err) {
        // Ignore focus errors (browser restrictions)
      }
    }

    if (incomingStableId && lastNotifiedCallIdRef.current !== incomingStableId && 'Notification' in window) {
      lastNotifiedCallIdRef.current = incomingStableId;
      if (Notification.permission === 'granted') {
        const body = incomingDisplayNumber ? `Caller ${incomingDisplayNumber}` : 'Incoming call';
        const notification = new Notification('Incoming call', {
          body,
          tag: incomingStableId
        });
        notification.onclick = () => {
          try {
            window.focus();
          } catch (err) {
            // ignore
          }
        };
      } else if (Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            const body = incomingDisplayNumber ? `Caller ${incomingDisplayNumber}` : 'Incoming call';
            const notification = new Notification('Incoming call', {
              body,
              tag: incomingStableId
            });
            notification.onclick = () => {
              try {
                window.focus();
              } catch (err) {
                // ignore
              }
            };
          }
        }).catch(() => {});
      }
    }
  }, [hasIncomingNotification, incomingDisplayNumber, incomingStableId]);

  const statusBadge = (() => {
    if (callStatus === 'failed') return { text: 'Failed', className: 'bg-danger' };
    if (isOnCall) return { text: 'On Call', className: 'bg-success' };
    if (isRinging) return { text: 'Ringing', className: 'bg-primary' };
    if (isDialing) {
      return { text: 'Dialing', className: 'bg-info' };
    }
    if (callStatus === 'connecting') {
      return { text: 'Connecting', className: 'bg-info' };
    }
    if (wrapupSeconds > 0) {
      return { text: `Wrap Up ${wrapupSeconds}s`, className: 'bg-warning text-dark' };
    }
    if (isOnCall) return { text: 'On Call', className: 'bg-success' };
    return { text: 'Idle', className: 'bg-secondary' };
  })();

  const canEditCli = true;
  const presenceStatus = (() => {
    if (isOutboundMode) return 'notready';
    if (agentAvailability === 'Not Ready') return 'notready';
    if (agentAvailability === 'Lunch') return 'lunch';
    if (agentAvailability === 'Training') return 'training';
    if (agentAvailability === 'Meeting') return 'meeting';
    if (agentAvailability === 'Prayer') return 'prayer';
    if (agentAvailability === 'Rest Room') return 'restroom';
    return 'idle';
  })();

  const agentStatus = (() => {
    if (callStatus === 'failed') return 'failed';
    if (isOnCall) return 'oncall';
    if (isRinging) return 'ringing';
    if (isDialing || callStatus === 'connecting') return 'oncall';
    if (wrapupSeconds > 0) return 'wrapup';
    if (presenceStatus !== 'idle') return presenceStatus;
    return 'idle';
  })();

  const handleAgentAvailabilityChange = useCallback(async (nextAvailability) => {
    if (!AVAILABILITY_OPTIONS.includes(nextAvailability) || isOutboundMode) return;
    if (!agentId) {
      setErrorMessage('Agent extension is not available. Status was not changed.');
      return;
    }

    const statusByAvailability = {
      Ready: 'idle',
      'Not Ready': 'notready',
      Lunch: 'lunch',
      Training: 'training',
      Meeting: 'meeting',
      Prayer: 'prayer',
      'Rest Room': 'restroom'
    };
    const result = await asteriskService.updateAgentStatus(agentId, statusByAvailability[nextAvailability], {
      cmsUserId,
      cmsUserName,
      direction: normalizedAgentMode,
      queueName: agentQueueName
    });

    if (!result?.success) {
      setErrorMessage('Agent status update failed. Current status was not changed.');
      return;
    }

    setAgentAvailability(nextAvailability);
    setQueueMetrics((prev) => ({
      ...prev,
      agentStatus: String(result.user?.status || statusByAvailability[nextAvailability]).trim(),
      agentStatusLabel: nextAvailability
    }));
  }, [agentId, agentQueueName, cmsUserId, cmsUserName, isOutboundMode, normalizedAgentMode]);

  useEffect(() => {
    if (isOnCall || outboundSipRingbackEventReceived) {
      stopOutboundRingback();
      return;
    }

    if (isOutboundFallbackRingbackEligible) {
      scheduleOutboundRingbackFallback();
      return;
    }

    const shouldStopRingback =
      !isOutboundMode ||
      !currentChannelId ||
      !hasActiveCallUi ||
      isOnCall ||
      ['in-call', 'connected', 'failed', 'idle', 'ended', 'disconnected', 'feedback', 'ending'].includes(normalizedCallStatus);

    if (shouldStopRingback) {
      stopOutboundRingback();
    }
  }, [
    currentChannelId,
    hasActiveCallUi,
    isOnCall,
    isOutboundFallbackRingbackEligible,
    isOutboundMode,
    normalizedCallStatus,
    outboundSipRingbackEventReceived,
    scheduleOutboundRingbackFallback,
    stopOutboundRingback
  ]);

  useEffect(() => {
    if (!isOutboundMode || !outboundCallResponseArmedRef.current) return;

    const activePrompt = getCallResponsePrompt(activeChannelInfo);
    const responseInfo = activePrompt ? activeChannelInfo : lastCallResponse;
    const prompt = activePrompt || getCallResponsePrompt(lastCallResponse);
    if (!prompt) return;

    const responseKey = [
      responseInfo?.channelId || currentChannelId || '',
      responseInfo?.stableId || '',
      responseInfo?.dialStatus || '',
      responseInfo?.sipResponseCode || '',
      responseInfo?.q850Cause || '',
      prompt.key
    ].map((value) => String(value || '').trim()).join('|');

    if (!responseKey || lastPlayedCallResponseRef.current === responseKey) return;
    lastPlayedCallResponseRef.current = responseKey;

    const suffix = statusCaller ? ` (${statusCaller})` : '';
    appendCallStatusLog(`Call failed: ${prompt.text}${suffix}`);
    outboundCallResponseArmedRef.current = false;
    const hangupChannelId = responseInfo?.channelId || currentChannelId || '';
    void (async () => {
      await playCallResponsePrompt(prompt);
      await hangupOutboundAfterResponsePrompt(hangupChannelId);
    })();
  }, [
    activeChannelInfo,
    appendCallStatusLog,
    currentChannelId,
    getCallResponsePrompt,
    hangupOutboundAfterResponsePrompt,
    isOutboundMode,
    lastCallResponse,
    playCallResponsePrompt,
    statusCaller
  ]);

  useEffect(() => () => {
    stopOutboundRingback();
    stopCallResponsePrompt();
    const audioContext = callResponseAudioContextRef.current;
    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close().catch(() => {});
    }
    callResponseAudioContextRef.current = null;
  }, [stopCallResponsePrompt, stopOutboundRingback]);

  useEffect(() => {
    if (!isOutboundMode || ['in-call', 'connected'].includes(normalizedCallStatus)) {
      outboundCallResponseArmedRef.current = false;
    }
  }, [isOutboundMode, normalizedCallStatus]);

  const statusIndicatorClassName = (() => {
    if (statusBadge.text.startsWith('Wrap Up')) return 'text-warning';
    if (statusBadge.text === 'On Call') return 'text-success';
    if (statusBadge.text === 'Ringing') return 'text-primary';
    if (statusBadge.text === 'Dialing') return 'text-info';
    if (statusBadge.text === 'Connecting') return 'text-info';
    if (statusBadge.text === 'Failed') return 'text-danger';
    return 'text-secondary';
  })();

  useEffect(() => {
    if (!agentId) return;
    asteriskService.setAgentContext({
      agentId,
      status: agentStatus,
      cmsUserId,
      cmsUserName,
      direction: normalizedAgentMode,
      queueName: agentQueueName
    });
  }, [agentId, agentStatus, cmsUserId, cmsUserName, normalizedAgentMode, agentQueueName]);

  useEffect(() => {
    let cancelled = false;

    const loadQueueMetrics = async () => {
      if (queueMetricsRequestInFlightRef.current) {
        return;
      }

      if (!agentId) {
        if (!cancelled) {
          setQueueMetrics({
            totalWaitingCallers: 0,
            longestWaitSeconds: 0,
            longestWaitStartedAt: '',
            nextCallerNumber: '',
            nextQueueName: '',
            agentStatus: '',
            agentStatusLabel: '',
            updatedAt: '',
            metricsReceivedAt: 0
          });
        }
        return;
      }

      queueMetricsRequestInFlightRef.current = true;
      try {
        const result = await asteriskService.getQueueMetrics(agentId);
        if (cancelled || !result?.success) return;

        setQueueMetrics((prev) => {
          const next = {
            totalWaitingCallers: Number(result.totalWaitingCallers || 0),
            longestWaitSeconds: Number(result.longestWaitSeconds || 0),
            longestWaitStartedAt: String(result.longestWaitStartedAt || '').trim(),
            nextCallerNumber: String(result.nextCallerNumber || '').trim(),
            nextQueueName: String(result.nextQueueName || '').trim(),
            agentStatus: String(result.agentStatus || '').trim(),
            agentStatusLabel: String(result.agentStatusLabel || '').trim(),
            updatedAt: String(result.updatedAt || '').trim(),
            metricsReceivedAt: Date.now()
          };

          const same =
            prev.totalWaitingCallers === next.totalWaitingCallers &&
            prev.longestWaitSeconds === next.longestWaitSeconds &&
            prev.longestWaitStartedAt === next.longestWaitStartedAt &&
            prev.nextCallerNumber === next.nextCallerNumber &&
            prev.nextQueueName === next.nextQueueName &&
            prev.agentStatus === next.agentStatus &&
            prev.agentStatusLabel === next.agentStatusLabel &&
            prev.updatedAt === next.updatedAt;

          return same ? prev : next;
        });
      } finally {
        queueMetricsRequestInFlightRef.current = false;
      }
    };

    loadQueueMetrics();
    const interval = setInterval(loadQueueMetrics, 3000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [agentId]);

  useEffect(() => {
    if (isOutboundMode || isOnCall || isRinging || isDialing || wrapupSeconds > 0) return;

    const availabilityByStatus = {
      idle: 'Ready',
      ready: 'Ready',
      notready: 'Not Ready',
      'not ready': 'Not Ready',
      lunch: 'Lunch',
      training: 'Training',
      meeting: 'Meeting',
      prayer: 'Prayer',
      restroom: 'Rest Room',
      'rest room': 'Rest Room'
    };
    const backendAvailability = availabilityByStatus[String(queueMetrics.agentStatus || '').trim().toLowerCase()];
    if (backendAvailability && backendAvailability !== agentAvailability) {
      setAgentAvailability(backendAvailability);
    }
  }, [agentAvailability, isDialing, isOnCall, isOutboundMode, isRinging, queueMetrics.agentStatus, wrapupSeconds]);

  const isAgentUnavailable =
    isOnCall ||
    isConnecting ||
    wrapupSeconds > 0;

  // NEW: fetch full contact info (like old Frontoffice)
  const fetchContactDetails = async (phoneNo, cid = -1, options = {}) => {
    try {
      const lockedComplaintId = normalizeText(options?.complaintId, '');
      const shouldLoadComplaint = options?.loadComplaint !== false;
      const shouldUpdateSearchRows = options?.updateSearchRows !== false;
      if (shouldUpdateSearchRows) {
        setSearchLoading(true);
      }
      logApi('find-contact By wajid', { phoneNo, cid, lockedComplaintId });
      //api url
      const res = await fetch(`${API_BASE}/find-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNo, manual: true, cid, CID: cid })
      });

      const text = await res.text();
      if (!res.ok) {
        console.error('find-contact error:', res.status, text);
        if (shouldUpdateSearchRows) {
          setSearchRows([]);
        }
        return;
      }

      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        console.error('Invalid JSON from find-contact:', text);
        if (shouldUpdateSearchRows) {
          setSearchRows([]);
        }
        return;
      }

      const contacts = Array.isArray(result?.contacts)
        ? result.contacts
        : Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : result?.contact
              ? [result.contact]
              : result?.record
                ? [result.record]
                : [];

      if (contacts.length > 0) {
        if (shouldUpdateSearchRows) {
          setSearchRows(contacts.map(toSearchRow));
        }
        const c = contacts[0];

        // If the backend included a latest complaint id for this contact,
        // hand it to fetchComplaintDetails so the Details tab also fills in.
        const latestComplaintId = String(
          c.latest_complaint_id ??
            c.LatestComplaintId ??
            c.LatestComplaintID ??
            c.latestComplaintId ??
            ''
        ).trim();
        const complaintIdToLoad = lockedComplaintId || latestComplaintId;
        if (shouldLoadComplaint && complaintIdToLoad) {
          // Avoid blocking — fire-and-forget.
          fetchComplaintDetails(complaintIdToLoad);
        }

        setFormData(prev => ({
          ...prev,
          cli: normalizeText(
            c.PhoneNo || c.phoneno || c.phoneNo || c.phone || c.ContactNumber || c.CLI || c.cli,
            prev.cli
          ),
          PhoneNo: normalizeText(
            c.PhoneNo || c.phoneno || c.phoneNo || c.phone || c.ContactNumber || c.CLI || c.cli,
            prev.PhoneNo
          ),
          name: normalizeText(
            c.Name || c.name || c.ContactPerson || c.fullName || c.FullName,
            prev.name
          ),
          contactId: normalizeText(
            c.ID || c.id || c.ContactID || c.ContactId || c.contactId || c.contact_id,
            prev.contactId
          ),
          gender: normalizeGender(c.Gender || c.gender) || prev.gender,
          age: normalizeText(c.Age || c.age, prev.age),
          cnic: normalizeText(c.CNIC || c.cnic, prev.cnic),
          city: normalizeText(c.City || c.city, prev.city),
          district: normalizeText(
            c.District || c.district || c.AdditionalInfo2,
            prev.district
          ),
          province: normalizeText(
            c.Province || c.province || c.AdditionalInfo3,
            prev.province
          ),
          country: normalizeText(
            c.Country || c.country || c.AdditionalInfo4,
            prev.country
          ),
          heardFrom: normalizeText(
            c.HeardFrom || c.heardFrom || c.heardfrom || c.AdditionalInfo5,
            prev.heardFrom
          ),
          fatherHusbandName: normalizeText(
            c.FatherName || c.fatherName || c.fathername || c.FatherHusbandName,
            prev.fatherHusbandName
          ),
          salary: normalizeText(
            c.Salary || c.salary || c.AdditionalInfo1,
            prev.salary
          ),
          address: normalizeText(c.Address || c.address, prev.address),
          callback1: normalizeText(
            c.CellNO || c.CellNo || c.cellno || c.Cell1,
            prev.callback1
          ),
          callback2: normalizeText(
            c.CallbackTwo || c.callbackTwo || c.callbacktwo || c.Callback2 || c.AdditionalInfo10,
            prev.callback2
          ),
          religion: normalizeText(
            c.Religion || c.religion || c.AdditionalInfo6,
            prev.religion
          ),
          maritalStatus: normalizeText(
            c.MaritalStatus || c.maritalstatus || c.maritalStatus || c.AdditionalInfo8,
            prev.maritalStatus
          ),
          remarks: normalizeText(
            c.Remarks || c.remarks || c.AdditionalInfo9,
            prev.remarks
          )
        }));
      } else {
        if (shouldUpdateSearchRows) {
          setSearchRows([]);
        }
        if (options?.clearOnMissing) {
          const acceptedCallerNumber = normalizeText(phoneNo, '');
          handleClearInfo();
          setComplaintDetail(null);
          setFormData(prev => ({
            ...prev,
            cli: acceptedCallerNumber,
            PhoneNo: acceptedCallerNumber,
            callStateUniqueId: normalizeText(options?.callStateUniqueId, prev.callStateUniqueId),
            callStateChannelId: normalizeText(options?.callStateChannelId, prev.callStateChannelId),
            acceptedBy: normalizeText(options?.acceptedBy, prev.acceptedBy),
            acceptedOn: normalizeText(options?.acceptedOn, prev.acceptedOn)
          }));
          setCurrentCallerId(acceptedCallerNumber);
        }
      }
    } catch (err) {
      console.error('fetchContactDetails error:', err);
      if (options?.updateSearchRows !== false) {
        setSearchRows([]);
      }
    } finally {
      if (options?.updateSearchRows !== false) {
        setSearchLoading(false);
      }
    }
  };

  // Keep refs pointing at the latest implementations so stable callbacks
  // (e.g. handleAcceptIncomingCall with empty deps) can invoke them.
  fetchContactDetailsRef.current = fetchContactDetails;
  fetchComplaintDetailsRef.current = fetchComplaintDetails;

  const fetchHeardFrom = useCallback(async () => {
    try {
      logApi('heard-from', {});
      //api url
      const res = await fetch(`${API_BASE}/heard-from`);
      const data = await res.json();
      if (data?.success) setHeardFromOptions(data.data);
    } catch (err) {
      console.error('Heard From fetch error:', err);
    }
  }, [logApi]);

  const fetchCities = useCallback(async () => {
    try {
      logApi('cities', {});
      //api url
      const res = await fetch(`${API_BASE}/cities`);
      const data = await res.json();
      const raw =
        (Array.isArray(data) && data) ||
        (Array.isArray(data?.data) && data.data) ||
        (Array.isArray(data?.value) && data.value) ||
        (Array.isArray(data?.cities) && data.cities) ||
        [];
      const normalized = raw
        .map((item) => {
          if (item === null || item === undefined) return null;
          if (typeof item === 'string') return item;
          return (
            item.CityName ||
            item.cityname ||
            item.cityName ||
            item.City ||
            item.city ||
            item.name ||
            item.label ||
            item.value ||
            item.Value ||
            ''
          );
        })
        .map((value) => (typeof value === 'string' ? value.trim() : value))
        .filter((value) => value && String(value).trim().length > 0);
      setCities(normalized);
      if (normalized.length === 0) {
        console.warn('City fetch returned no usable options.', data);
      }
    } catch (err) {
      console.error('City fetch error:', err);
    }
  }, [logApi]);

  const fetchCountries = useCallback(async () => {
    try {
      logApi('countries', {});
      //api url
      const res = await fetch(`${API_BASE}/countries`);
      const data = await res.json();
      const raw =
        (Array.isArray(data) && data) ||
        (Array.isArray(data?.data) && data.data) ||
        (Array.isArray(data?.value) && data.value) ||
        (Array.isArray(data?.countries) && data.countries) ||
        [];
      const normalized = raw
        .map((item) => {
          if (item === null || item === undefined) return null;
          if (typeof item === 'string') return item;
          return (
            item.CountryName ||
            item.countryname ||
            item.countryName ||
            item.Country ||
            item.country ||
            item.name ||
            item.label ||
            item.value ||
            item.Value ||
            ''
          );
        })
        .map((value) => (typeof value === 'string' ? value.trim() : value))
        .filter((value) => value && String(value).trim().length > 0);
      setCountries(normalized);
      if (normalized.length === 0) {
        console.warn('Country fetch returned no usable options.', data);
      }
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  }, [logApi]);

  const fetchGenderOptions = useCallback(async () => {
    try {
      logApi('gender-options', {});
      //api url
      const res = await fetch(`${API_BASE}/gender-options`);
      const data = await res.json();
      if (data?.success) setGenderOptions(data.data);
    } catch (err) {
      console.error('Gender options error:', err);
    }
  }, [logApi]);

  const fetchReligions = useCallback(async () => {
    try {
      logApi('religions', {});
      //api url
      const res = await fetch(`${API_BASE}/religions`);
      const data = await res.json();
      if (data?.success) setReligions(data.data);
    } catch (err) {
      console.error('Religions error:', err);
    }
  }, [logApi]);

  const fetchMaritalStatusOptions = useCallback(async () => {
    try {
      logApi('marital-status', {});
      //api url
      const res = await fetch(`${API_BASE}/marital-status`);
      const data = await res.json();
      if (data?.success) setMaritalStatusOptions(data.data);
    } catch (err) {
      console.error('Marital status error:', err);
    }
  }, [logApi]);

  const fetchCategories = useCallback(async () => {
    try {
      logApi('complaint-categories', {});
      //api url
      const res = await fetch(`${API_BASE}/complaint-categories`);
      const json = await res.json();
      if (json?.success && Array.isArray(json.data)) {
        setCategories(json.data);
      } else {
        setCategories([]);
        console.error('Bad categories response:', json);
      }
    } catch (e) {
      console.error('Category load error:', e);
      setCategories([]);
    }
  }, [logApi]);
  // Load subcategories when main category changes
  useEffect(() => {
    const mainName = formData.mainCategory;
    if (!mainName) {
      setSubCategoryOptions([]);
      setChildCategoryOptions([]);
      return;
    }
    const shouldSkipReset = Boolean(skipCategoryResetRef.current && mainName);

    const match =
      categories.find((c) =>
        normalizeName(c.name ?? c.CCDescription ?? c.CategoryName ?? '') ===
        normalizeName(mainName)
      ) || null;

    const param = match?.id
      ? `ccid=${encodeURIComponent(match.id)}`
      : `category=${encodeURIComponent(mainName)}`;

    (async () => {
      try {
        logApi('subcategories', { url: `${API_BASE}/subcategories?${param}` });
        //api url
        const res = await fetch(`${API_BASE}/subcategories?${param}`);
        const json = await res.json();
        if (json?.success && Array.isArray(json.data)) {
          const data = json.data.map((s) => ({
            id: s.id,
            name: s.name,
            parent: mainName,
          }));
          setSubCategoryOptions(data);
          // clear category if no longer valid
          if (!data.some((d) => normalizeName(d.name) === normalizeName(formData.category)) && !shouldSkipReset) {
            setFormData((prev) => ({ ...prev, category: '', subCategory: '' }));
          }
          setChildCategoryOptions([]);
        } else {
          setSubCategoryOptions([]);
          setChildCategoryOptions([]);
        }
      } catch (err) {
        console.error('Subcategory load error:', err);
        setSubCategoryOptions([]);
        setChildCategoryOptions([]);
      }
    })();
  }, [categories, formData.category, formData.mainCategory, logApi, normalizeName]);

  // Load child categories when subcategory changes
  useEffect(() => {
    const subName = formData.category;
    if (!subName) {
      setChildCategoryOptions([]);
      return;
    }
    const shouldSkipReset = Boolean(skipCategoryResetRef.current && subName);
    const sub = subCategoryOptions.find(
      (s) => normalizeName(s.name) === normalizeName(subName)
    );
    if (!sub?.id) {
      setChildCategoryOptions([]);
      return;
    }

    (async () => {
      try {
        logApi('subchildren', { url: `${API_BASE}/subchildren?subcategory_id=${encodeURIComponent(sub.id)}` });
       //api url
        const res = await fetch(
          `${API_BASE}/subchildren?subcategory_id=${encodeURIComponent(sub.id)}`
        );
        const json = await res.json();
        if (json?.success && Array.isArray(json.data)) {
          const kids = json.data.map((c) => ({
            id: c.id,
            name: c.name,
            parent: subName,
          }));
          setChildCategoryOptions(kids);
          if (!kids.some((k) => normalizeName(k.name) === normalizeName(formData.subCategory)) && !shouldSkipReset) {
            setFormData((prev) => ({ ...prev, subCategory: '' }));
          }
        } else {
          setChildCategoryOptions([]);
        }
      } catch (err) {
        console.error('Child category load error:', err);
        setChildCategoryOptions([]);
      }
    })();
  }, [formData.category, formData.subCategory, logApi, normalizeName, subCategoryOptions]);

  const onCityChange = useCallback(async (selectedCity) => {
    setFormData(prev => ({
      ...prev,
      city: selectedCity,
      country: selectedCity ? 'Pakistan' : prev.country
    }));

    if (!selectedCity) {
      setDistricts([]);
      setProvinces([]);
      return;
    }

    try {
      //api url
      const url = `${API_BASE}/city-info?city=${encodeURIComponent(selectedCity)}`;
      logApi('city-info', { url });
      const res = await fetch(url);
      const data = await res.json();
      const raw =
        (Array.isArray(data) && data) ||
        (Array.isArray(data?.data) && data.data) ||
        (Array.isArray(data?.value) && data.value) ||
        [];
      if (raw.length > 0) {
        const info = raw[0] || {};
        const district =
          info.DistrictName ||
          info.districtname ||
          info.District ||
          info.district ||
          info.name ||
          '';
        const province =
          info.ProvinceName ||
          info.provincename ||
          info.Province_Name ||
          info.province_name ||
          info.Province ||
          info.province ||
          '';
        const districtValue = String(district || '').trim();
        const provinceValue = String(province || '').trim();
        setDistricts(districtValue ? [{ DistrictName: districtValue }] : []);
        setProvinces(provinceValue ? [{ ProvinceName: provinceValue }] : []);
        setFormData(prev => ({
          ...prev,
          district: districtValue || prev.district,
          province: provinceValue || prev.province
        }));
      }
    } catch (err) {
      console.error('Error fetching city info:', err);
    }
  }, [logApi]);

  const onDistrictChange = useCallback((district) => {
    setFormData(prev => ({ ...prev, district }));
  }, []);

  useEffect(() => {
    if (initialLookupLoadedRef.current) {
      return;
    }
    initialLookupLoadedRef.current = true;

    fetchHeardFrom();
    fetchCities();
    fetchCountries();
    fetchGenderOptions();
    fetchReligions();
    fetchMaritalStatusOptions();
    fetchCategories();
  }, [
    fetchHeardFrom,
    fetchCities,
    fetchCountries,
    fetchGenderOptions,
    fetchReligions,
    fetchMaritalStatusOptions,
    fetchCategories
  ]);

  // FULL MAPPING row → formData
  const handleListRowClick = row => {
    console.log('List row clicked:', row);
    const clearedIdentity = clearIdentityTab();

    const phone = normalizeText(
      row.ContactNumber ||
        row.PhoneNo ||
        row.CLI ||
        row.cli ||
        row.phone ||
        row.phoneNo ||
        row.phoneno,
      clearedIdentity.cli || clearedIdentity.PhoneNo || ''
    );
    const name = normalizeText(
      row.ContactPerson || row.Name || row.name || row.fullName,
      clearedIdentity.name
    );
    const cnic = normalizeText(row.CNIC || row.cnic, clearedIdentity.cnic);
    const qid = pickFirstValue(
      row.QID,
      row.qid,
      row.QueryID,
      row.QueryId,
      row.queryID,
      row.queryId,
      row.ComplaintID,
      row.ComplaintId,
      row.complaintID,
      row.complaintId,
      row.complaintid
    );
    const callerId = normalizeText(
      row.CallersID || row.CallerID || row.callerId,
      formData.callerId
    );

    const status = normalizeStatus(
      pickFirstValue(row.Status, row.status),
      formData.status || ''
    );
    const mainCategory = normalizeText(
      row.MainCat || row.MainCategory || row.mainCategory,
      formData.mainCategory
    );
    const category = normalizeText(row.Cat || row.Category || row.category, formData.category);
    const subCategory = normalizeText(
      row['Sub-Cat'] || row.SubCategory || row.subCategory,
      formData.subCategory
    );
    const office = normalizeText(row.Office || row.office, formData.office);
    const contactId = normalizeText(
      row.ContactID ||
        row.ContactId ||
        row.contactId ||
        row.contact_id ||
        row.CID ||
        row.cid,
      clearedIdentity.contactId
    );
    const gender = normalizeGender(row.Gender || row.gender) || clearedIdentity.gender;
    const age = normalizeText(row.Age || row.age, clearedIdentity.age);
    const city = normalizeText(row.City || row.city, clearedIdentity.city);
    const district = normalizeText(
      row.District || row.district || row.AdditionalInfo2,
      clearedIdentity.district
    );
    const province = normalizeText(
      row.Province || row.province || row.AdditionalInfo3,
      clearedIdentity.province
    );
    const country = normalizeText(
      row.Country || row.country || row.AdditionalInfo4,
      clearedIdentity.country
    );
    const heardFrom = normalizeText(
      row.HeardFrom || row.heardFrom || row.heardfrom || row.AdditionalInfo5,
      clearedIdentity.heardFrom
    );
    const fatherHusbandName = normalizeText(
      row.FatherName || row.fatherName || row.fathername || row.FatherHusbandName || row.fatherHusbandName,
      clearedIdentity.fatherHusbandName
    );
    const salary = normalizeText(
      row.Salary || row.salary || row.AdditionalInfo1,
      clearedIdentity.salary
    );
    const address = normalizeText(row.Address || row.address, clearedIdentity.address);
    const callback1 = normalizeText(
      row.CellNO || row.CellNo || row.cellno || row.Cell1 || row.callback1,
      clearedIdentity.callback1
    );
    const callback2 = normalizeText(
      row.CallbackTwo ||
        row.callbackTwo ||
        row.callbacktwo ||
        row.Callback2 ||
        row.AdditionalInfo10 ||
        row.callback2,
      clearedIdentity.callback2
    );
    const religion = normalizeText(
      row.Religion || row.religion || row.AdditionalInfo6,
      clearedIdentity.religion
    );
    const maritalStatus = normalizeText(
      row.MaritalStatus || row.maritalstatus || row.maritalStatus || row.AdditionalInfo8,
      clearedIdentity.maritalStatus
    );
    const remarks = normalizeText(
      row.Remarks || row.remarks || row.AdditionalInfo9,
      clearedIdentity.remarks
    );

    const launchedByName = normalizeText(
      row.CreatedBy || row.createdBy,
      formData.launchedBy || launchedByDefault
    );
    const acceptedByName = normalizeText(
      row.ModifyiedBy || row.ModifiedBy || row.modifiedBy,
      formData.acceptedBy
    );
    const launchedOnValue = normalizeDateInput(
      pickFirstValue(row.CreatedOn, row.createdOn)
    ) || formData.launchedOn;
    const acceptedOnValue = normalizeDateInput(
      pickFirstValue(row.ModifiedOn, row.modifiedOn)
    ) || formData.acceptedOn;

    const updatedFormData = {
      ...formData,
      ...clearedIdentity,
      ...getEmptyDetailFields(launchedByDefault),

      cli: phone,
      PhoneNo: phone,
      name,
      gender,
      age,
      cnic,
      city,
      district,
      province,
      country,
      heardFrom,
      fatherHusbandName,
      salary,
      address,
      callback1,
      callback2,
      religion,
      maritalStatus,
      remarks,
      queryId: qid,
      status,
      launchedBy: launchedByName,
      CreatedBy: launchedByName,
      acceptedBy: acceptedByName,
      launchedOn: launchedOnValue,
      acceptedOn: acceptedOnValue,
      mainCategory,
      category,
      subCategory,
      callerId,
      office,
      contactId
    };

    skipCategoryResetRef.current = true;
    setFormData(updatedFormData);
    const nextSearchFilters = {
      cli: phone || '',
      cnic: cnic || '',
      name: name || '',
      callerId: qid || ''
    };
    setSearchFilters(nextSearchFilters);
    handleSearchSubmit(nextSearchFilters);
    setCurrentCallerId(phone || '');
    setIsListOpen(false);
    setCallerDataLoaded(true);
    setTimeout(() => setCallerDataLoaded(false), 3000);

    // hydrate remaining identity fields (gender, age, city, etc.)
    if (doFindContactRef.current) {
      const lookupId = qid || contactId || -1;
      fetchContactDetails(phone, lookupId, {
        complaintId: qid,
        loadComplaint: false,
        updateSearchRows: false
      });
    }
    if (city) {
      onCityChange(city);
    }
    doFindContactRef.current = true;
    if (qid) {
      fetchComplaintDetails(qid);
    }
  };

  const pickLatestHistoryRow = useCallback((rows = []) => {
    const normalizedRows = Array.isArray(rows) ? rows.filter(Boolean) : [];
    if (normalizedRows.length <= 1) return normalizedRows[0] || null;

    const readQid = (row) => pickFirstValue(
      row.QID,
      row.qid,
      row.QueryID,
      row.QueryId,
      row.queryID,
      row.queryId,
      row.ComplaintID,
      row.ComplaintId,
      row.complaintID,
      row.complaintId,
      row.complaintid,
      row.latest_complaint_id,
      row.latestComplaintId
    );
    const readDateMs = (row) => {
      const raw = pickFirstValue(
        row.ModifiedOn,
        row.modifiedOn,
        row.modifiedon,
        row.CreatedOn,
        row.createdOn,
        row.createdon,
        row.CreatedAt,
        row.createdAt,
        row.created_at
      );
      const parsed = raw ? new Date(raw).getTime() : NaN;
      return Number.isFinite(parsed) ? parsed : 0;
    };

    return [...normalizedRows].sort((left, right) => {
      const dateDiff = readDateMs(right) - readDateMs(left);
      if (dateDiff !== 0) return dateDiff;
      return Number(readQid(right) || 0) - Number(readQid(left) || 0);
    })[0];
  }, []);

  const handleCliLookup = async (value) => {
    const phone = normalizeText(value, '');
    if (!phone || cliLookupInFlightRef.current) return;

    cliLookupInFlightRef.current = true;
    setSearchLoading(true);
    setErrorMessage('');

    try {
      const payload = {
        all: true,
        RoleName: resolveRoleName(),
        cli: phone
      };
      logApi('cli-enter-search-history', payload);
      const res = await fetch(`${API_BASE}/search-history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });

      const text = await res.text();
      if (!res.ok) {
        throw new Error(text || res.statusText || `HTTP ${res.status}`);
      }

      let result = null;
      try {
        result = text ? JSON.parse(text) : null;
      } catch {
        throw new Error('Invalid search response.');
      }

      const rows =
        Array.isArray(result?.contacts) ? result.contacts
          : Array.isArray(result?.data) ? result.data
            : Array.isArray(result?.Data) ? result.Data
              : Array.isArray(result) ? result
                : [];
      const latestRow = pickLatestHistoryRow(rows);

      if (latestRow) {
        handleListRowClick(latestRow);
        return;
      }

      handleClearInfo();
      setComplaintDetail(null);
      setFormData(prev => ({
        ...prev,
        cli: phone,
        PhoneNo: phone
      }));
      setSearchFilters({ cli: phone, cnic: '', name: '', callerId: '' });
      setCurrentCallerId(phone);
      await fetchContactDetails(phone, -1, { updateSearchRows: true });
      setCallerDataLoaded(true);
      setTimeout(() => setCallerDataLoaded(false), 3000);
      showPageInfo('No previous query found for this CLI. Contact details loaded if available.');
    } catch (error) {
      showPageError(`CLI lookup failed: ${error.message}`);
    } finally {
      cliLookupInFlightRef.current = false;
      setSearchLoading(false);
    }
  };

  const handleVoicemailRowClick = row => {
    const clearedIdentity = clearIdentityTab();
    const phone = normalizeText(
      row.CallerNumber ||
        row.ContactPhoneNo ||
        row.ContactCellNo ||
        row.ComplaintContactNumber ||
        row.caller_number,
      clearedIdentity.cli || clearedIdentity.PhoneNo || ''
    );
    const name = normalizeText(
      row.CallerName ||
        row.ContactName ||
        row.ComplaintContactPerson ||
        row.caller_name,
      clearedIdentity.name
    );
    const contactId = normalizeText(
      row.ContactId || row.contact_id || row.CID || row.cid,
      clearedIdentity.contactId
    );
    const complaintId = normalizeText(
      row.ComplaintId || row.complaint_id || row.QueryId || row.queryid,
      ''
    );
    const queryId = normalizeText(row.QueryId || row.queryid || complaintId, complaintId);
    const uniqueId = normalizeText(row.UniqueId || row.uniqueid || row.CallStateUniqueId, '');
    const channelId = normalizeText(row.ChannelId || row.channelid || row.CallStateChannelId, '');

    setFormData(prev => ({
      ...prev,
      ...clearedIdentity,
      cli: phone,
      PhoneNo: phone,
      name,
      contactId,
      queryId,
      callerId: queryId || phone || prev.callerId,
      source: SOURCE_OPTIONS[2],
      callStateUniqueId: uniqueId || prev.callStateUniqueId,
      callStateChannelId: channelId || prev.callStateChannelId
    }));

    setCurrentCallerId(phone);
    setSearchFilters({
      cli: phone || '',
      cnic: '',
      name: name || '',
      callerId: queryId || contactId || phone || ''
    });
    setIsVoicemailOpen(false);
    setIsHistoryOpen(true);
    setCallerDataLoaded(true);
    setTimeout(() => setCallerDataLoaded(false), 3000);

    if (phone && fetchContactDetailsRef.current) {
      fetchContactDetailsRef.current(phone, complaintId || queryId || contactId || -1, {
        complaintId: complaintId || queryId,
        loadComplaint: false
      });
    }

    if ((complaintId || queryId) && fetchComplaintDetailsRef.current) {
      fetchComplaintDetailsRef.current(complaintId || queryId);
    }
  };

  const handleSearchFilterChange = useCallback((field, value) => {
    setSearchFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSearchSubmit = useCallback(async (overrideFilters) => {
    const activeFilters = overrideFilters || searchFilters;
    if (!activeFilters.cli && !activeFilters.cnic && !activeFilters.name && !activeFilters.callerId) {
      setSearchRows([]);
      return;
    }

    setSearchLoading(true);
    try {
      const hasContactSearchFilter = Boolean(
        String(activeFilters.cli || '').trim() ||
        String(activeFilters.cnic || '').trim() ||
        String(activeFilters.name || '').trim()
      );
      const payload = {
        phoneNo: activeFilters.cli || '',
        cnic: activeFilters.cnic || '',
        name: activeFilters.name || '',
        callerId: hasContactSearchFilter ? '' : (activeFilters.callerId || ''),
        CID: hasContactSearchFilter ? '' : (activeFilters.callerId || ''),
        manual: true,
      };
      logApi('find-contact (search)', payload);
      const searchHistoryPayload = {
        all: true,
        RoleName: resolveRoleName(),
        cli: activeFilters.cli || '',
        name: activeFilters.name || '',
        callerId: hasContactSearchFilter ? '' : (activeFilters.callerId || ''),
      };
      const useHistorySearch = Boolean(
        String(activeFilters.callerId || '').trim() && !hasContactSearchFilter
      );
      const searchUrl = useHistorySearch ? `${API_BASE}/search-history` : `${API_BASE}/find-contact`;
      const searchPayload = useHistorySearch ? searchHistoryPayload : payload;

      const res = await fetch(searchUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchPayload),
      });

      const text = await res.text();
      if (!res.ok) {
        console.error('Search failed:', res.status, text);
        setSearchRows([]);
        return;
      }

      let result;
      try {
        result = JSON.parse(text);
      } catch (err) {
        console.error('Invalid JSON from search:', text);
        setSearchRows([]);
        return;
      }

      const rows =
        Array.isArray(result?.contacts) ? result.contacts
          : Array.isArray(result?.data) ? result.data
            : Array.isArray(result?.Data) ? result.Data
              : Array.isArray(result) ? result
                : [];

      if (rows.length > 0) {
        setSearchRows(rows.map(toSearchRow));
      } else {
        setSearchRows([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setSearchRows([]);
    } finally {
      setSearchLoading(false);
    }
  }, [logApi, resolveRoleName, searchFilters, toSearchRow]);

  useEffect(() => {
    if (!isSearchOpen) {
      searchTriggeredRef.current = false;
      return;
    }
    const hasFilters =
      !!searchFilters.cli ||
      !!searchFilters.cnic ||
      !!searchFilters.name ||
      !!searchFilters.callerId;
    if (hasFilters && !searchTriggeredRef.current && !searchLoading) {
      searchTriggeredRef.current = true;
      handleSearchSubmit();
    }
  }, [handleSearchSubmit, isSearchOpen, searchFilters.cli, searchFilters.cnic, searchFilters.name, searchFilters.callerId, searchLoading]);

  const handleSearchRowClick = (row) => {
    const pickedQueryId = pickFirstValue(
      row.queryId,
      row.QID,
      row.QueryID,
      row.QueryId,
      row.ComplaintID,
      row.ComplaintId,
      row.complaintId,
      row.complaintid
    );
    const pickedCallerId = row.callerId || row.CallerID || row.CallersID || '';
    const pickedContactId =
      row.contactId ||
      row.contact_id ||
      row.ContactID ||
      row.ContactId ||
      row.id ||
      formData.contactId;
    setFormData(prev => ({
      ...prev,
      cli: row.phoneNo || prev.cli,
      name: row.name || prev.name,
      fatherHusbandName: row.fatherName || prev.fatherHusbandName,
      gender: row.gender || prev.gender,
      age: row.age || prev.age,
      province: row.additionalInfo3 || prev.province,
      district: row.additionalInfo2 || prev.district,
      city: row.city || prev.city,
      country: row.additionalInfo4 || prev.country,
      heardFrom: row.additionalInfo5 || prev.heardFrom,
      cnic: row.cnic || prev.cnic,
      callback1: row.cellNo || prev.callback1,
      callback2: row.additionalInfo10 || prev.callback2,
      salary: row.additionalInfo1 || prev.salary,
      religion: row.additionalInfo6 || prev.religion,
      remarks: row.additionalInfo9 || prev.remarks,
      maritalStatus: row.additionalInfo8 || prev.maritalStatus,
      queryId: pickedQueryId || prev.queryId,
      callerId: pickedCallerId,
      contactId: pickedContactId || prev.contactId,
    }));

    if (pickedQueryId) {
      fetchComplaintDetails(pickedQueryId);
    }

    setIsSearchOpen(false);
    setCallerDataLoaded(true);
    setTimeout(() => setCallerDataLoaded(false), 3000);
  };

  const handleDialpadOpen = useCallback(() => {
    if (!isOutboundMode || hasActiveCallUi) return;
    setIsDialpadOpen(true);
  }, [hasActiveCallUi, isOutboundMode]);
  const handleDialpadClose = () => setIsDialpadOpen(false);

  useEffect(() => {
    if (!isOutboundMode && isDialpadOpen) {
      setIsDialpadOpen(false);
    }
  }, [isDialpadOpen, isOutboundMode]);

  const handleDialpadAppend = useCallback(value => {
    setDialedNumber(prev => `${prev}${value}`.slice(0, 15));
  }, []);

  const handleDialpadBackspace = useCallback(() => {
    setDialedNumber(prev => prev.slice(0, -1));
  }, []);

  const handleDialpadClear = useCallback(() => {
    setDialedNumber('');
  }, []);

  const handleHangup = useCallback(async () => {
    try {
      console.log('Hanging up call...');

      const isPendingOutboundDial =
        isOutboundMode &&
        ['dialing', 'connecting', 'ringing'].includes(normalizedCallStatus) &&
        !ariCurrentChannel;
      if (isPendingOutboundDial) {
        outboundDialCancelRequestedRef.current = true;
      }
      
      if (softphoneCall) {
        softphoneHangup();
      }
      
      if (ariCurrentChannel) {
        await ariHangupCall(ariCurrentChannel);
      }
      
      setIsLive(false);
      setCallDuration(0);
      setDialedNumber('');

      setTranscript(prev => [
        ...prev,
        {
          speaker: 'System',
          text: 'Call ended successfully',
          timestamp: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })
        }
      ]);
    } catch (error) {
      console.error('Hangup failed:', error);
      setCallError(`Hangup failed: ${error.message}`);
      setIsLive(false);
      setCallDuration(0);
    }
  }, [ariCurrentChannel, ariHangupCall, isOutboundMode, normalizedCallStatus, softphoneCall, softphoneHangup]);



  const handleDialpadCall = useCallback(async () => {
    if (!dialedNumber || callStatus === 'dialing') return;

    if (!isOutboundMode) {
      alert('Switch to Outbound mode before using the dialpad.');
      return;
    }

    if (!isConnected) {
      alert('Not connected to ARI bridge! Please check the connection.');
      return;
    }
    if (!agentId) {
      alert('No SIP extension found for this agent. Please register the softphone before making an outbound call.');
      return;
    }

    try {
      outboundDialCancelRequestedRef.current = false;
      setIsDialpadOpen(false);
      console.log(`Making outbound call to: ${dialedNumber}`);
      lastPlayedCallResponseRef.current = '';
      stopCallResponsePrompt();
      stopOutboundRingback();
      void unlockCallResponseAudio();
      if (isOutboundMode) {
        outboundCallResponseArmedRef.current = true;
      } else {
        outboundCallResponseArmedRef.current = false;
      }
      const autoAnswerReady = await prepareAutoAnswer();
      if (!autoAnswerReady) {
        throw new Error('Microphone access is required before starting the call');
      }
      const callOwnerId = user?.userId || user?.id || agentId || 'agent';
      const result = await ariMakeCall(dialedNumber, null, callOwnerId, agentId);

      if (result?.success) {
        if (outboundDialCancelRequestedRef.current) {
          outboundDialCancelRequestedRef.current = false;
          await ariHangupCall(result.channelId);
          ariResetCurrentCall(result.channelId);
          setIsLive(false);
          setIsDialpadOpen(false);
          setDialedNumber('');
          setCallDuration(0);
          return;
        }

        setIsLive(true);
        setIsDialpadOpen(false);
        setDialedNumber('');
        setCallDuration(0);

        setTranscript(prev => [
          ...prev,
          {
            speaker: 'System',
            text: `Call initiated to ${dialedNumber}`,
            timestamp: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })
          }
        ]);
      }
    } catch (error) {
      outboundDialCancelRequestedRef.current = false;
      console.error('Call failed:', error);
      outboundCallResponseArmedRef.current = false;
      stopOutboundRingback();
      clearAutoAnswer(true);
      alert(`Call failed: ${error.message}`);
      setIsLive(false);
    }
  }, [dialedNumber, callStatus, isConnected, agentId, stopCallResponsePrompt, stopOutboundRingback, unlockCallResponseAudio, isOutboundMode, prepareAutoAnswer, ariMakeCall, ariHangupCall, ariResetCurrentCall, user?.userId, user?.id, clearAutoAnswer]);

  const handleDialpadInputKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === 'NumpadEnter') {
      event.preventDefault();
      void handleDialpadCall();
    }
  }, [handleDialpadCall]);

  useEffect(() => {
    if (!isDialpadOpen) return undefined;

    const handleKeyDown = e => {
      // Do not hijack typing in inputs/textareas/content-editables
      const tag = (e.target?.tagName || '').toLowerCase();
      const isEditable = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (isEditable) return;

      const { key } = e;
      if (/^[0-9]$/.test(key)) {
        e.preventDefault();
        handleDialpadAppend(key);
        return;
      }
      if (key === '*' || key === '#') {
        e.preventDefault();
        handleDialpadAppend(key);
        return;
      }
      if (key === 'Backspace') {
        e.preventDefault();
        handleDialpadBackspace();
        return;
      }
      if (key === 'Delete') {
        e.preventDefault();
        handleDialpadClear();
        return;
      }
      if (key === 'Enter' || key === 'NumpadEnter') {
        e.preventDefault();
        handleDialpadCall();
        return;
      }
      if (key.startsWith('Numpad')) {
        const num = key.replace('Numpad', '');
        if (/^[0-9]$/.test(num)) {
          e.preventDefault();
          handleDialpadAppend(num);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    const input = dialInputRef.current;
    if (input) {
      input.focus();
      const pos = input.value.length;
      input.setSelectionRange(pos, pos);
    }

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isDialpadOpen,
    handleDialpadAppend,
    handleDialpadBackspace,
    handleDialpadClear,
    handleDialpadCall
  ]);

  const isAnsweredSoftphoneCall = softphoneCall?.state === 'answered';

  useEffect(() => {
    if (callStatus === 'in-call' || callStatus === 'connected' || isAnsweredSoftphoneCall) {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      callTimerRef.current = setInterval(
        () => setCallDuration(prev => prev + 1),
        1000
      );
    } else {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
      if (callStatus !== 'dialing' && callStatus !== 'ringing') {
        setCallDuration(0);
      }
    }

    return () => {
      if (
        callTimerRef.current &&
        callStatus !== 'in-call' &&
        callStatus !== 'connected' &&
        !isAnsweredSoftphoneCall
      ) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
    };
  }, [callStatus, isAnsweredSoftphoneCall]);

  const incomingCallPanel = (
    <IncomingCallNotification
      onAcceptCall={handleAcceptIncomingCall}
      onAcceptStart={handleAcceptIncomingCallStart}
      agentId={agentId}
      isAgentUnavailable={isAgentUnavailable}
      incomingCalls={incomingNotificationCalls}
      acceptIncomingCall={ariAcceptIncomingCall}
      rejectIncomingCall={ariRejectIncomingCall}
      acceptSipIncomingCall={handleAcceptSipIncomingCall}
      rejectSipIncomingCall={softphoneHangup}
      cancelPendingAutoAnswer={clearAutoAnswer}
      onHangup={handleHangup}
      isCallActive={isLive}
      isConnected={isConnected}
      inline
    />
  );

  return (
    <div
      className="frontoffice-app d-flex flex-column vh-100 bg-light"
      data-bs-theme={theme}
      data-theme={theme}
    >
      <div ref={topBarRef} className="frontoffice-topbar-shell">
        <TopAppBarNew
          onLogout={handleLogout}
          onAgentDashboardOpen={handleDashboardOpen}
          onCommentsOpen={handleNotesOpen}
          onChatOpen={handleChatOpen}
          onShortcutsHelpOpen={handleShortcutsHelpOpen}
          onTodayCallActivityOpen={() => setIsTodayCallActivityOpen(true)}
          isChatOpen={isChatOpen}
          unreadMessageCount={Object.values(unreadCounts).reduce((sum, count) => sum + count, 0)}
          onToggleHealth={handleToggleHealth}
          healthEnabled={healthEnabled}
          callStatus={callStatus}
          callDuration={asteriskCallDuration || callDuration}
          activeNumber={activeNumberToShow}
          statusText={statusBadge.text}
          statusClassName={statusBadge.className}
          statusIndicatorClassName={statusIndicatorClassName}
          agentMode={agentMode}
          agentAvailability={agentAvailability}
          onAgentModeChange={setAgentMode}
          onAgentAvailabilityChange={handleAgentAvailabilityChange}
          theme={theme}
          onToggleTheme={() => setTheme((currentTheme) => currentTheme === 'dark' ? 'light' : 'dark')}
          asteriskConnected={isConnected}
          username={username}
          callerId={effectiveCallerId}
          queryId={topBarId}
        />
      </div>

      {callerDataLoaded && (
        <div
          className="alert alert-success alert-dismissible fade show mx-3 mb-0"
          role="alert"
        >
          <i className="bi bi-check-circle-fill me-2"></i>
          Caller data loaded successfully! Information has been populated in the
          form.
          <button
            type="button"
            className="btn-close"
            onClick={() => setCallerDataLoaded(false)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {errorMessage && (
        <div
          className="alert alert-danger alert-dismissible fade show mx-3 mb-0"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage('')}
            aria-label="Close"
          ></button>
        </div>
      )}

      {callError && (
        <div
          className="alert alert-danger alert-dismissible fade show mx-3 mb-0"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {`Call failed: ${callError}`}
          <button
            type="button"
            className="btn-close"
            onClick={() => setCallError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {infoMessage && (
        <div className="alert alert-info mx-3 mb-0" role="alert">
          <i className="bi bi-info-circle-fill me-2"></i>
          {infoMessage}
        </div>
      )}

      <main className="flex-fill px-2 py-2 frontoffice-layout">
        {isMobile ? (
          <div className="frontoffice-mobile-stack">
            <div className="accordion frontoffice-accordion" id="frontofficeMobileAccordion">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${mobileAccordionOpen === 'controls' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleMobileAccordion('controls')}
                  >
                    Call Controls
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${mobileAccordionOpen === 'controls' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <LeftPanelNew
                      ref={leftPanelRef}
                      isLive={isLive}
                      isCallActive={isCallControlActive}
                      isMuted={softphoneMuted}
                      onToggleMute={softphoneCall ? softphoneToggleMute : null}
                      onDialpadOpen={handleDialpadOpen}
                      onHangup={handleHangup}
                      onEmergencyHangup={handleEmergencyHangup}
                      onTransferOpen={() => {}}
                      onTransferComplete={handleTransferComplete}
                      onAttendedTransferComplete={handleAttendedTransferComplete}
                      onSearchHistoryOpen={handleSearchOpen}
                      onListOpen={handleListOpen}
                      onVoicemailOpen={handleVoicemailOpen}
                      onSIPRegistrationOpen={handleSIPRegistrationOpen}
                      onProfileOpen={handleProfileOpen}
                      onAgentDashboardOpen={handleDashboardOpen}
                      onReminderOpen={handleReminderOpen}
                      onViewHistoryOpen={handleHistoryOpen}
                      onNoteOpen={handleNotesOpen}
                      onSmsOpen={handleSmsOpen}
                      onVideoChatOpen={topic =>
                        console.log('Video chat opened:', topic)
                      }
                      asteriskConnected={isConnected}
                      currentChannel={hasActiveCallUi ? currentChannel : null}
                      currentUser={currentUser}
                      callerId={effectiveCallerId}
                      queryId={effectiveQueryId}
                      queueMetrics={queueMetrics}
                      recordingState={recordingState}
                      canUseDialpad={isOutboundMode}
                      onSoftphoneHoldChange={softphoneCall ? softphoneSetHold : null}
                      incomingCallContent={incomingCallPanel}
                      showStandaloneHangup={showOutboundHangup}
                      hideStandaloneHangup={isLive && !isOutboundMode}
                    />
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${mobileAccordionOpen === 'transcript' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleMobileAccordion('transcript')}
                  >
                    Live Transcript
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${mobileAccordionOpen === 'transcript' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <RightPanelNew
                      transcript={transcript}
                      isLive={isLive}
                      transcriptStatus={transcriptStatus}
                      onTextInsert={handleFieldUpdate}
                      recordings={agentRecordings}
                      recordingsLoading={agentRecordingsLoading}
                      recordingsError={agentRecordingsError}
                      recordingFilterDraft={agentRecordingFilterDraft}
                      onRecordingFilterDraftChange={handleRecordingFilterDraftChange}
                      onRecordingFiltersApply={handleApplyRecordingFilters}
                      isRecordingFilterDirty={isRecordingFilterDirty}
                      onRecordingSelect={handleRecordingSelect}
                      selectedRecordingKey={selectedRecordingKey}
                      speakerRoster={transcriptSpeakerRoster}
                      agentSpeakerId={agentSpeakerId}
                      onAgentSpeakerChange={setAgentSpeakerId}
                      isCallActive={isOnCall}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="frontoffice-panel">
              <CenterPanelNew
                formData={formData}
                onFieldUpdate={handleFieldUpdate}
                onPrepareInitiation={() => console.log('Prepare initiation')}
                onProceed={lockedHandleProceed}
                timelineData={timelineData}
                callerDataLoaded={callerDataLoaded}
                currentUserName={cmsUserName}
                currentUserIdentity={currentUserIdentity}
                onUpdateContact={lockedHandleUpdateContact}
                onSaveForm={lockedHandleSaveForm}
                onSaveDetails={lockedHandleSaveDetails}
                onDetailsDraftChange={handleDetailsDraftChange}
                onDetailsFieldChange={handleDetailsFieldChange}
                detailsAutoSaveStatus={detailsAutoSaveStatus}
                onDetailsAutoSaveRetry={handleDetailsAutoSaveRetry}
                onNewQuery={lockedHandleNewQuery}
                onCliLookup={handleCliLookup}
                onClearInfo={handleClearInfo}
                onViewNotes={handleNotesOpen}
                onSendSms={handleSmsOpen}
                saving={formSaving}
                cityOptions={cities}
                districtOptions={districts}
                provinceOptions={provinces}
                countryOptions={countries}
                genderOptions={genderOptions}
                heardFromOptions={heardFromOptions}
                maritalStatusOptions={maritalStatusOptions}
                religionOptions={religions}
                categories={categories}
                subCategoryOptions={subCategoryOptions}
                childCategoryOptions={childCategoryOptions}
                onCityChange={onCityChange}
                onDistrictChange={onDistrictChange}
                identityErrors={identityErrors}
                skipCategoryResetRef={skipCategoryResetRef}
                clearSelectionsToken={clearSelectionsToken}
                isNewQuery={isNewQuery}
                canEditCli={canEditCli}
              />
            </div>
          </div>
        ) : (
          <div className="frontoffice-grid">
            <div className="frontoffice-panel">
              <LeftPanelNew
                ref={leftPanelRef}
                isLive={isLive}
                isCallActive={isCallControlActive}
                isMuted={softphoneMuted}
                onToggleMute={softphoneCall ? softphoneToggleMute : null}
                onDialpadOpen={handleDialpadOpen}
                onHangup={handleHangup}
                onEmergencyHangup={handleEmergencyHangup}
                onTransferOpen={() => {}}
                onTransferComplete={handleTransferComplete}
                onAttendedTransferComplete={handleAttendedTransferComplete}
                onSearchHistoryOpen={handleSearchOpen}
                onListOpen={handleListOpen}
                onVoicemailOpen={handleVoicemailOpen}
                onSIPRegistrationOpen={handleSIPRegistrationOpen}
                onProfileOpen={handleProfileOpen}
                onAgentDashboardOpen={handleDashboardOpen}
                onReminderOpen={handleReminderOpen}
                onViewHistoryOpen={handleHistoryOpen}
                onNoteOpen={handleNotesOpen}
                onSmsOpen={handleSmsOpen}
                onVideoChatOpen={topic =>
                  console.log('Video chat opened:', topic)
                }
                asteriskConnected={isConnected}
                currentChannel={hasActiveCallUi ? currentChannel : null}
                currentUser={currentUser}
                callerId={effectiveCallerId}
                queryId={effectiveQueryId}
                queueMetrics={queueMetrics}
                recordingState={recordingState}
                canUseDialpad={isOutboundMode}
                onSoftphoneHoldChange={softphoneCall ? softphoneSetHold : null}
                incomingCallContent={incomingCallPanel}
                showStandaloneHangup={showOutboundHangup}
                hideStandaloneHangup={isLive && !isOutboundMode}
              />
            </div>

            <div className="frontoffice-panel">
              <CenterPanelNew
                formData={formData}
                onFieldUpdate={handleFieldUpdate}
                onPrepareInitiation={() => console.log('Prepare initiation')}
                onProceed={lockedHandleProceed}
                timelineData={timelineData}
                callerDataLoaded={callerDataLoaded}
                currentUserName={cmsUserName}
                currentUserIdentity={currentUserIdentity}
                onUpdateContact={lockedHandleUpdateContact}
                onSaveForm={lockedHandleSaveForm}
                onSaveDetails={lockedHandleSaveDetails}
                onDetailsDraftChange={handleDetailsDraftChange}
                onDetailsFieldChange={handleDetailsFieldChange}
                detailsAutoSaveStatus={detailsAutoSaveStatus}
                onDetailsAutoSaveRetry={handleDetailsAutoSaveRetry}
                onNewQuery={lockedHandleNewQuery}
                onCliLookup={handleCliLookup}
                onClearInfo={handleClearInfo}
                onViewNotes={handleNotesOpen}
                onSendSms={handleSmsOpen}
                saving={formSaving}
                cityOptions={cities}
                districtOptions={districts}
                provinceOptions={provinces}
                countryOptions={countries}
                genderOptions={genderOptions}
                heardFromOptions={heardFromOptions}
                maritalStatusOptions={maritalStatusOptions}
                religionOptions={religions}
                categories={categories}
                subCategoryOptions={subCategoryOptions}
                childCategoryOptions={childCategoryOptions}
                onCityChange={onCityChange}
                onDistrictChange={onDistrictChange}
                identityErrors={identityErrors}
                skipCategoryResetRef={skipCategoryResetRef}
                clearSelectionsToken={clearSelectionsToken}
                isNewQuery={isNewQuery}
                canEditCli={canEditCli}
              />
            </div>

            <div className="frontoffice-panel frontoffice-panel-right-accordion">
              <RightTranscriptAccordion
                isOpen={isDesktopTranscriptOpen}
                onToggle={() => setIsDesktopTranscriptOpen((prev) => !prev)}
              >
                <RightPanelNew
                  transcript={transcript}
                  isLive={isLive}
                  transcriptStatus={transcriptStatus}
                  onTextInsert={handleFieldUpdate}
                  recordings={agentRecordings}
                  recordingsLoading={agentRecordingsLoading}
                  recordingsError={agentRecordingsError}
                  recordingFilterDraft={agentRecordingFilterDraft}
                  onRecordingFilterDraftChange={handleRecordingFilterDraftChange}
                  onRecordingFiltersApply={handleApplyRecordingFilters}
                  isRecordingFilterDirty={isRecordingFilterDirty}
                  onRecordingSelect={handleRecordingSelect}
                  selectedRecordingKey={selectedRecordingKey}
                  speakerRoster={transcriptSpeakerRoster}
                  agentSpeakerId={agentSpeakerId}
                  onAgentSpeakerChange={setAgentSpeakerId}
                  isCallActive={isOnCall}
                />
              </RightTranscriptAccordion>
            </div>
          </div>
        )}
      </main>

      {isDialpadOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 1050 }}
        >
          <form
            className="bg-white rounded-3 shadow-lg p-3"
            style={{ width: '360px' }}
            onSubmit={(event) => {
              event.preventDefault();
              void handleDialpadCall();
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Dialpad</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleDialpadClose}
              ></button>
            </div>
            <div className="mb-3">
              <input
                type="text"
                className="form-control form-control-lg text-center"
                value={dialedNumber}
                placeholder="Enter number"
                ref={dialInputRef}
                onKeyDown={handleDialpadInputKeyDown}
                onChange={e =>
                  setDialedNumber(
                    e.target.value.replace(/[^0-9+*#]/g, '').slice(0, 15)
                  )
                }
              />
            </div>
            <div className="d-grid gap-2 mb-3">
              {[['1', '2', '3'], ['4', '5', '6'], ['7', '8', '9'], ['*', '0', '#']].map(
                (row, idx) => (
                  <div
                    className="d-grid gap-2"
                    key={idx}
                    style={{
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      display: 'grid'
                    }}
                  >
                    {row.map(value => (
                      <button
                        key={value}
                        type="button"
                        className="btn btn-outline-secondary py-3 fw-semibold"
                        onClick={() => handleDialpadAppend(value)}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                )
              )}
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={handleDialpadBackspace}
              >
                <i className="bi bi-backspace me-1"></i>
                Backspace
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={handleDialpadClear}
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear
              </button>
            </div>
            <button
              type="submit"
              className="btn btn-success w-100 mt-3"
              disabled={!dialedNumber || hasActiveCallUi || !isOutboundMode}
              title={!isOutboundMode ? 'Switch to Outbound mode to place a call' : undefined}
            >
              <i className="bi bi-telephone-outbound me-2"></i>
              {!isOutboundMode ? 'Outbound Only' : hasActiveCallUi ? 'Call Active' : 'Call'}
            </button>
          </form>
        </div>
      )}

      {isListOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 1050 }}
        >
          <div
            className="position-relative bg-white rounded-3 shadow-lg w-100"
            style={{ maxWidth: '1200px', maxHeight: '90vh' }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h5 className="mb-0">Caller List</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleListClose}
              ></button>
            </div>
            <div className="p-3" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <ListTab
                data={listData}
                setData={setListData}
                onRowClick={handleListRowClick}
                storageUserId={cmsUserId || cmsUserName}
                reloadKey={listReloadKey}
              />
            </div>
          </div>
        </div>
      )}

      {isVoicemailOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 1050 }}
        >
          <div
            className="position-relative bg-white rounded-3 shadow-lg w-100"
            style={{ maxWidth: '1240px', maxHeight: '90vh' }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h5 className="mb-0">Voicemail</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleVoicemailClose}
              ></button>
            </div>
            <div className="p-3" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <VoicemailTab
                onRowClick={handleVoicemailRowClick}
                defaultCaller={formData.cli || effectiveCallerId}
                defaultAgent={agentId}
              />
            </div>
          </div>
        </div>
      )}

      {isDashboardOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 1050 }}
        >
          <div
            className="position-relative bg-white rounded-3 shadow-lg w-100"
            style={{ maxWidth: '1000px', maxHeight: '90vh' }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h5 className="mb-0">Agent Dashboard</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleDashboardClose}
              ></button>
            </div>
            <div className="p-3" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <CallerDashbordTab />
            </div>
          </div>
        </div>
      )}

      {isReminderOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 1050 }}
        >
          <div
            className="position-relative bg-white rounded-3 shadow-lg w-100"
            style={{ maxWidth: '1000px', maxHeight: '90vh' }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h5 className="mb-0">Reminders</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleReminderClose}
              ></button>
            </div>
            <div className="p-3" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <RemindersTab createdBy={createdByForReminders} />
            </div>
          </div>
        </div>
      )}

      {isHistoryOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 1050 }}
        >
          <div
            className="position-relative bg-white rounded-3 shadow-lg w-100"
            style={{ maxWidth: '1000px', maxHeight: '90vh' }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h5 className="mb-0">Caller History</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleHistoryClose}
              ></button>
            </div>
            <div className="p-3" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <ViewHistoryTab
                queryId={historyQueryId}
                callerId={effectiveCallerId || formData.cli}
                callUniqueId={formData.callStateUniqueId || complaintDetail?.CallStateUniqueId || ''}
              />
            </div>
          </div>
        </div>
      )}

      {isNotesOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 1050 }}
        >
          <div
            className="position-relative bg-white rounded-3 shadow-lg w-100"
            style={{ maxWidth: '1000px', maxHeight: '90vh' }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h5 className="mb-0">Notes</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleNotesClose}
              ></button>
            </div>
            <div className="p-3" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <NotesTab queryId={notesQueryId} currentUserId={notesUserId} />
            </div>
          </div>
        </div>
      )}

      {isSmsOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 1050 }}
        >
          <div
            className="position-relative bg-white rounded-3 shadow-lg w-100"
            style={{ maxWidth: '1000px', maxHeight: '90vh' }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h5 className="mb-0">Send Whatsapp</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleSmsClose}
              ></button>
            </div>
            <div className="p-3" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <SmsOpen />
            </div>
          </div>
        </div>
      )}

      {isProfileOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 1050 }}
        >
          <div
            className="position-relative bg-white rounded-3 shadow-lg w-100"
            style={{ maxWidth: '1000px', maxHeight: '90vh' }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h5 className="mb-0">Caller Profile</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleProfileClose}
              ></button>
            </div>
            <div className="p-3" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <CallerProfileTab agentId={user?.userId} />
            </div>
          </div>
        </div>
      )}

      {isSearchOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 1050 }}
        >
          <div
            className="position-relative bg-white rounded-3 shadow-lg w-100"
            style={{ maxWidth: '1200px', maxHeight: '90vh' }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h5 className="mb-0">Search</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleSearchClose}
              ></button>
            </div>
            <div className="p-3" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <SearchTab
                rows={searchRows}
                loading={searchLoading}
                filters={searchFilters}
                onFilterChange={handleSearchFilterChange}
                onSubmit={handleSearchSubmit}
                onRowClick={handleSearchRowClick}
              />
            </div>


          
          </div>
        </div>
      )}

      {/* Agent Selector */}
      {showAgentSelector && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(15, 23, 42, 0.22)', zIndex: 1050 }}>
          <div
            className="position-relative bg-white w-100"
            style={{
              maxWidth: '520px',
              maxHeight: '88vh',
              borderRadius: '20px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              boxShadow: 'none'
            }}
          >
            <ChatAgentSelector
              onSelectAgent={handleSelectAgent}
              onClose={() => setShowAgentSelector(false)}
              selectedAgent={selectedAgent}
            />
          </div>
        </div>
      )}

      {/* Chat Window */}
      {isChatOpen && currentChatSession && selectedAgent && (
        <div
          className="position-fixed frontoffice-chat-drawer"
          style={{
            zIndex: 1015,
            width: isMobile ? '100vw' : 'min(420px, calc(100vw - 28px))',
            maxWidth: '100vw',
            top: `${topBarHeight + (isMobile ? 0 : 12)}px`,
            right: isMobile ? '0' : '14px',
            bottom: isMobile ? '0' : '14px'
          }}
        >
          <div
            className="position-absolute top-0 end-0 bottom-0 bg-white d-flex flex-column frontoffice-chat-drawer-panel"
            style={{
              width: '100%',
              border: '1px solid #dbe2ea',
              borderRadius: isMobile ? '0' : '24px',
              background: 'rgba(255,255,255,0.98)',
              backdropFilter: 'blur(14px)',
              boxShadow: isMobile
                ? 'none'
                : '0 24px 60px rgba(15, 23, 42, 0.16), 0 8px 24px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden'
            }}
          >
            <ChatWindow
              sessionId={currentChatSession}
              targetAgent={selectedAgent}
              onClose={() => setIsChatOpen(false)}
            />
          </div>
        </div>
      )}

      {isTodayCallActivityOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-2 p-md-3"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.42)', zIndex: 1060 }}
          role="dialog"
          aria-modal="true"
          aria-label="Today Call Activity"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsTodayCallActivityOpen(false);
          }}
        >
          <div
            className="position-relative rounded-3 shadow-lg w-100 today-call-activity-modal"
            data-bs-theme={theme}
            style={{ maxWidth: '1500px', maxHeight: '92vh', overflowY: 'auto' }}
          >
            <AgentCallHistoryReport
              ownToday
              embedded
              onClose={() => setIsTodayCallActivityOpen(false)}
            />
          </div>
        </div>
      )}

      <ShortcutHelpModal
        open={isShortcutsHelpOpen}
        sections={FRONT_OFFICE_SHORTCUT_SECTIONS}
        onClose={handleShortcutsHelpClose}
        pageHref="/frontoffice-shortcuts"
      />

      {/* Chat Notifications */}
      <ChatNotification onOpenSession={handleChatNotificationOpen} />

    </div>
  );
};

export default FrontOfficeNew;
